import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { CalendarIcon, Camera, Check, ImageIcon, Info, Loader2, Trash2, X } from "lucide-react";
import { useAction, useConvex, useMutation, useQuery } from "convex/react";
import { useSearchParams } from "react-router-dom";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import {
  generateDocumentPdf,
  uploadGeneratedPdfToSignedUrl,
  getDocumentUrl,
  DOC_TYPE_LABELS,
  type GeneratableDocType,
} from "@/lib/document-generation";
import { toUserFacingErrorMessage } from "@/lib/errors";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { CustomerStoragePanel } from "@/components/internal/credit-key/CustomerStoragePanel";
import { TrackingEventsDialogTrigger } from "@/components/internal/shared/TrackingEventsDialogTrigger";
import {
  OrderDocumentsCell,
  buildDocumentLinks as sharedBuildDocumentLinks,
  hasStoredDocumentForType as sharedHasStoredDocumentForType,
  type DocumentLink,
} from "@/components/internal/shared/OrderDocumentsCell";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar, CalendarDayButton } from "@/components/ui/calendar";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useOrderByNumber, useOrders, useOrdersByStatus } from "@/hooks/useOrders";
import { canStartAdminCancellation, useAdminOrderCancellation } from "@/hooks/useAdminOrderCancellation";
import { AdminCancellationDialog } from "@/components/orders/AdminCancellationDialog";
import { useLogisticsCarriers } from "@/hooks/useLogistics";
import { BADGE_TONES } from "@/lib/badge-tones";
import { exportToCSV } from "@/lib/export";
import {
  formatCurrency,
  formatDate,
  formatDeliveryMethod,
  formatRelativeTime,
  formatStatusLabel,
  formatUtcDateOnly,
  timestampToUtcDateKey,
} from "@/lib/format";
import { hasPermission, hasRole, INTERNAL_ROLES } from "@/lib/roles";
import {
  getOrderStatusBadgeClass,
  getPaymentStatusBadgeClass,
} from "@/lib/status-tag-colors";
import { getCarrierBadgeClass } from "@/lib/carrier-tag-colors";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
  getOrderSequenceBadgeClass,
  getOrderSequenceLabel,
  isDisplayableSequence,
} from "@/lib/customer-tag-colors";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { useAppLocale } from "@/lib/i18n";
import { isHiddenInInternalOrderTablesByDefault } from "@/lib/order-status-visibility";
import { m } from "@/paraglide/messages.js";

function getActorLabel(actor: unknown): string {
	if (!actor) return "";
	if (typeof actor === "string") return actor.trim();
	if (typeof actor !== "object") return "";
	if ("label" in actor && typeof actor.label === "string") return actor.label.trim();
	if (
		"kind" in actor &&
		typeof actor.kind === "string" &&
		actor.kind === "system" &&
		"key" in actor &&
		typeof actor.key === "string"
	) {
		if (actor.key === "system_cron") return "System cron";
		if (actor.key === "payment_webhook") return "Payment webhook";
		return "System";
	}
	return "";
}
import { cn } from "@/lib/utils";

type OrderStatus = "awaiting_inventory" | "held_for_customer" | "ready_to_prepare" | "ready_for_pickup" | "in_transit" | "delivered" | "on_hold" | "cancelled";
type FullOrderStatus =
  | "checkout_initiated"
  | "pending_payment"
  | "payment_processing"
  | "awaiting_inventory"
  | "held_for_customer"
  | "ready_to_prepare"
  | "ready_for_pickup"
  | "in_transit"
  | "delivered"
  | "on_hold"
  | "cancelled";

type OrdersTabValue = OrderStatus | "all";
type OrdersViewMode = "modern" | "day_sheet";
type DaySheetReviewStatus = "pending" | "ready_for_inspection" | "approved" | "caution" | "hold";

type ActiveInventoryRecheckSession = {
  sessionId: string;
  startedAt: number;
  expiresAt: number;
  warehouseId: string | null;
  warehouseName: string | null;
};

type InventoryRecheckSessionBanner = ActiveInventoryRecheckSession & {
  status: "active";
  startedByName: string | null;
  cargoCount: number;
};

type InventoryRecheckLineItemImpact = {
  cargoId: string;
  deductedBags: number;
  refundedBags: number;
  netAvailableChange: number;
};

type InventoryRecheckOrderImpact = {
  orderId: string;
  deductedBags: number;
  refundedBags: number;
  netAvailableChange: number;
  lastChangedAt: number | null;
  lineItemImpacts: InventoryRecheckLineItemImpact[];
};

type InventoryRecheckOrderImpactResponse = {
  session: ActiveInventoryRecheckSession | null;
  impacts: InventoryRecheckOrderImpact[];
};

function getStatusTabs(): { value: OrdersTabValue; label: string }[] {
  return [
    { value: "all", label: m.all() },
    { value: "awaiting_inventory", label: "Awaiting Inventory" },
    { value: "held_for_customer", label: "Held for customer" },
    { value: "ready_to_prepare", label: m.status_ready_to_pull() },
    { value: "ready_for_pickup", label: m.status_ready_for_pickup() },
    { value: "in_transit", label: m.status_in_transit() },
    { value: "delivered", label: m.status_delivered() },
    { value: "on_hold", label: m.status_on_hold() },
    { value: "cancelled", label: formatStatusLabel("cancelled") },
  ];
}

const ORDER_TAB_VALUES = new Set<OrdersTabValue>([
  "all",
  "held_for_customer",
  "awaiting_inventory",
  "ready_to_prepare",
  "ready_for_pickup",
  "in_transit",
  "delivered",
  "on_hold",
  "cancelled",
]);

const DAY_SHEET_STATUS_VALUES = new Set<OrderStatus>([
  "held_for_customer",
  "awaiting_inventory",
  "ready_to_prepare",
  "ready_for_pickup",
  "in_transit",
  "delivered",
  "on_hold",
]);

const SPECULATIVE_CARGO_STATUS_VALUES = new Set(["at_origin", "in_transit"]);
const ORDER_SUBMITTED_LABEL = "Submitted";
const LOGISTICS_ORDER_SUMMARY_LIMIT = 1000;
const LOGISTICS_ORDER_DETAILS_LIMIT = 150;
const LOGISTICS_TIME_ZONE = "America/Chicago";
const MANUAL_SHIPPING_LABEL_MAX_BYTES = 10 * 1024 * 1024;
const MANUAL_SHIPPING_LABEL_ACCEPT = [
  "application/pdf",
  "image/gif",
  "image/heic",
  "image/heif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/plain",
  ".pdf",
  ".gif",
  ".heic",
  ".heif",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".txt",
  ".zpl",
].join(",");
const ORDER_LOADING_PHOTO_MIN_COUNT = 1;
const ORDER_LOADING_PHOTO_MAX_COUNT = 320;
const ORDER_LOADING_PHOTO_MAX_BYTES = 100 * 1024 * 1024;
const ORDER_LOADING_PHOTO_ACCEPT = [
  "image/gif",
  "image/heic",
  "image/heif",
  "image/jpeg",
  "image/png",
  "image/webp",
  ".gif",
  ".heic",
  ".heif",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
].join(",");
const SHADOW_BORDER = "shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_-1px_rgba(0,0,0,0.06),0_2px_4px_0_rgba(0,0,0,0.04)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08)]";
const SHADOW_BORDER_HOVER = "hover:shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_2px_-1px_rgba(0,0,0,0.08),0_2px_4px_0_rgba(0,0,0,0.06)] dark:hover:shadow-[0_0_0_1px_rgba(255,255,255,0.13)]";
const SHADOW_MODAL = "shadow-[0_24px_80px_-24px_rgba(0,0,0,0.45),0_0_0_1px_rgba(0,0,0,0.08)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08)]";

const LOGISTICS_DATE_TIME_PARTS_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LOGISTICS_TIME_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const LOGISTICS_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LOGISTICS_TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
});

function isExcludedFromLogisticsAllTab(status: unknown): boolean {
  const normalized = String(status ?? "").trim().toLowerCase();
  return (
    isHiddenInInternalOrderTablesByDefault(normalized)
    || normalized === "pending_payment"
    || normalized === "payment_processing"
    || normalized === "cancelled"
  );
}

function getDaySheetStatusOptions(): { value: OrderStatus; label: string }[] {
  return getStatusTabs().filter(
    (tab): tab is { value: OrderStatus; label: string } =>
      tab.value !== "all" && DAY_SHEET_STATUS_VALUES.has(tab.value as OrderStatus),
  );
}

function getDaySheetReviewOptions(): {
  value: DaySheetReviewStatus;
  colorLabel: string;
  statusLabel: string;
  description: string;
}[] {
  return [
    {
      value: "pending",
      colorLabel: m.orders_review_color_white(),
      statusLabel: m.orders_review_awaiting_review(),
      description: m.orders_review_pending_description(),
    },
    {
      value: "ready_for_inspection",
      colorLabel: m.orders_review_color_blue(),
      statusLabel: m.orders_review_ready_for_inspection(),
      description: m.orders_review_blue_description(),
    },
    {
      value: "approved",
      colorLabel: m.orders_review_color_green(),
      statusLabel: m.orders_review_reviewed(),
      description: m.orders_review_approved_description(),
    },
    {
      value: "caution",
      colorLabel: m.orders_review_color_yellow(),
      statusLabel: m.orders_review_caution(),
      description: m.orders_review_caution_description(),
    },
    {
      value: "hold",
      colorLabel: m.orders_review_color_red(),
      statusLabel: m.status_on_hold(),
      description: m.orders_review_hold_description(),
    },
  ];
}
const SHIPPING_REVIEW_REQUIRED_STATUSES = new Set<FullOrderStatus>([
  "ready_for_pickup",
  "in_transit",
  "delivered",
]);

function resolveOrdersTabValue(rawValue: string | null): OrdersTabValue {
  const normalized = String(rawValue ?? "").trim().toLowerCase();
  if (!normalized) return "all";

  if (normalized === "ready_to_pull") return "ready_to_prepare";
  if (ORDER_TAB_VALUES.has(normalized as OrdersTabValue)) return normalized as OrdersTabValue;

  return "all";
}

function resolveOrdersTabFromSearchParams(searchParams: URLSearchParams): OrdersTabValue {
  const statusParam = searchParams.get("status");
  if (statusParam) return resolveOrdersTabValue(statusParam);
  return resolveOrdersTabValue(searchParams.get("tab"));
}

function resolveOrdersViewMode(rawValue: string | null): OrdersViewMode {
  const normalized = String(rawValue ?? "").trim().toLowerCase();
  return normalized === "modern" ? "modern" : "day_sheet";
}

const ORDER_STATUS_TRANSITIONS: Record<FullOrderStatus, FullOrderStatus[]> = {
  held_for_customer: ["on_hold"],
  checkout_initiated: ["pending_payment", "awaiting_inventory", "ready_to_prepare", "cancelled", "on_hold"],
  pending_payment: ["payment_processing", "awaiting_inventory", "ready_to_prepare", "cancelled", "on_hold"],
  payment_processing: ["awaiting_inventory", "ready_to_prepare", "pending_payment", "cancelled", "on_hold"],
  awaiting_inventory: ["ready_to_prepare", "cancelled", "on_hold"],
  ready_to_prepare: ["ready_for_pickup", "cancelled", "on_hold"],
  ready_for_pickup: [
    "in_transit",
    "delivered",
    "ready_to_prepare",
    "cancelled",
    "on_hold",
  ],
  in_transit: ["delivered", "ready_for_pickup", "on_hold"],
  delivered: [],
  on_hold: [
    "held_for_customer",
    "pending_payment",
    "payment_processing",
    "awaiting_inventory",
    "ready_to_prepare",
    "ready_for_pickup",
    "in_transit",
    "cancelled",
  ],
  cancelled: [],
};

function getBulkStatusActions(): {
  target: FullOrderStatus;
  label: string;
  variant: "outline" | "secondary" | "destructive";
}[] {
  return [
    { target: "awaiting_inventory", label: "Awaiting Inventory", variant: "outline" },
    { target: "ready_to_prepare", label: m.status_ready_to_pull(), variant: "outline" },
    { target: "ready_for_pickup", label: m.status_ready_for_pickup(), variant: "outline" },
    { target: "in_transit", label: m.status_in_transit(), variant: "outline" },
    { target: "delivered", label: m.status_delivered(), variant: "outline" },
    { target: "on_hold", label: m.status_on_hold(), variant: "destructive" },
  ];
}

// DocumentLink type imported from shared OrderDocumentsCell

type CarrierOption = {
  _id: string;
  name: string;
  shippeekCode?: string | null;
};

const LOCAL_PICKUP_OPTION_ID = "__local_pickup__";
const MANUAL_ADDRESS_OPTION_ID = "__manual_address__";
const AMENDMENT_PENDING_STALE_MS = 3 * 24 * 60 * 60 * 1000;

type OrderAmendmentData = {
  editedOnce?: boolean;
  pendingAmendment?: unknown;
  pendingPaymentLinkId?: string | null;
  pendingPaymentLinkUrl?: string | null;
  pendingStartedAt?: number | null;
  holdReviewAt?: number | null;
  resumeStatus?: string | null;
  totalDelta?: number;
};

type AmendmentIndicatorState = {
  editedOnce: boolean;
  isPendingOnHold: boolean;
  isPendingThreeDays: boolean;
  pendingStartedAt: number | null;
  holdReviewAt: number | null;
  amountDue: number;
  resumeStatus: string | null;
  paymentLinkStatus: "created" | "missing";
};

type AmendmentQueueRow = {
  orderId: string;
  orderNumber: string;
  customer: string;
  pendingStartedAt: number | null;
  holdReviewAt: number | null;
  amountDue: number;
  resumeStatus: string | null;
  paymentLinkStatus: "created" | "missing";
  isPendingThreeDays: boolean;
  isPendingOnHold: boolean;
};

type CarrierChangeContextAddress = {
  id: string;
  shippingInfoId: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
  updatedAt: number;
};

type CarrierChangeContextData = {
  orderId: string;
  orderNumber: string | null;
  currentDelivery: {
    deliveryMethod: string | null;
    isLocalPickup: boolean;
    logisticsScheduledDate: number | null;
    deliveryDate: number | null;
    pickupDate: number | null;
    deliveryAddress: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    } | null;
  };
  shippingAddresses: CarrierChangeContextAddress[];
  defaultShippingInfoId: string | null;
  recommendedShippingInfoId: string | null;
  recommendedSource: "current_address" | "default" | "latest" | null;
  dateSeed: string;
};

type ManualShippingAddressDraft = {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

type CarrierReratePreviewResult = {
  success: boolean;
  rerated: boolean;
  applied: false;
  autoRerate: true;
  rateNeedsReview: boolean;
  previousDeliveryMethod: "pickup" | "shipping";
  deliveryMethod: "shipping";
  carrierName: string | null;
  newDeliveryCharge: number;
  message: string;
  selectedRate: {
    id: string;
    carrier: string | null;
    carrierCode: string | null;
    serviceName: string | null;
    totalPrice: number;
  } | null;
};

type PendingCarrierChange = {
  orderId: string;
  orderNumber: string;
  currentMode: "shipping" | "pickup";
  currentCarrierName: string;
  currentCarrierCode: string;
  currentTrackingNumber: string;
  currentTrackingUrl: string;
  currentQuoteId: string;
  paymentMethod: string;
  paymentStatus: string;
  nextMode: "shipping" | "pickup";
  nextCarrierId: string;
  nextCarrierName: string;
  selectedShippingInfoId: string;
  useManualShippingAddress: boolean;
  manualShippingAddress: ManualShippingAddressDraft;
  shippingDate: string;
  contextHydrated: boolean;
  contextError: string | null;
};

type LogisticsDetailsDraft = {
  driverFullname: string;
  trackingNumber: string;
  shippingDate: string;
  driverCheckin: string;
  driverCheckout: string;
};

type ParsedDateTimeInput = {
  value: number | undefined;
  invalid: boolean;
};

type LogisticsDetailsEditorState = {
  orderId: string;
  orderNumber: string;
  companyName: string;
  trackingAuditSummary: string;
  original: {
    driverFullname: string | undefined;
    trackingNumber: string | undefined;
    shippingDate: string | undefined;
    driverCheckin: number | undefined;
    driverCheckout: number | undefined;
  };
  draft: LogisticsDetailsDraft;
};

type ManualShippingLabelUploadState = {
  orderId: string;
  orderNumber: string;
  companyName: string;
  existingLabelCount: number;
  file: File | null;
  error: string | null;
};

type LoadingPhotoUploadState = {
  order: any;
  orderId: string;
  orderNumber: string;
  companyName: string;
  existingPhotoCount: number;
  files: File[];
  error: string | null;
  advanceAfterUpload: boolean;
};

type LoadingPhotoWithUrl = {
  id: string;
  originalFilename: string;
  contentType: string;
  fileSize: number;
  uploadedAt?: number;
  uploadedBy?: string | null;
  url: string;
};

type LoadingPhotoViewerState = {
  orderId: string;
  orderNumber: string;
  companyName: string;
  photos: LoadingPhotoWithUrl[] | null;
  isLoading: boolean;
  error: string | null;
};

type MissingInTransitDetailsOrder = {
  order: any;
  orderId: string;
  orderNumber: string;
  missingFields: string[];
};

function getDeliveryMethodBadgeClass(deliveryMethod: unknown): string {
  const normalized = String(deliveryMethod ?? "").trim().toLowerCase();
  if (!normalized) return BADGE_TONES.slate;
  if (normalized.includes("pickup")) return BADGE_TONES.sky;
  if (normalized.includes("delivery") || normalized.includes("ship")) return BADGE_TONES.violet;
  if (normalized.includes("freight")) return BADGE_TONES.cyan;
  return BADGE_TONES.slate;
}

function getMoneyDisplay(value: unknown) {
  if (typeof value === "string") return value;
  if (typeof value === "number") return formatCurrency(value);
  return formatCurrency(0);
}

function getQuotedShippingCost(order: {
  delivery?: { deliveryCharge?: unknown } | null;
  shippingCost?: unknown;
}) {
  const deliveryCharge = order.delivery?.deliveryCharge;
  if (typeof deliveryCharge === "number") return deliveryCharge;

  if (typeof order.shippingCost === "number") return order.shippingCost;

  return null;
}

function normalizeCarrierValue(value: unknown) {
  return String(value ?? "").trim().toLowerCase();
}

function getSelectedShippingServiceName(delivery: any): string | null {
  const metadata = delivery?.shippingMetadata && typeof delivery.shippingMetadata === "object"
    ? delivery.shippingMetadata
    : {};
  const service = String(
    delivery?.selectedService ??
      delivery?.serviceName ??
      metadata.selectedService ??
      metadata.serviceName ??
      metadata.selectedServiceName ??
      "",
  ).trim();
  return service || null;
}

function isGuaranteedDelivery(delivery: any): boolean {
  if (!delivery) return false;
  if (delivery.guaranteedService === true) return true;
  const metadata = delivery.shippingMetadata && typeof delivery.shippingMetadata === "object"
    ? delivery.shippingMetadata
    : {};
  if (metadata.guaranteedService === true) return true;
  const service = getSelectedShippingServiceName(delivery);
  return typeof service === "string" && /\bguaranteed\b/i.test(service);
}

function normalizeOrderDocumentType(value: unknown) {
  return String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function getShippingLabelAttachmentCount(order: any): number {
  const attachments = Array.isArray(order?.attachments) ? order.attachments : [];
  return attachments.filter(
    (attachment: any) => normalizeOrderDocumentType(attachment?.type) === "shipping_label",
  ).length;
}

function isSupportedManualShippingLabelFile(file: File): boolean {
  const contentType = String(file.type ?? "").trim().toLowerCase().split(";")[0] ?? "";
  if (
    [
      "application/pdf",
      "image/gif",
      "image/heic",
      "image/heif",
      "image/jpeg",
      "image/png",
      "image/webp",
      "text/plain",
    ].includes(contentType)
  ) {
    return true;
  }

  const filename = file.name.toLowerCase();
  return /\.(pdf|gif|heic|heif|jpe?g|png|webp|txt|zpl)$/.test(filename);
}

function formatManualShippingLabelFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) return `${kilobytes.toFixed(1)} KB`;
  return `${(kilobytes / 1024).toFixed(1)} MB`;
}

function isSupportedOrderLoadingPhotoFile(file: File): boolean {
  const contentType = String(file.type ?? "").trim().toLowerCase().split(";")[0] ?? "";
  if (
    [
      "image/gif",
      "image/heic",
      "image/heif",
      "image/jpeg",
      "image/png",
      "image/webp",
    ].includes(contentType)
  ) {
    return true;
  }

  return /\.(gif|heic|heif|jpe?g|png|webp)$/i.test(file.name);
}

function getOrderLoadingPhotoCount(order: any): number {
  const explicitCount = Number(order?.loadingPhotoCount);
  if (Number.isFinite(explicitCount) && explicitCount >= 0) return explicitCount;
  return Array.isArray(order?.loadingPhotos) ? order.loadingPhotos.length : 0;
}

function getOrderLoadingPhotoRequirementLabel(order: any): string | null {
  const count = getOrderLoadingPhotoCount(order);
  if (count >= ORDER_LOADING_PHOTO_MIN_COUNT) return null;
  return `Truck/loading photos (${count}/${ORDER_LOADING_PHOTO_MIN_COUNT})`;
}

function normalizeOrderNumberQuery(value: string): string {
  const normalized = value.trim().replace(",", "-").replace(/\s+/g, "");
  if (!normalized) return "";
  const orderToken = normalized.match(/^([a-zA-Z]+)-?(\d+)$/);
  if (!orderToken) return normalized;
  const prefix = orderToken[1];
  const sequence = orderToken[2];
  if (!prefix || !sequence) return normalized;
  return `${prefix.toUpperCase()}-${sequence}`;
}

function extractPositiveAmount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

function parseOrderAmendmentData(order: any): OrderAmendmentData | null {
  const raw = order?.amendmentData;
  if (!raw || typeof raw !== "object") return null;
  return raw as OrderAmendmentData;
}

function resolvePendingAmountDue(amendmentData: OrderAmendmentData | null): number {
  if (!amendmentData) return 0;
  const fromTotalDelta = extractPositiveAmount(amendmentData.totalDelta);
  if (fromTotalDelta !== null) return fromTotalDelta;

  const pending = amendmentData.pendingAmendment;
  if (!pending || typeof pending !== "object") return 0;

  const pendingRecord = pending as Record<string, unknown>;
  const directTotalDelta = extractPositiveAmount(pendingRecord.totalDelta);
  if (directTotalDelta !== null) return directTotalDelta;
  const amountDue = extractPositiveAmount(pendingRecord.amountDue);
  if (amountDue !== null) return amountDue;
  const delta = extractPositiveAmount(pendingRecord.delta);
  if (delta !== null) return delta;
  return 0;
}

function getAmendmentIndicatorState(order: any, now = Date.now()): AmendmentIndicatorState {
  const amendmentData = parseOrderAmendmentData(order);
  const hasPendingAmendment =
    amendmentData?.pendingAmendment !== null && amendmentData?.pendingAmendment !== undefined;
  const isPendingOnHold = order?.status === "on_hold" && hasPendingAmendment;

  const pendingStartedAt =
    typeof amendmentData?.pendingStartedAt === "number" ? amendmentData.pendingStartedAt : null;
  const holdReviewAt =
    typeof amendmentData?.holdReviewAt === "number" ? amendmentData.holdReviewAt : null;
  const isPendingThreeDays = isPendingOnHold
    ? holdReviewAt !== null
      ? holdReviewAt <= now
      : pendingStartedAt !== null
        ? now - pendingStartedAt >= AMENDMENT_PENDING_STALE_MS
        : false
    : false;

  const amountDue = resolvePendingAmountDue(amendmentData);
  const hasPaymentLink =
    String(amendmentData?.pendingPaymentLinkUrl ?? "").trim().length > 0 ||
    String(amendmentData?.pendingPaymentLinkId ?? "").trim().length > 0;

  return {
    editedOnce: amendmentData?.editedOnce === true,
    isPendingOnHold,
    isPendingThreeDays,
    pendingStartedAt,
    holdReviewAt,
    amountDue,
    resumeStatus:
      typeof amendmentData?.resumeStatus === "string" && amendmentData.resumeStatus.trim().length > 0
        ? amendmentData.resumeStatus
        : null,
    paymentLinkStatus: hasPaymentLink ? "created" : "missing",
  };
}

function OrderAmendmentIndicatorBadge({ order }: { order: any }) {
  const indicators = getAmendmentIndicatorState(order);

  if (indicators.isPendingThreeDays) {
    return (
      <Badge variant="outline" className="border-red-300 bg-red-50 text-red-800 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-100">
        {m.orders_pending_three_days()}
      </Badge>
    );
  }

  if (indicators.isPendingOnHold) {
    return (
      <Badge variant="outline" className="border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800/60 dark:bg-blue-950/30 dark:text-blue-100">
        {m.status_pending_payment()}
      </Badge>
    );
  }

  if (indicators.editedOnce) {
    return (
      <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100">
        {m.orders_edited_once_double_check()}
      </Badge>
    );
  }

  return <span className="text-muted-foreground">-</span>;
}

function normalizeOptionalTextValue(value: unknown): string | undefined {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : undefined;
}

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function toDateInputValue(timestamp: unknown): string {
  if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function buildManualShippingAddressDraftFromOrder(order: any): ManualShippingAddressDraft {
  const deliveryAddress = order?.delivery?.deliveryAddress ?? {};
  const street = String(
    deliveryAddress?.street ?? deliveryAddress?.address ?? deliveryAddress?.addressLine1 ?? "",
  ).trim();
  return {
    street,
    city: String(deliveryAddress?.city ?? "").trim(),
    state: String(deliveryAddress?.state ?? "").trim(),
    postalCode: String(deliveryAddress?.postalCode ?? "").trim(),
    country: String(deliveryAddress?.country ?? "US").trim() || "US",
  };
}

function formatShippingAddressLabel(address: CarrierChangeContextAddress): string {
  const line1 = address.street || m.orders_no_street();
  const cityStatePostal = [address.city, address.state, address.postalCode].filter(Boolean).join(", ");
  return cityStatePostal ? `${line1} · ${cityStatePostal}` : line1;
}

function formatTimestampForDateTimeInput(timestamp: unknown): string {
  if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return "";
  const parts = getLogisticsDateTimeParts(timestamp);
  return parts ? formatLogisticsDateTimeInputValue(parts) : "";
}

function getCurrentDateTimeInputValue(): string {
  return formatTimestampForDateTimeInput(Date.now());
}

function parseDateTimeInputValue(value: string): ParsedDateTimeInput {
  const normalized = value.trim();
  if (!normalized) return { value: undefined, invalid: false };

  const parts = parseLogisticsDateTimeInputParts(normalized);
  if (!parts) return { value: undefined, invalid: true };

  const utcGuess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    0,
    0,
  );

  const initialOffset = getLogisticsTimeZoneOffsetMs(utcGuess);
  if (initialOffset === null) return { value: undefined, invalid: true };

  let timestamp = utcGuess - initialOffset;
  const resolvedOffset = getLogisticsTimeZoneOffsetMs(timestamp);
  if (resolvedOffset === null) return { value: undefined, invalid: true };
  if (resolvedOffset !== initialOffset) {
    timestamp = utcGuess - resolvedOffset;
  }

  const resolvedParts = getLogisticsDateTimeParts(timestamp);
  if (
    !resolvedParts ||
    resolvedParts.year !== parts.year ||
    resolvedParts.month !== parts.month ||
    resolvedParts.day !== parts.day ||
    resolvedParts.hour !== parts.hour ||
    resolvedParts.minute !== parts.minute
  ) {
    return { value: undefined, invalid: true };
  }

  return { value: timestamp, invalid: false };
}

type LogisticsDateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function getLogisticsDateTimeParts(timestamp: number): LogisticsDateTimeParts | null {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  const parts = LOGISTICS_DATE_TIME_PARTS_FORMATTER.formatToParts(date);
  const values = {
    year: Number(parts.find((part) => part.type === "year")?.value ?? Number.NaN),
    month: Number(parts.find((part) => part.type === "month")?.value ?? Number.NaN),
    day: Number(parts.find((part) => part.type === "day")?.value ?? Number.NaN),
    hour: Number(parts.find((part) => part.type === "hour")?.value ?? Number.NaN),
    minute: Number(parts.find((part) => part.type === "minute")?.value ?? Number.NaN),
    second: Number(parts.find((part) => part.type === "second")?.value ?? Number.NaN),
  };

  return Object.values(values).every((value) => Number.isFinite(value)) ? values : null;
}

function getLogisticsTimeZoneOffsetMs(timestamp: number): number | null {
  const parts = getLogisticsDateTimeParts(timestamp);
  if (!parts) return null;

  const zonedUtcTimestamp = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    0,
  );
  return zonedUtcTimestamp - timestamp;
}

function formatLogisticsDateTimeInputValue(parts: LogisticsDateTimeParts): string {
  const month = String(parts.month).padStart(2, "0");
  const day = String(parts.day).padStart(2, "0");
  const hour = String(parts.hour).padStart(2, "0");
  const minute = String(parts.minute).padStart(2, "0");

  return `${parts.year}-${month}-${day}T${hour}:${minute}`;
}

function parseLogisticsDateTimeInputParts(value: string): LogisticsDateTimeParts | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }

  const validationDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
  if (
    validationDate.getUTCFullYear() !== year ||
    validationDate.getUTCMonth() !== month - 1 ||
    validationDate.getUTCDate() !== day ||
    validationDate.getUTCHours() !== hour ||
    validationDate.getUTCMinutes() !== minute
  ) {
    return null;
  }

  return { year, month, day, hour, minute, second: 0 };
}

function hasCompleteManualShippingAddress(address: ManualShippingAddressDraft): boolean {
  return (
    String(address.street ?? "").trim().length > 0 &&
    String(address.city ?? "").trim().length > 0 &&
    String(address.state ?? "").trim().length > 0 &&
    String(address.postalCode ?? "").trim().length > 0
  );
}

function buildLogisticsDetailsOriginal(order: any) {
  return {
    driverFullname: normalizeOptionalTextValue(order?.delivery?.driverFullname),
    trackingNumber: normalizeOptionalTextValue(order?.delivery?.trackingNumber),
    shippingDate: normalizeOptionalTextValue(
      toDateInputValue(
        order?.delivery?.logisticsScheduledDate ??
          order?.delivery?.deliveryDate ??
          order?.delivery?.pickupDate,
      ),
    ),
    driverCheckin:
      typeof order?.delivery?.driverCheckin === "number" ? order.delivery.driverCheckin : undefined,
    driverCheckout:
      typeof order?.delivery?.driverCheckout === "number" ? order.delivery.driverCheckout : undefined,
  };
}

function buildLogisticsDetailsDraft(order: any): LogisticsDetailsDraft {
  const original = buildLogisticsDetailsOriginal(order);
  return {
    driverFullname: original.driverFullname ?? "",
    trackingNumber: original.trackingNumber ?? "",
    shippingDate: original.shippingDate ?? "",
    driverCheckin: formatTimestampForDateTimeInput(original.driverCheckin),
    driverCheckout: formatTimestampForDateTimeInput(original.driverCheckout),
  };
}

function getMissingInTransitDetails(order: any): string[] {
  const missingFields: string[] = [];
  const driverName = normalizeOptionalTextValue(order?.delivery?.driverFullname);
  const trackingNumber = normalizeOptionalTextValue(order?.delivery?.trackingNumber);
  const driverCheckin = order?.delivery?.driverCheckin;

  if (!driverName) missingFields.push("Driver Name");
  if (!trackingNumber) missingFields.push(m.orders_tracking_number());
  if (typeof driverCheckin !== "number" || !Number.isFinite(driverCheckin)) {
    missingFields.push(m.orders_logistics_driver_checkin());
  }

  const photoRequirement = getOrderLoadingPhotoRequirementLabel(order);
  if (photoRequirement) missingFields.push(photoRequirement);

  return missingFields;
}

// buildDocumentLinks, hasStoredDocumentForType, isMatchingDocType moved to shared OrderDocumentsCell
const buildDocumentLinks = sharedBuildDocumentLinks;
const hasStoredDocumentForType = sharedHasStoredDocumentForType;

function isUpsOrder(order: any) {
  const carrierName = normalizeCarrierValue(order?.delivery?.carrierName);
  const metadataCarrier = normalizeCarrierValue(order?.delivery?.carrierCode);
  const metadataName = normalizeCarrierValue(order?.delivery?.carrier);
  return (
    carrierName === "ups" ||
    carrierName === "upgf" ||
    carrierName.includes("ups") ||
    metadataCarrier === "ups" ||
    metadataCarrier === "upgf" ||
    metadataCarrier.includes("ups") ||
    metadataName.includes("ups")
  );
}

function isEstesOrder(order: any) {
  const carrierName = normalizeCarrierValue(order?.delivery?.carrierName);
  const metadataCarrier = normalizeCarrierValue(order?.delivery?.carrierCode);
  const metadataName = normalizeCarrierValue(order?.delivery?.carrier);
  return (
    carrierName.startsWith("exla") ||
    carrierName.includes("estes") ||
    metadataCarrier.startsWith("exla") ||
    metadataCarrier.includes("estes") ||
    metadataName.includes("estes")
  );
}

function isAaaCooperOrder(order: any) {
  const values = [
    order?.delivery?.carrierName,
    order?.delivery?.carrierCode,
    order?.delivery?.carrier,
  ].map((value) => normalizeCarrierValue(value).replace(/[^a-z0-9]+/g, " ").trim());

  return values.some((value) =>
    value === "aact" ||
    value === "aaa" ||
    value === "act" ||
    value === "aaa cooper" ||
    value === "aaa cooper act" ||
    value === "aaa cooper transportation" ||
    value.includes("aaa cooper"),
  );
}

function isDirectLtlBookingOrder(order: any) {
  return isEstesOrder(order) || isAaaCooperOrder(order);
}

function isUpsBookingEligible(order: any) {
  const status = String(order?.status ?? "");

  return (
    (status === "ready_to_prepare" || status === "ready_for_pickup") &&
    isUpsOrder(order)
  );
}

function isEstesBookingEligible(order: any) {
  const status = String(order?.status ?? "");

  return (
    (status === "ready_to_prepare" || status === "ready_for_pickup") &&
    isDirectLtlBookingOrder(order)
  );
}

function isEstesCancellationEligible(order: any) {
  const status = String(order?.status ?? "");

  return (
    status === "ready_for_pickup" &&
    isEstesOrder(order)
  );
}

function getOrderScheduledTimestamp(order: any): number {
  if (typeof order?.delivery?.logisticsScheduledDate === "number") {
    return order.delivery.logisticsScheduledDate;
  }
  if (typeof order?.delivery?.deliveryDate === "number") return order.delivery.deliveryDate;
  if (typeof order?.delivery?.pickupDate === "number") return order.delivery.pickupDate;
  if (typeof order?._creationTime === "number") return order._creationTime;
  if (typeof order?.createdAt === "number") return order.createdAt;
  return Date.now();
}

function getOrderCreatedTimestamp(order: any): number | null {
  if (typeof order?.createdAt === "number") return order.createdAt;
  if (typeof order?._creationTime === "number") return order._creationTime;
  return null;
}

function getDisplayedOrderStatus(order: any): FullOrderStatus {
  return String(order?.operationalStatus ?? order?.status ?? "pending_payment") as FullOrderStatus;
}

function normalizeCargoStatusValue(value: unknown): string {
  return String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function orderHasSpeculativeCargo(order: any): boolean {
  const items = Array.isArray(order?.items) ? order.items : [];
  return items.some((item: any) => {
    const status = normalizeCargoStatusValue(
      item?.cargo?.status ?? item?.cargoStatus ?? item?.options?.cargoStatus,
    );
    return SPECULATIVE_CARGO_STATUS_VALUES.has(status);
  });
}

function canShowOrderStatusTarget(order: any, currentStatus: FullOrderStatus, target: FullOrderStatus): boolean {
  if (target !== "awaiting_inventory") return true;
  if (currentStatus === "awaiting_inventory") return true;
  return orderHasSpeculativeCargo(order);
}

function getOrderHoldReason(order: any): string | null {
  const reason = String(order?.holdReason ?? "").trim();
  return reason || null;
}

// Logistics intake starts when an order first becomes actionable for the warehouse.
function getOrderSubmittedTimestamp(order: any): number | null {
  const statusHistory = Array.isArray(order?.statusHistory) ? order.statusHistory : [];
  const readyToPrepareTimestamps = statusHistory
    .filter(
      (entry: any) =>
        entry?.status === "ready_to_prepare" && typeof entry?.timestamp === "number",
    )
    .map((entry: any) => entry.timestamp);

  if (readyToPrepareTimestamps.length > 0) {
    return Math.min(...readyToPrepareTimestamps);
  }

  return getOrderCreatedTimestamp(order);
}

function formatOrderSubmittedTimestamp(timestamp: unknown): string {
  if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return "-";

  const formatted = formatDate(timestamp, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return formatted || "-";
}

function getTrackingAuditSummary(delivery: any): string {
  const trackingNumber = String(delivery?.trackingNumber ?? "").trim();
  if (!trackingNumber) return "";

  const actor = getActorLabel(delivery?.trackingUpdatedBy);
  const updatedAt =
    typeof delivery?.trackingUpdatedAt === "number" && Number.isFinite(delivery.trackingUpdatedAt)
      ? formatOrderSubmittedTimestamp(delivery.trackingUpdatedAt)
      : "";

  if (actor && updatedAt) {
    return m.orders_tracking_audit_by_at({ actor, date: updatedAt });
  }
  if (actor) {
    return m.orders_tracking_audit_by({ actor });
  }
  if (updatedAt) {
    return m.orders_tracking_audit_at({ date: updatedAt });
  }
  return m.orders_tracking_audit_legacy();
}

function TrackingAuditLine({
  delivery,
  className,
}: {
  delivery: any;
  className?: string;
}) {
  const summary = getTrackingAuditSummary(delivery);
  if (!summary) return null;

  return (
    <p className={cn("text-[10px] leading-4 text-muted-foreground", className)}>
      {summary}
    </p>
  );
}

function isDateOnlyScheduledTimestamp(order: any): boolean {
  return (
    typeof order?.delivery?.logisticsScheduledDate === "number" ||
    typeof order?.delivery?.deliveryDate === "number" ||
    typeof order?.delivery?.pickupDate === "number"
  );
}

function getLocalDateKey(timestamp: number): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getOrderScheduledDateKey(order: any): string {
  const timestamp = getOrderScheduledTimestamp(order);
  return isDateOnlyScheduledTimestamp(order)
    ? timestampToUtcDateKey(timestamp)
    : getLocalDateKey(timestamp);
}

function formatDaySheetHeading(dayKey: string): string {
  if (!dayKey) return m.orders_unscheduled();
  const parsed = new Date(`${dayKey}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return dayKey;
  return parsed.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function formatTimeForDaySheet(timestamp: unknown): string {
  if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return "-";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "-";
  return LOGISTICS_TIME_FORMATTER.format(date);
}

function resolveDaySheetReviewStatus(value: unknown): DaySheetReviewStatus {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return "pending";
  if (
    normalized === "ready_for_inspection" ||
    normalized === "inspection_ready" ||
    normalized === "pulled" ||
    normalized === "blue" ||
    normalized.includes("inspection")
  ) {
    return "ready_for_inspection";
  }
  if (
    normalized === "passed" ||
    normalized === "approved" ||
    normalized === "green" ||
    normalized.includes("pass") ||
    normalized.includes("approve")
  ) {
    return "approved";
  }
  if (
    normalized === "caution" ||
    normalized === "yellow" ||
    normalized.includes("caution") ||
    normalized.includes("change")
  ) {
    return "caution";
  }
  if (
    normalized === "hold" ||
    normalized === "on_hold" ||
    normalized === "red" ||
    normalized.includes("hold")
  ) {
    return "hold";
  }
  return "pending";
}

function getDaySheetReviewStatus(order: any): DaySheetReviewStatus {
  return resolveDaySheetReviewStatus(order?.qualityCheckStatus);
}

function getDaySheetReviewLabel(status: DaySheetReviewStatus): string {
  return (
    getDaySheetReviewOptions().find((option) => option.value === status)?.statusLabel
    ?? m.orders_review_awaiting_review()
  );
}

function getDaySheetReviewBadgeClass(status: DaySheetReviewStatus): string {
  switch (status) {
    case "ready_for_inspection":
      return "border-sky-300 bg-sky-100 text-sky-950";
    case "approved":
      return "border-emerald-300 bg-emerald-100 text-emerald-950";
    case "caution":
      return "border-amber-300 bg-amber-100 text-amber-950";
    case "hold":
      return "border-red-300 bg-red-100 text-red-950";
    default:
      return "border-slate-300 bg-white text-slate-700";
  }
}

function getDaySheetReviewButtonClass(
  option: DaySheetReviewStatus,
  isActive: boolean,
): string {
  const activeClasses = getDaySheetReviewBadgeClass(option);
  return cn(
    "h-7 px-2 text-[11px]",
    isActive
      ? activeClasses
      : "border-border bg-background text-muted-foreground hover:bg-muted/70 hover:text-foreground",
  );
}

function getDaySheetCarrierName(order: any): string {
  return String(order?.delivery?.carrierName ?? order?.delivery?.carrier ?? "")
    .trim()
    .toLowerCase();
}

function compareOrdersByCarrier(leftOrder: any, rightOrder: any): number {
  const leftCarrier = getDaySheetCarrierName(leftOrder);
  const rightCarrier = getDaySheetCarrierName(rightOrder);
  if (leftCarrier && rightCarrier && leftCarrier !== rightCarrier) {
    return leftCarrier.localeCompare(rightCarrier);
  }
  if (leftCarrier && !rightCarrier) return -1;
  if (!leftCarrier && rightCarrier) return 1;

  const leftTs = getOrderScheduledTimestamp(leftOrder);
  const rightTs = getOrderScheduledTimestamp(rightOrder);
  if (leftTs !== rightTs) return leftTs - rightTs;

  return String(leftOrder?.orderNumber ?? leftOrder?._id ?? "").localeCompare(
    String(rightOrder?.orderNumber ?? rightOrder?._id ?? ""),
  );
}

function formatInventoryRecheckDelta(value: number): string {
  return `${value > 0 ? "+" : ""}${value}`;
}

function getInventoryRecheckBadgeClass(value: number): string {
  if (value === 0) return "border-green-300 bg-green-50 text-green-800";
  if (value > 0) return "border-emerald-300 bg-emerald-50 text-emerald-800";
  if (Math.abs(value) <= 5) return "border-amber-300 bg-amber-50 text-amber-800";
  return "border-red-300 bg-red-50 text-red-800";
}

function getReviewBlockReason(order: any, nextStatus: FullOrderStatus): string | null {
  if (!SHIPPING_REVIEW_REQUIRED_STATUSES.has(nextStatus)) return null;

  const reviewStatus = getDaySheetReviewStatus(order);
  if (reviewStatus === "approved") return null;
  if (reviewStatus === "ready_for_inspection") {
    return m.orders_review_block_blue();
  }
  if (reviewStatus === "caution") {
    return m.orders_review_block_yellow();
  }
  if (reviewStatus === "hold") {
    return m.orders_review_block_red();
  }
  return m.orders_review_block_white();
}

function getOrderBagCount(order: any): number {
  const items = Array.isArray(order?.items) ? order.items : [];
  return items.reduce((sum: number, item: any) => {
    const quantity = Number(item?.quantity ?? 0);
    return sum + (Number.isFinite(quantity) ? quantity : 0);
  }, 0);
}

function getReadyCellValue(order: any): "YES" | "NO" {
  const status = String(order?.status ?? "").trim();
  if (getDaySheetReviewStatus(order) !== "approved") return "NO";
  if (status === "ready_for_pickup" || status === "in_transit" || status === "delivered") return "YES";
  return "NO";
}

function getDaySheetStatusTargets(order: any, status: FullOrderStatus): FullOrderStatus[] {
  const targets = ORDER_STATUS_TRANSITIONS[status] ?? [];
  const filteredTargets = targets.filter(
    (target): target is FullOrderStatus =>
      DAY_SHEET_STATUS_VALUES.has(target as OrderStatus) &&
      canShowOrderStatusTarget(order, status, target),
  );
  return [status, ...filteredTargets];
}

function getDaySheetRowClass(reviewStatus: DaySheetReviewStatus): string {
  switch (reviewStatus) {
    case "ready_for_inspection":
      return "bg-sky-50/90 hover:bg-sky-100/90 dark:bg-sky-950/20 dark:hover:bg-sky-950/30";
    case "approved":
      return "bg-emerald-50/90 hover:bg-emerald-100/90 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/30";
    case "caution":
      return "bg-amber-50/80 hover:bg-amber-100/80 dark:bg-amber-950/20 dark:hover:bg-amber-950/30";
    case "hold":
      return "bg-rose-50/90 hover:bg-rose-100/90 dark:bg-rose-950/20 dark:hover:bg-rose-950/30";
    default:
      return "bg-white hover:bg-slate-50 dark:bg-background dark:hover:bg-muted/40";
  }
}

function getDaySheetOrderNumberCellClass(status: unknown): string {
  const normalized = String(status ?? "").trim().toLowerCase();
  switch (normalized) {
    case "awaiting_inventory":
      return "border-l-4 border-orange-500 text-orange-950 dark:text-orange-200";
    case "ready_to_prepare":
      return "border-l-4 border-amber-500 text-amber-950 dark:text-amber-200";
    case "ready_for_pickup":
      return "border-l-4 border-sky-500 text-sky-950 dark:text-sky-200";
    case "in_transit":
      return "border-l-4 border-indigo-500 text-indigo-950 dark:text-indigo-200";
    case "delivered":
      return "border-l-4 border-emerald-500 text-emerald-950 dark:text-emerald-200";
    case "on_hold":
      return "border-l-4 border-rose-500 text-rose-950 dark:text-rose-200";
    default:
      return "border-l-4 border-slate-300 dark:border-slate-700";
  }
}

function getDaySheetStatusBadgeClass(status: unknown): string {
  const normalized = String(status ?? "").trim().toLowerCase();
  switch (normalized) {
    case "awaiting_inventory":
      return "border-orange-300 bg-orange-100 text-orange-900 dark:border-orange-700 dark:bg-orange-950/40 dark:text-orange-200";
    case "ready_to_prepare":
      return "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200";
    case "ready_for_pickup":
      return "border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-200";
    case "in_transit":
      return "border-indigo-300 bg-indigo-100 text-indigo-900 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200";
    case "delivered":
      return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200";
    case "on_hold":
      return "border-rose-300 bg-rose-100 text-rose-900 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-200";
    default:
      return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200";
  }
}

function getDaySheetStatusSelectClass(status: unknown): string {
  const normalized = String(status ?? "").trim().toLowerCase();
  switch (normalized) {
    case "awaiting_inventory":
      return "border-orange-300 bg-orange-100/70 text-orange-900 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-200";
    case "ready_to_prepare":
      return "border-amber-300 bg-amber-100/70 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200";
    case "ready_for_pickup":
      return "border-sky-300 bg-sky-100/70 text-sky-900 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200";
    case "in_transit":
      return "border-indigo-300 bg-indigo-100/70 text-indigo-900 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200";
    case "delivered":
      return "border-emerald-300 bg-emerald-100/70 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200";
    case "on_hold":
      return "border-rose-300 bg-rose-100/70 text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200";
    default:
      return "border-slate-300 bg-slate-100/70 text-slate-900 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200";
  }
}

function getDaySheetReadyBadgeClass(value: "YES" | "NO"): string {
  if (value === "YES") {
    return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200";
  }
  return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200";
}

function EmptyDaySheetCell() {
  return (
    <span aria-hidden className="select-none text-muted-foreground/35">
      ·
    </span>
  );
}

function DaySheetText({ value }: { value: unknown }) {
  const str =
    value === null || value === undefined ? "" : String(value).trim();
  if (!str || str === "-" || str === "—") return <EmptyDaySheetCell />;
  return <>{str}</>;
}

function DaySheetTimeCell({ timestamp }: { timestamp: unknown }) {
  if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) {
    return <EmptyDaySheetCell />;
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return <EmptyDaySheetCell />;
  return <>{LOGISTICS_TIME_FORMATTER.format(date)}</>;
}

const SUBMITTED_VS_CREATED_THRESHOLD_MS = 5 * 60 * 1000;

function formatSubmittedDelta(
  createdAt: number | null,
  submittedAt: number | null,
): string | null {
  if (!createdAt || !submittedAt) return null;
  const delta = submittedAt - createdAt;
  if (Math.abs(delta) < SUBMITTED_VS_CREATED_THRESHOLD_MS) return null;
  const sign = delta > 0 ? "+" : "−";
  const absMs = Math.abs(delta);
  const minutes = Math.round(absMs / 60_000);
  if (minutes < 60) return `${sign}${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${sign}${hours}h`;
  const days = Math.round(hours / 24);
  return `${sign}${days}d`;
}

const DAY_SHEET_REVIEW_SPARKLINE_CONFIG: {
  key: DaySheetReviewStatus;
  dotClass: string;
  label: string;
}[] = [
  { key: "pending", dotClass: "bg-slate-300", label: "Awaiting" },
  { key: "ready_for_inspection", dotClass: "bg-sky-400", label: "Ready for QC" },
  { key: "approved", dotClass: "bg-emerald-500", label: "Approved" },
  { key: "caution", dotClass: "bg-amber-400", label: "Caution" },
  { key: "hold", dotClass: "bg-rose-500", label: "Hold" },
];

function DaySheetReviewSparkline({ orders }: { orders: any[] }) {
  const counts = React.useMemo(() => {
    const map: Record<DaySheetReviewStatus, number> = {
      pending: 0,
      ready_for_inspection: 0,
      approved: 0,
      caution: 0,
      hold: 0,
    };
    for (const order of orders) {
      map[getDaySheetReviewStatus(order)] += 1;
    }
    return map;
  }, [orders]);
  return (
    <div className="flex items-center gap-2">
      {DAY_SHEET_REVIEW_SPARKLINE_CONFIG.map((item) => {
        const count = counts[item.key];
        if (count === 0) return null;
        return (
          <span
            key={item.key}
            className="inline-flex items-center gap-1 text-[11px] tabular-nums text-muted-foreground"
            title={`${item.label}: ${count}`}
          >
            <span className={cn("h-2 w-2 rounded-full", item.dotClass)} aria-hidden />
            {count}
          </span>
        );
      })}
    </div>
  );
}


// AttachmentsCell now uses the shared OrderDocumentsCell
const AttachmentsCell = OrderDocumentsCell;

/**
 * DatePicker for the day sheet with dots on dates that have orders scheduled.
 */
function DaySheetDatePicker({
  value,
  onChange,
  orderDates,
}: {
  value: string;
  onChange: (value: string) => void;
  orderDates: Set<string>;
}) {
  const [open, setOpen] = React.useState(false);
  const date = value ? new Date(value + "T00:00:00") : undefined;

  const handleSelect = (selected: Date | undefined) => {
    if (selected) {
      const yyyy = selected.getFullYear();
      const mm = String(selected.getMonth() + 1).padStart(2, "0");
      const dd = String(selected.getDate()).padStart(2, "0");
      onChange(`${yyyy}-${mm}-${dd}`);
    } else {
      onChange("");
    }
    setOpen(false);
  };

  const modifiers = React.useMemo(() => {
    const dates: Date[] = [];
    for (const key of orderDates) {
      const d = new Date(key + "T00:00:00");
      if (!Number.isNaN(d.getTime())) dates.push(d);
    }
    return { hasOrders: dates };
  }, [orderDates]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-[220px] justify-start text-left font-normal h-9",
            !value && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date
            ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            : "All Days"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          defaultMonth={date}
          modifiers={modifiers}
          modifiersClassNames={{ hasOrders: "day-has-orders" }}
          components={{
            DayButton: DaySheetCalendarDayButton,
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function DaySheetCalendarDayButton({ className, day, modifiers, ...props }: any) {
  const hasOrders = modifiers?.hasOrders === true;

  return (
    <CalendarDayButton
      className={cn("relative", className)}
      day={day}
      modifiers={modifiers}
      {...props}
    >
      {props.children}
      {hasOrders ? (
        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-primary" />
      ) : null}
    </CalendarDayButton>
  );
}

function CarrierAssignmentCell({
  order,
  carriers,
  isEditable,
  onRequestChange,
}: {
  order: any;
  carriers: CarrierOption[];
  isEditable: boolean;
  onRequestChange: (order: any, carrier: CarrierOption) => void;
}) {
  useAppLocale();
  const [open, setOpen] = React.useState(false);

  const currentCarrierName = String(order?.delivery?.carrierName ?? "").trim();
  const currentCarrierCode = String(order?.delivery?.carrierCode ?? "").trim();
  const currentCarrierId = String(order?.delivery?.carrierId ?? "").trim();
  const isCurrentLocalPickup =
    normalizeCarrierValue(order?.delivery?.deliveryMethod) === "pickup" ||
    normalizeCarrierValue(order?.delivery?.deliveryMethod) === "local_pickup" ||
    order?.delivery?.isLocalPickup === true;
  const currentDisplayName = isCurrentLocalPickup
    ? m.delivery_method_local_pickup()
    : currentCarrierName || m.orders_unassigned();
  const currentBadgeClass = isCurrentLocalPickup
    ? getCarrierBadgeClass("", "")
    : getCarrierBadgeClass(currentCarrierName, currentCarrierCode);
  const selectedServiceName = getSelectedShippingServiceName(order?.delivery);
  const isGuaranteed = !isCurrentLocalPickup && isGuaranteedDelivery(order?.delivery);
  const guaranteedBadgeTitle = selectedServiceName
    ? `Guaranteed shipping service: ${selectedServiceName}`
    : "Guaranteed shipping service";

  const resolvedCurrentCarrierId = React.useMemo(() => {
    if (isCurrentLocalPickup) return LOCAL_PICKUP_OPTION_ID;
    if (currentCarrierId) return currentCarrierId;
    const normalizedName = normalizeCarrierValue(currentCarrierName);
    const normalizedCode = normalizeCarrierValue(currentCarrierCode);
    const match = carriers.find((carrier) => {
      const carrierName = normalizeCarrierValue(carrier.name);
      const carrierCode = normalizeCarrierValue(carrier.shippeekCode ?? "");
      if (normalizedName && carrierName && normalizedName === carrierName) return true;
      if (normalizedCode && carrierCode && normalizedCode === carrierCode) return true;
      return false;
    });
    return match?._id ?? "";
  }, [carriers, currentCarrierCode, currentCarrierId, currentCarrierName, isCurrentLocalPickup]);

  const canEdit = isEditable && (carriers.length > 0 || isCurrentLocalPickup);

  if (!canEdit) {
    return (
      <div className="flex flex-wrap items-center gap-1">
        <Badge
          variant="outline"
          className={currentBadgeClass}
        >
          {currentDisplayName || "-"}
        </Badge>
        {isGuaranteed ? (
          <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
            title={guaranteedBadgeTitle}
          >
            Guaranteed
          </Badge>
        ) : null}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <span className="flex flex-wrap items-center gap-1">
            <Badge
              variant="outline"
              className={cn(
                currentBadgeClass,
                "cursor-pointer",
              )}
            >
              {currentDisplayName}
            </Badge>
            {isGuaranteed ? (
              <Badge
                variant="outline"
                className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
                title={guaranteedBadgeTitle}
              >
                Guaranteed
              </Badge>
            ) : null}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder={m.orders_search_carriers()} />
          <CommandList className="max-h-full">
            <CommandEmpty>{m.orders_no_carriers_found()}</CommandEmpty>
            <CommandGroup className="max-h-[320px] overflow-y-auto overflow-x-hidden">
              <CommandItem
                key={LOCAL_PICKUP_OPTION_ID}
                onSelect={() => {
                  if (resolvedCurrentCarrierId === LOCAL_PICKUP_OPTION_ID) {
                    setOpen(false);
                    return;
                  }
                  setOpen(false);
                  onRequestChange(order, {
                    _id: LOCAL_PICKUP_OPTION_ID,
                      name: m.delivery_method_local_pickup(),
                      shippeekCode: null,
                    });
                }}
              >
                <div
                  className={cn(
                    "flex size-4 items-center justify-center rounded-sm border border-primary",
                    resolvedCurrentCarrierId === LOCAL_PICKUP_OPTION_ID
                      ? "bg-primary"
                      : "opacity-50 [&_svg]:invisible",
                  )}
                >
                  <Check className="size-3.5" />
                </div>
                <Badge variant="outline" className={cn(getCarrierBadgeClass("", ""), "text-xs")}>
                  {m.delivery_method_local_pickup()}
                </Badge>
              </CommandItem>
              {carriers.map((carrier) => {
                const isCurrentCarrier = resolvedCurrentCarrierId === carrier._id;
                return (
                  <CommandItem
                    key={carrier._id}
                    onSelect={() => {
                      if (isCurrentCarrier) {
                        setOpen(false);
                        return;
                      }
                      setOpen(false);
                      onRequestChange(order, carrier);
                    }}
                  >
                    <div
                      className={cn(
                        "flex size-4 items-center justify-center rounded-sm border border-primary",
                        isCurrentCarrier
                          ? "bg-primary"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="size-3.5" />
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        getCarrierBadgeClass(carrier.name, carrier.shippeekCode),
                        "text-xs",
                      )}
                    >
                      {carrier.name}
                    </Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function OrderLineItemsPanel({
  order,
  showRecheckColumn,
}: {
  order: any;
  showRecheckColumn: boolean;
}) {
  const targetOrderId = order._id ?? order.id;
  const items = order.items ?? [];
  const itemGridClass = showRecheckColumn
    ? "grid grid-cols-[minmax(220px,2fr)_minmax(150px,1.2fr)_minmax(64px,0.45fr)_minmax(108px,0.7fr)_minmax(120px,0.85fr)_minmax(130px,0.95fr)_minmax(150px,1.1fr)] gap-3"
    : "grid grid-cols-[minmax(240px,2.1fr)_minmax(180px,1.3fr)_minmax(64px,0.45fr)_minmax(108px,0.7fr)_minmax(120px,0.85fr)_minmax(130px,0.95fr)] gap-3";
  const lineItemImpactMap = new Map<string, InventoryRecheckLineItemImpact>(
    (order.inventoryRecheckImpact?.lineItemImpacts ?? []).map(
      (impact: InventoryRecheckLineItemImpact) => [impact.cargoId, impact],
    ),
  );
  const indicators = getAmendmentIndicatorState(order);
  const hasIndicators =
    indicators.editedOnce || indicators.isPendingOnHold || indicators.isPendingThreeDays;

  return (
    <div className="mx-auto max-w-5xl space-y-2">
      {targetOrderId ? <CustomerStoragePanel orderId={String(targetOrderId)} /> : null}
      {hasIndicators ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2">
          {indicators.editedOnce ? (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
              {m.orders_edited_once_double_check()}
            </Badge>
          ) : null}
          {indicators.isPendingOnHold ? (
            <Badge variant="outline" className="border-blue-300 bg-blue-50 text-blue-800">
              {m.orders_on_hold_amendment_pending()}
            </Badge>
          ) : null}
          {indicators.isPendingThreeDays ? (
            <Badge variant="outline" className="border-red-300 bg-red-50 text-red-800">
              {m.orders_amendment_pending_three_days()}
            </Badge>
          ) : null}
          <span className="text-xs text-muted-foreground">
            {m.orders_order_label({ orderNumber: String(order.orderNumber ?? targetOrderId ?? "N/A") })}
          </span>
        </div>
      ) : null}
      <div className={`${itemGridClass} rounded-md border bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground`}>
        <span className="truncate">{m.product()}</span>
        <span className="truncate">{m.cargo()}</span>
        <span className="truncate">{m.quantity_short()}</span>
        <span className="truncate">{m.weight()}</span>
        <span className="truncate">{m.orders_unit_price()}</span>
        <span className="truncate">{m.orders_line_total()}</span>
        {showRecheckColumn ? <span className="truncate">Recheck</span> : null}
      </div>
      {items.length > 0 ? (
        <div className="space-y-1">
          {items.map((item: any) => {
            const quantity = Number(item.quantity ?? 0);
            const unitPrice = Number(item.unitPrice ?? 0);
            const unitWeight = Number(item.unitWeight ?? 0);
            const itemWeight = quantity * unitWeight;
            const lineTotal = quantity * unitPrice * unitWeight;
            const cargoImpact = lineItemImpactMap.get(String(item.cargoId ?? "")) ?? null;
            return (
              <div
                key={item._id ?? `${item.productId}-${item.cargoId}`}
                className={`${itemGridClass} rounded-md px-3 py-2 text-sm`}
              >
                <span className="min-w-0 truncate">{item.product?.name ?? "-"}</span>
                <span className="min-w-0 truncate">{item.cargo?.ociNumber ?? "-"}</span>
                <span className="min-w-0">{quantity}</span>
                <span className="min-w-0">{itemWeight > 0 ? itemWeight.toFixed(2) : "-"}</span>
                <span className="min-w-0">{formatCurrency(unitPrice)}</span>
                <span className="min-w-0">{formatCurrency(lineTotal)}</span>
                {showRecheckColumn ? (
                  <span className="min-w-0">
                    {cargoImpact ? (
                      <Badge
                        variant="outline"
                        className={getInventoryRecheckBadgeClass(cargoImpact.netAvailableChange)}
                      >
                        {formatInventoryRecheckDelta(cargoImpact.netAvailableChange)} during recheck
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
          {m.orders_no_line_items()}
        </div>
      )}
    </div>
  );
}

const WAREHOUSE_ALERT_ACK_STORAGE_KEY = "anthem.warehouseOrderAlertAcknowledgements.v1";
const WAREHOUSE_CANCELLED_VISIBILITY_MS = 24 * 60 * 60 * 1000;

type WarehouseOrderAlert = {
  key: string;
  orderId: string;
  orderNumber: string;
  status: FullOrderStatus;
  title: string;
  englishMessage: string;
  spanishMessage: string;
  sortTime: number;
};

function readWarehouseAlertAcknowledgements(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const rawValue = window.localStorage.getItem(WAREHOUSE_ALERT_ACK_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : [];
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((value): value is string => typeof value === "string"));
  } catch {
    return new Set();
  }
}

function writeWarehouseAlertAcknowledgements(keys: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(WAREHOUSE_ALERT_ACK_STORAGE_KEY, JSON.stringify(Array.from(keys).slice(-500)));
  } catch {
    // Ignore local acknowledgement persistence failures; the visible alert remains on screen.
  }
}

function getLatestStatusTimestamp(order: any, status: string): number {
  const history = Array.isArray(order?.statusHistory) ? order.statusHistory : [];
  const timestamps = history
    .filter((entry: any) => String(entry?.status ?? "") === status && typeof entry?.timestamp === "number")
    .map((entry: any) => Number(entry.timestamp));
  return timestamps.length > 0 ? Math.max(...timestamps) : Number(order?.updatedAt ?? order?._creationTime ?? 0);
}

function isWarehouseCancelledVisibilityExpired(order: any, now = Date.now()): boolean {
  if (getDisplayedOrderStatus(order) !== "cancelled") return false;
  const cancelledAt = getLatestStatusTimestamp(order, "cancelled");
  return cancelledAt > 0 && now - cancelledAt > WAREHOUSE_CANCELLED_VISIBILITY_MS;
}

function buildWarehouseOrderAlert(order: any): WarehouseOrderAlert | null {
  const orderId = String(order?._id ?? order?.id ?? "").trim();
  if (!orderId) return null;

  const status = getDisplayedOrderStatus(order);
  const orderNumber = String(order?.orderNumber ?? orderId);
  const indicators = getAmendmentIndicatorState(order);
  const statusChangedAt = getLatestStatusTimestamp(order, status);
  const alertVersion = Math.max(statusChangedAt, Number(indicators.pendingStartedAt ?? 0));

  if (status === "cancelled") {
    return {
      key: `${orderId}:cancelled:${alertVersion}`,
      orderId,
      orderNumber,
      status,
      title: `Order #${orderNumber} is CANCELLED`,
      englishMessage: "Stop pulling, wrapping, loading, or releasing this order. Keep any staged pallet aside until a manager gives instructions.",
      spanishMessage: "No prepare, envolver, cargar ni entregar este pedido. Separe cualquier tarima preparada hasta que un gerente dé instrucciones.",
      sortTime: alertVersion,
    };
  }

  if (indicators.isPendingOnHold) {
    return {
      key: `${orderId}:edited_on_hold:${alertVersion}`,
      orderId,
      orderNumber,
      status,
      title: `Order #${orderNumber} was edited and is ON HOLD`,
      englishMessage: "Do not continue picking or shipping this order until the amendment/payment hold is cleared.",
      spanishMessage: "No siga preparando ni enviando este pedido hasta que se resuelva el cambio o el pago pendiente.",
      sortTime: alertVersion,
    };
  }

  if (status === "on_hold") {
    return {
      key: `${orderId}:on_hold:${alertVersion}`,
      orderId,
      orderNumber,
      status,
      title: `Order #${orderNumber} is ON HOLD`,
      englishMessage: "Do not continue picking or shipping this order until the hold is cleared.",
      spanishMessage: "No siga preparando ni enviando este pedido hasta que se quite la pausa.",
      sortTime: alertVersion,
    };
  }

  if (indicators.editedOnce && (status === "ready_to_prepare" || status === "ready_for_pickup")) {
    return {
      key: `${orderId}:edited:${alertVersion}`,
      orderId,
      orderNumber,
      status,
      title: `Order #${orderNumber} was EDITED`,
      englishMessage: "Double-check the line items before pulling, wrapping, or loading this order.",
      spanishMessage: "Revise otra vez los productos antes de preparar, envolver o cargar este pedido.",
      sortTime: alertVersion,
    };
  }

  return null;
}

function WarehouseOrderAlertsDialog({
  alerts,
  onAcknowledge,
}: {
  alerts: WarehouseOrderAlert[];
  onAcknowledge: () => void;
}) {
  return (
    <Dialog open={alerts.length > 0}>
      <DialogContent className="max-w-2xl" onInteractOutside={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Warehouse order change / Cambio de pedido</DialogTitle>
          <DialogDescription>
            These orders changed after warehouse work may have started. Confirm only after the team has seen them.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {alerts.map((alert) => (
            <div key={alert.key} className="rounded-lg border border-red-300 bg-red-50 p-3 text-red-950">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={getOrderStatusBadgeClass(alert.status)}>
                  {formatStatusLabel(alert.status)}
                </Badge>
                <p className="font-semibold">{alert.title}</p>
              </div>
              <p className="mt-2 text-sm">{alert.englishMessage}</p>
              <p className="mt-1 text-sm font-medium">{alert.spanishMessage}</p>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={onAcknowledge}>Seen / Entendido</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StorageQueueSignal({ order, onReview }: { order: any; onReview: () => void }) {
  const storage = order.customerStorage;
  if (!storage || !["held_for_customer", "release_requested"].includes(storage.status)) return null;
  const stored = storage.warehouseStatus === "stored";
  const attention = storage.status === "release_requested" ? "Release requested"
    : storage.freeUntil <= Date.now() ? "Free storage ended"
    : storage.releaseAt <= Date.now() ? "Release date reached"
    : null;
  return (
    <div className="mt-1 flex flex-col items-start gap-1 font-sans">
      <Button
        type="button"
        variant="ghost"
        className={cn("min-h-10 justify-start px-2 text-xs font-semibold", stored
          ? "text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950"
          : "text-amber-800 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-950")}
        onClick={(event) => { event.stopPropagation(); onReview(); }}
      >
        {stored ? <Check className="size-4" aria-hidden="true" /> : null}
        {stored ? "Stored for Customer" : attention ?? "Assign storage"}
      </Button>
      {stored && attention ? <p className="px-2 text-xs font-semibold text-amber-800 dark:text-amber-300">{attention}</p> : null}
    </div>
  );
}

function useColumns(
  showDriverColumns: boolean,
  showInventoryRecheckColumn: boolean,
  carriers: CarrierOption[],
  canEditCarrier: boolean,
  onRequestCarrierChange: (order: any, carrier: CarrierOption) => void,
  canEditLogisticsDetails: boolean,
  onRequestEditLogisticsDetails: (order: any) => void,
  onRequestLoadingPhotoUpload: (order: any, advanceAfterUpload?: boolean) => void,
  onRequestViewLoadingPhotos: (order: any) => void,
  canUploadShippingLabels: boolean,
  onRequestManualShippingLabelUpload: (order: any) => void,
  canAdminCancelOrders: boolean,
  isStartingCancellation: boolean,
  onRequestAdminCancelOrder: (order: any) => void,
) {
  useAppLocale();
  return React.useMemo<ColumnDef<any>[]>(
    () => {
      const baseColumns: ColumnDef<any>[] = [
      {
        id: "orderNumber",
        accessorKey: "orderNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.table_order_number()} />,
        cell: ({ row }) => <div className="flex flex-col items-start"><span className="font-mono text-xs font-medium">{row.original.orderNumber || "N/A"}</span><StorageQueueSignal order={row.original} onReview={() => row.toggleExpanded(true)} /></div>,
        meta: { label: m.order(), placeholder: m.search_order_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "customer",
        accessorFn: (row) => row.user?.company || row.user?.name || row.userId || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.orders_customer()} />,
        cell: ({ row }) => {
          const company = row.original.user?.company || "";
          const clientName = row.original.user?.name || row.original.userId || m.status_unknown();
          return (
            <div className="flex flex-col">
              <span className="text-xs font-medium">
                {company || clientName}
              </span>
              {company && company !== clientName && (
                <span className="text-xs text-muted-foreground">
                  {clientName}
                </span>
              )}
              {isDisplayableSequence(row.original.customerOrderSequence) && (
                <span className={`mt-0.5 inline-flex w-fit items-center rounded border text-[10px] font-medium py-0 px-1.5 animate-badge-shine ${getOrderSequenceBadgeClass(row.original.customerOrderSequence)}`}>
                  {getOrderSequenceLabel(row.original.customerOrderSequence)}
                </span>
              )}
            </div>
          );
        },
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true;
          const search = String(filterValue).toLowerCase();
          const company = (row.original.user?.company ?? "").toLowerCase();
          const name = (row.original.user?.name ?? "").toLowerCase();
          return company.includes(search) || name.includes(search);
        },
        meta: { label: m.orders_customer(), placeholder: m.orders_search_customer(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "isEnterprise",
        accessorFn: (row) => Boolean(row.isEnterprise),
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.enterprise()} />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getEnterpriseBadgeClass(row.original.isEnterprise)}>
            {getEnterpriseBadgeLabel(row.original.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: m.enterprise(),
          variant: "select",
          options: [
            { label: m.customer_type_enterprise(), value: "true" },
            { label: m.customer_type_standard(), value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues) =>
          filterValues.some((value: string) => value === String(Boolean(row.original.isEnterprise))),
      },
      {
        id: "itemsCount",
        accessorFn: (row) => row.itemCount ?? row.items?.length ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.orders_items()} />,
        cell: ({ row }) => <span>{row.original.itemCount ?? row.original.items?.length ?? 0}</span>,
        meta: { label: m.orders_items(), variant: "number", placeholder: m.orders_min_items() },
        enableColumnFilter: true,
      },
      {
        id: "totalAmount",
        accessorKey: "totalAmount",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.orders_total()} />,
        cell: ({ row }) => <span className="font-medium">{getMoneyDisplay(row.original.totalAmount)}</span>,
        meta: { label: m.orders_total(), variant: "number", placeholder: m.orders_min_amount() },
        enableColumnFilter: true,
      },
      {
        id: "shippingQuotedCost",
        accessorFn: (row) => getQuotedShippingCost(row),
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.orders_shipping_quoted()} />,
        cell: ({ row }) => {
          const shippingCost = getQuotedShippingCost(row.original);
          if (shippingCost === null || shippingCost === 0) return <span className="text-muted-foreground">-</span>;
          return <span className="font-medium">{getMoneyDisplay(shippingCost)}</span>;
        },
        meta: { label: m.orders_shipping_quoted(), variant: "number", placeholder: m.orders_min_amount() },
        enableColumnFilter: true,
      },
      {
        id: "deliveryMethod",
        accessorFn: (row) => {
          const raw = row.delivery?.deliveryMethod ?? "";
          return raw ? `${formatDeliveryMethod(raw)} ${raw}` : "";
        },
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.orders_delivery_type()} />,
        cell: ({ row }) => {
          const m = row.original.delivery?.deliveryMethod;
          if (!m) return <span className="text-muted-foreground">-</span>;
          return (
            <Badge
              variant="outline"
              className={getDeliveryMethodBadgeClass(m)}
            >
              {formatDeliveryMethod(m)}
            </Badge>
          );
        },
        meta: { label: m.orders_delivery_type(), placeholder: m.search_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "carrier",
        accessorFn: (row) => [
          row.delivery?.carrierName ?? "",
          getSelectedShippingServiceName(row.delivery),
          isGuaranteedDelivery(row.delivery) ? "Guaranteed" : "",
        ].filter(Boolean).join(" "),
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.orders_carrier()} />,
        cell: ({ row }) => (
          <CarrierAssignmentCell
            order={row.original}
            carriers={carriers}
            isEditable={canEditCarrier}
            onRequestChange={onRequestCarrierChange}
          />
        ),
        meta: { label: m.orders_carrier(), placeholder: m.orders_search_carrier(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "documents",
        accessorFn: (row) => row.documentsCount ?? row.attachments?.length ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.orders_docs()} />,
        cell: ({ row }) => (
          <AttachmentsCell
            row={row}
            canUploadShippingLabels={canUploadShippingLabels}
            onRequestManualShippingLabelUpload={onRequestManualShippingLabelUpload}
          />
        ),
        meta: { label: m.orders_documents(), variant: "number", placeholder: m.orders_min_docs() },
        enableColumnFilter: true,
      },
      {
        id: "loadingPhotos",
        accessorFn: (row) => getOrderLoadingPhotoCount(row),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Loading Photos" />,
        cell: ({ row }) => {
          const count = getOrderLoadingPhotoCount(row.original);
          const needsPhotos = count < ORDER_LOADING_PHOTO_MIN_COUNT;
          return (
            <div className="flex flex-col gap-1">
              <Button
                type="button"
                size="xs"
                variant={count > 0 ? "outline" : "ghost"}
                className="h-7 justify-start px-2 text-xs"
                disabled={count === 0}
                onClick={() => onRequestViewLoadingPhotos(row.original)}
              >
                <ImageIcon className="mr-1 h-3.5 w-3.5" />
                {count} photo{count === 1 ? "" : "s"}
              </Button>
              {canEditLogisticsDetails && count < ORDER_LOADING_PHOTO_MAX_COUNT ? (
                <Button
                  type="button"
                  size="xs"
                  variant={needsPhotos ? "secondary" : "ghost"}
                  className="h-7 justify-start px-2 text-xs"
                  onClick={() => onRequestLoadingPhotoUpload(row.original)}
                >
                  Upload
                </Button>
              ) : null}
            </div>
          );
        },
        meta: { label: "Loading Photos", variant: "number", placeholder: "Minimum photos" },
        enableColumnFilter: true,
      },
      {
        id: "paymentStatus",
        accessorFn: (row) => row.payment?.status ?? "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.orders_payment()} />,
        cell: ({ row }) => {
          const s = row.original.payment?.status;
          if (!s) return <span className="text-muted-foreground">-</span>;
          return (
            <Badge
              variant="outline"
              className={getPaymentStatusBadgeClass(s)}
            >
              {formatStatusLabel(s)}
            </Badge>
          );
        },
        meta: { label: m.orders_payment(), placeholder: m.search_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "submittedAt",
        accessorFn: (row) => getOrderSubmittedTimestamp(row) ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} label={ORDER_SUBMITTED_LABEL} />,
        cell: ({ row }) => {
          const submittedAt = getOrderSubmittedTimestamp(row.original);
          if (!submittedAt) return <span className="text-muted-foreground">-</span>;
          return <span className="text-sm whitespace-nowrap">{formatOrderSubmittedTimestamp(submittedAt)}</span>;
        },
        meta: { label: ORDER_SUBMITTED_LABEL, variant: "date" },
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        id: "createdAt",
        accessorFn: (row) => getOrderCreatedTimestamp(row) ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.created()} />,
        cell: ({ row }) => {
          const createdAt = getOrderCreatedTimestamp(row.original);
          if (!createdAt) return <span className="text-muted-foreground">-</span>;
          return <span className="text-sm whitespace-nowrap">{formatOrderSubmittedTimestamp(createdAt)}</span>;
        },
        meta: { label: m.created(), variant: "date" },
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        id: "status",
        accessorFn: (row) => getDisplayedOrderStatus(row),
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.status()} />,
        cell: ({ row }) => {
          const status = getDisplayedOrderStatus(row.original);
          const holdReason = getOrderHoldReason(row.original);
          return (
            <div className="space-y-1">
              <Badge variant="outline" className={getOrderStatusBadgeClass(status)}>
                {formatStatusLabel(status)}
              </Badge>
              {status === "on_hold" && holdReason ? (
                <p className="max-w-[300px] text-[11px] leading-4 text-red-700 dark:text-red-300">
                  {holdReason}
                </p>
              ) : null}
            </div>
          );
        },
        meta: {
          label: m.status(),
          variant: "select",
          options: [
            { label: m.status_pending_payment(), value: "pending_payment" },
            { label: m.status_payment_processing(), value: "payment_processing" },
            { label: "Awaiting Inventory", value: "awaiting_inventory" },
            { label: m.status_ready_to_pull(), value: "ready_to_prepare" },
            { label: m.status_ready_for_pickup(), value: "ready_for_pickup" },
            { label: m.status_in_transit(), value: "in_transit" },
            { label: m.status_delivered(), value: "delivered" },
            { label: m.status_on_hold(), value: "on_hold" },
            { label: m.status_cancelled(), value: "cancelled" },
          ],
        },
        enableColumnFilter: true,
      },
      ...(showInventoryRecheckColumn
        ? [
            {
              id: "inventoryRecheck",
              accessorFn: (row: any) => row.inventoryRecheckImpact?.netAvailableChange ?? 0,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} label="Recheck" />
              ),
              cell: ({ row }: any) => {
                const impact = row.original.inventoryRecheckImpact;
                if (!impact) {
                  return <span className="text-muted-foreground">-</span>;
                }

                return (
                  <div className="space-y-1">
                    <Badge variant="outline" className={getInventoryRecheckBadgeClass(impact.netAvailableChange)}>
                      {formatInventoryRecheckDelta(impact.netAvailableChange)}
                    </Badge>
                    <p className="text-[11px] text-muted-foreground">during recheck</p>
                  </div>
                );
              },
              meta: { label: "Recheck", variant: "number" as const },
              enableColumnFilter: false,
            },
          ]
        : []),
      {
        id: "amendmentIndicator",
        accessorFn: (row) => {
          const indicators = getAmendmentIndicatorState(row);
          if (indicators.isPendingThreeDays) return "pending_3_days";
          if (indicators.isPendingOnHold) return "pending_payment";
          if (indicators.editedOnce) return "edited_once";
          return "none";
        },
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.orders_amendment()} />,
        cell: ({ row }) => <OrderAmendmentIndicatorBadge order={row.original} />,
        meta: {
          label: m.orders_amendment(),
          variant: "select",
          options: [
            { label: m.orders_edited_once(), value: "edited_once" },
            { label: m.status_pending_payment(), value: "pending_payment" },
            { label: m.orders_pending_three_days(), value: "pending_3_days" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues) => {
          if (!Array.isArray(filterValues) || filterValues.length === 0) return true;
          const indicators = getAmendmentIndicatorState(row.original);
          const value = indicators.isPendingThreeDays
            ? "pending_3_days"
            : indicators.isPendingOnHold
              ? "pending_payment"
              : indicators.editedOnce
                ? "edited_once"
                : "none";
          return filterValues.includes(value);
        },
      },
      ];

      if (showDriverColumns) {
        baseColumns.splice(7, 0,
          {
            id: "driverName",
            accessorFn: (row: any) => row.delivery?.driverFullname ?? "",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} label={m.orders_driver()} />
            ),
            cell: ({ row }: any) => (
              <span>{row.original.delivery?.driverFullname || "-"}</span>
            ),
            meta: { label: m.orders_driver(), placeholder: m.orders_search_driver(), variant: "text" as const },
            enableColumnFilter: true,
          },
          {
            id: "trackingNumber",
            accessorFn: (row: any) => row.delivery?.trackingNumber ?? "",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} label={m.orders_tracking_number()} />
            ),
            cell: ({ row }: any) => {
              const trackingNumber = String(row.original.delivery?.trackingNumber ?? "").trim();
              if (!trackingNumber) {
                return <span className="text-muted-foreground">-</span>;
              }
              return (
                <div className="space-y-0.5">
                  <TrackingEventsDialogTrigger
                    orderId={String(row.original._id ?? row.original.id ?? "")}
                    trackingNumber={trackingNumber}
                    carrierName={row.original.delivery?.carrierName}
                    carrierCode={row.original.delivery?.carrierCode}
                    trackingUrl={row.original.delivery?.trackingUrl}
                    triggerClassName="text-xs"
                  />
                  <TrackingAuditLine delivery={row.original.delivery} className="max-w-52" />
                </div>
              );
            },
            meta: { label: m.orders_tracking(), placeholder: m.orders_search_tracking(), variant: "text" as const },
            enableColumnFilter: true,
          },
          {
            id: "logisticsDetails",
            accessorFn: (row: any) => row.delivery?.driverFullname ?? "",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} label={m.nav_logistics()} />
            ),
            cell: ({ row }: any) => (
              canEditLogisticsDetails ? (
                <Button
                  size="xs"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={() => onRequestEditLogisticsDetails(row.original)}
                >
                  {m.orders_edit_logistics()}
                </Button>
              ) : (
                <span className="text-muted-foreground">-</span>
              )
            ),
            meta: { label: m.nav_logistics(), variant: "text" as const },
            enableColumnFilter: false,
          },
        );
      }

      if (canAdminCancelOrders) {
        baseColumns.push({
          id: "adminActions",
          accessorFn: (row: any) => row.status ?? "",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label={m.orders_actions()} />
          ),
          cell: ({ row }: any) => {
            if (!canStartAdminCancellation(row.original)) {
              return <span className="text-muted-foreground">-</span>;
            }

            return (
              <Button
                size="xs"
                variant="destructive"
                className="h-7 px-2 text-xs transition-transform active:scale-[0.96]"
                disabled={isStartingCancellation}
                onClick={() => onRequestAdminCancelOrder(row.original)}
              >
                Admin cancel
              </Button>
            );
          },
          meta: { label: m.orders_actions(), variant: "text" as const },
          enableColumnFilter: false,
        });
      }

      return baseColumns;
    },
    [
      canAdminCancelOrders,
      isStartingCancellation,
      canEditCarrier,
      carriers,
      onRequestCarrierChange,
      canEditLogisticsDetails,
      onRequestEditLogisticsDetails,
      onRequestLoadingPhotoUpload,
      onRequestViewLoadingPhotos,
      canUploadShippingLabels,
      onRequestManualShippingLabelUpload,
      onRequestAdminCancelOrder,
      showInventoryRecheckColumn,
      showDriverColumns,
    ],
  );
}

export default function LogisticsOrdersPage() {
  const convex = useConvex();
  const [searchParams, setSearchParams] = useSearchParams();
  const { locale } = useAppLocale();
  const { authUserId, userRole, isPending } = useAuthIdentity();
  const statusTabs = React.useMemo(() => getStatusTabs(), [locale]);
  const daySheetReviewOptions = React.useMemo(() => getDaySheetReviewOptions(), [locale]);
  const canBrowseStatusTabs =
    hasRole(userRole, INTERNAL_ROLES.ADMIN) ||
    hasRole(userRole, INTERNAL_ROLES.LOGISTICS) ||
    hasRole(userRole, INTERNAL_ROLES.SALES);
  const canChangeOrderStatus =
    hasRole(userRole, INTERNAL_ROLES.ADMIN) ||
    hasRole(userRole, INTERNAL_ROLES.LOGISTICS);
  const canAdminCancelOrders = hasPermission(userRole, "admin:manage");
  const isReadOnlyOrdersViewer = hasRole(userRole, INTERNAL_ROLES.SALES);
  const isWarehouseQueueView = !canBrowseStatusTabs;
  const searchTab = React.useMemo(
    () => resolveOrdersTabFromSearchParams(searchParams),
    [searchParams],
  );
  const searchViewMode = React.useMemo(
    () => resolveOrdersViewMode(searchParams.get("view")),
    [searchParams],
  );
  const selectedSheetDay = React.useMemo(() => {
    const day = String(searchParams.get("day") ?? "").trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : "";
  }, [searchParams]);
  const [activeTab, setActiveTab] = React.useState<OrdersTabValue>(
    canBrowseStatusTabs ? searchTab : "ready_to_prepare",
  );
  const [viewMode, setViewMode] = React.useState<OrdersViewMode>(searchViewMode);
  const effectiveTab: OrdersTabValue = canBrowseStatusTabs ? activeTab : "ready_to_prepare";
  const orderNumberParam = searchParams.get("orderNumber") ?? "";
  const fromParam = searchParams.get("from") ?? "";
  const toParam = searchParams.get("to") ?? "";
  const companyParam = searchParams.get("company") ?? "";
  const normalizedOrderNumber = React.useMemo(
    () => normalizeOrderNumberQuery(orderNumberParam),
    [orderNumberParam],
  );
  React.useEffect(() => {
    const nextTab = canBrowseStatusTabs ? searchTab : "ready_to_prepare";
    setActiveTab((currentTab) => (currentTab === nextTab ? currentTab : nextTab));
  }, [canBrowseStatusTabs, searchTab]);
  React.useEffect(() => {
    setViewMode((currentMode) => (currentMode === searchViewMode ? currentMode : searchViewMode));
  }, [searchViewMode]);

  const handleTabChange = React.useCallback(
    (nextTabValue: string) => {
      if (!canBrowseStatusTabs) return;
      const nextTab = resolveOrdersTabValue(nextTabValue);
      setActiveTab(nextTab);

      const nextSearchParams = new URLSearchParams(searchParams);
      if (nextTab === "all") {
        nextSearchParams.delete("status");
      } else {
        nextSearchParams.set("status", nextTab);
      }
      nextSearchParams.delete("tab");
      setSearchParams(nextSearchParams, { replace: true });
    },
    [canBrowseStatusTabs, searchParams, setSearchParams],
  );
  const handleViewModeChange = React.useCallback(
    (nextMode: OrdersViewMode) => {
      if (nextMode !== "modern" && nextMode !== "day_sheet") return;
      setViewMode(nextMode);
      setSearchParams(
        (prev) => {
          const nextSearchParams = new URLSearchParams(prev);
          if (nextMode === "day_sheet") {
            nextSearchParams.delete("view");
          } else {
            nextSearchParams.set("view", nextMode);
          }
          return nextSearchParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const handleSheetDayFilterChange = React.useCallback(
    (nextDay: string) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      const normalizedDay = String(nextDay ?? "").trim();
      if (normalizedDay) {
        nextSearchParams.set("day", normalizedDay);
      } else {
        nextSearchParams.delete("day");
      }
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const orderSummaries = useOrders(LOGISTICS_ORDER_SUMMARY_LIMIT);
  const ordersResponse = useOrdersByStatus(
    isWarehouseQueueView ? "warehouse_queue" : effectiveTab === "all" ? undefined : effectiveTab,
    LOGISTICS_ORDER_DETAILS_LIMIT,
  );
  const deepLinkedOrder = useOrderByNumber(normalizedOrderNumber || undefined);
  const carriersData = useLogisticsCarriers();
  const carriers = React.useMemo<CarrierOption[]>(
    () => (Array.isArray(carriersData) ? carriersData : []),
    [carriersData],
  );
  const [acknowledgedWarehouseAlertKeys, setAcknowledgedWarehouseAlertKeys] = React.useState<Set<string>>(
    () => readWarehouseAlertAcknowledgements(),
  );
  const orders = React.useMemo(() => {
    let baseOrders: any[];
    if (effectiveTab === "all") {
      baseOrders = (ordersResponse?.orders ?? []).filter(
        (order: any) => !isExcludedFromLogisticsAllTab(order?.status),
      );
      const sortTime = (o: any) => Number(getOrderSubmittedTimestamp(o) ?? 0);
      baseOrders.sort((a, b) => sortTime(b) - sortTime(a));
    } else {
      baseOrders = ordersResponse?.orders ?? [];
    }
    if (deepLinkedOrder) {
      if (effectiveTab === "all" || getDisplayedOrderStatus(deepLinkedOrder) === effectiveTab) {
        const deepLinkedOrderId = String((deepLinkedOrder as any)?._id ?? (deepLinkedOrder as any)?.id ?? "");
        if (deepLinkedOrderId) {
          const alreadyIncluded = baseOrders.some(
            (order: any) => String(order?._id ?? order?.id ?? "") === deepLinkedOrderId,
          );
          if (!alreadyIncluded) {
            baseOrders = [deepLinkedOrder as any, ...baseOrders];
          }
        }
      }
    }

    const fromTs = fromParam ? new Date(fromParam).getTime() : 0;
    const toTs = toParam ? new Date(toParam + "T23:59:59.999Z").getTime() : 0;
    const companyLower = companyParam.toLowerCase();

    if (fromTs > 0 || toTs > 0 || companyLower) {
      baseOrders = baseOrders.filter((order: any) => {
        const submittedAt = getOrderSubmittedTimestamp(order) ?? 0;
        if (fromTs > 0 && submittedAt < fromTs) return false;
        if (toTs > 0 && submittedAt > toTs) return false;
        if (companyLower) {
          const orderCompany = (order.user?.company ?? "").toLowerCase();
          const orderName = (order.user?.name ?? "").toLowerCase();
          if (!orderCompany.includes(companyLower) && !orderName.includes(companyLower)) return false;
        }
        return true;
      });
    }

    if (isWarehouseQueueView) {
      const now = Date.now();
      baseOrders = baseOrders.filter((order: any) => {
        if (!isWarehouseCancelledVisibilityExpired(order, now)) return true;
        const alert = buildWarehouseOrderAlert(order);
        return Boolean(alert && !acknowledgedWarehouseAlertKeys.has(alert.key));
      });
    }

    return baseOrders;
  }, [
    acknowledgedWarehouseAlertKeys,
    deepLinkedOrder,
    effectiveTab,
    isWarehouseQueueView,
    ordersResponse?.orders,
    fromParam,
    toParam,
    companyParam,
  ]);
  const canViewInventoryRecheck = hasPermission(userRole, "inventory:view");
  const activeInventoryRecheckSummary = useQuery(
    api.inventoryRecheck.getActiveSessionBanner,
    authUserId && canViewInventoryRecheck ? {} : "skip",
  ) as InventoryRecheckSessionBanner | null | undefined;
  const visibleOrderIds = React.useMemo(
    () =>
      orders
        .map((order: any) => String(order?._id ?? order?.id ?? "").trim())
        .filter((orderId: string): orderId is string => orderId.length > 0),
    [orders],
  );
  const inventoryRecheckOrderData = useQuery(
    api.inventoryRecheck.getOrderImpacts,
    authUserId && canViewInventoryRecheck && visibleOrderIds.length > 0
      ? { orderIds: visibleOrderIds as any }
      : "skip",
  ) as InventoryRecheckOrderImpactResponse | undefined;
  const visibleInventoryRecheckOrderCount = inventoryRecheckOrderData?.impacts.length ?? 0;
  const visibleInventoryRecheckDelta = React.useMemo(
    () =>
      (inventoryRecheckOrderData?.impacts ?? []).reduce(
        (total, impact) => total + impact.netAvailableChange,
        0,
      ),
    [inventoryRecheckOrderData?.impacts],
  );
  const ordersWithRecheck = React.useMemo(() => {
    const impactMap = new Map(
      (inventoryRecheckOrderData?.impacts ?? []).map((impact) => [impact.orderId, impact]),
    );

    return orders.map((order: any) => {
      const orderId = String(order?._id ?? order?.id ?? "").trim();
      return {
        ...order,
        inventoryRecheckImpact: impactMap.get(orderId) ?? null,
      };
    });
  }, [inventoryRecheckOrderData?.impacts, orders]);
  const warehouseOrderAlerts = React.useMemo(() => {
    if (!isWarehouseQueueView) return [];
    return ordersWithRecheck
      .map(buildWarehouseOrderAlert)
      .filter((alert): alert is WarehouseOrderAlert => Boolean(alert))
      .sort((a, b) => b.sortTime - a.sortTime);
  }, [isWarehouseQueueView, ordersWithRecheck]);
  const unacknowledgedWarehouseOrderAlerts = React.useMemo(
    () => warehouseOrderAlerts.filter((alert) => !acknowledgedWarehouseAlertKeys.has(alert.key)),
    [acknowledgedWarehouseAlertKeys, warehouseOrderAlerts],
  );
  const handleAcknowledgeWarehouseAlerts = React.useCallback(() => {
    setAcknowledgedWarehouseAlertKeys((current) => {
      const next = new Set(current);
      for (const alert of unacknowledgedWarehouseOrderAlerts) {
        next.add(alert.key);
      }
      writeWarehouseAlertAcknowledgements(next);
      return next;
    });
  }, [unacknowledgedWarehouseOrderAlerts]);
  const isDaySheetView = viewMode === "day_sheet";
  /** Set of "YYYY-MM-DD" keys that have at least one order scheduled – used to render dots in the calendar. */
  const orderScheduledDates = React.useMemo(() => {
    const dateKeys = new Set<string>();
    for (const order of ordersWithRecheck) {
      const key = getOrderScheduledDateKey(order);
      if (key) dateKeys.add(key);
    }
    return dateKeys;
  }, [ordersWithRecheck]);
  const filteredSheetOrders = React.useMemo(() => {
    if (!isDaySheetView) return [];
    if (!selectedSheetDay) return ordersWithRecheck;
    return ordersWithRecheck.filter((order: any) => {
      return getOrderScheduledDateKey(order) === selectedSheetDay;
    });
  }, [isDaySheetView, ordersWithRecheck, selectedSheetDay]);
  const daySheetGroups = React.useMemo(() => {
    const groupsMap = new Map<string, any[]>();
    for (const order of filteredSheetOrders) {
      const dateKey = getOrderScheduledDateKey(order) || "unscheduled";
      const existing = groupsMap.get(dateKey);
      if (existing) {
        existing.push(order);
      } else {
        groupsMap.set(dateKey, [order]);
      }
    }
    return Array.from(groupsMap.entries())
      .sort(([a], [b]) => {
        if (a === "unscheduled") return 1;
        if (b === "unscheduled") return -1;
        return b.localeCompare(a);
      })
      .map(([dateKey, groupedOrders]) => ({
        dateKey,
        dateLabel: dateKey === "unscheduled" ? "Unscheduled" : formatDaySheetHeading(dateKey),
        orders: groupedOrders.slice().sort(compareOrdersByCarrier),
      }));
  }, [filteredSheetOrders]);
  const showDriverColumns =
    effectiveTab === "ready_for_pickup" ||
    effectiveTab === "in_transit" ||
    effectiveTab === "delivered";
  const showDaySheetLogisticsEditAction =
    effectiveTab === "all" || showDriverColumns;
  const canEditCarrier =
    hasRole(userRole, INTERNAL_ROLES.ADMIN) ||
    hasRole(userRole, INTERNAL_ROLES.LOGISTICS);
  const canEditLogisticsDetails =
    hasRole(userRole, INTERNAL_ROLES.ADMIN) ||
    hasRole(userRole, INTERNAL_ROLES.LOGISTICS) ||
    hasRole(userRole, INTERNAL_ROLES.WAREHOUSE);
  const canUploadShippingLabels = hasPermission(userRole, "logistics:edit");
  const bulkUpdateOrderStatus = useMutation(api.logistics.bulkUpdateOrderStatus);
  const updateOrderStatus = useMutation(api.logistics.updateOrderStatus);
  const updateLogisticsInfo = useMutation(api.logistics.updateLogisticsInfo);
  const updateOrderCarrier = useAction((api.logistics as any).updateOrderCarrier);
  const previewCarrierRerate = useAction((api.logistics as any).previewCarrierRerate);
  const bookUpsParcelOrders = useAction(api.logistics.bookUpsParcelOrders);
  const bookEstesLtlOrders = useAction((api.logistics as any).bookEstesLtlOrders);
  const cancelEstesLtlOrders = useAction((api.logistics as any).cancelEstesLtlOrders);
  const uploadManualShippingLabel = useAction((api.logistics as any).uploadManualShippingLabel);
  const createOrderLoadingPhotoR2UploadUrl = useAction((api.logistics as any).createOrderLoadingPhotoR2UploadUrl);
  const finalizeOrderLoadingPhotoR2Upload = useAction((api.logistics as any).finalizeOrderLoadingPhotoR2Upload);
  const getOrderLoadingPhotoUrls = useAction((api.logistics as any).getOrderLoadingPhotoUrls);
  const deleteOrderLoadingPhoto = useAction((api.logistics as any).deleteOrderLoadingPhoto);
  const [isBookingUps, setIsBookingUps] = React.useState(false);
  const [isBookingEstes, setIsBookingEstes] = React.useState(false);
  const [isCancellingEstes, setIsCancellingEstes] = React.useState(false);
  const [generatingOrderIds, setGeneratingOrderIds] = React.useState<Set<string>>(new Set());
  const [daySheetDetailsOrder, setDaySheetDetailsOrder] = React.useState<any | null>(null);
  const [pendingLogisticsDetailsEdit, setPendingLogisticsDetailsEdit] =
    React.useState<LogisticsDetailsEditorState | null>(null);
  const [pendingManualShippingLabelUpload, setPendingManualShippingLabelUpload] =
    React.useState<ManualShippingLabelUploadState | null>(null);
  const [pendingLoadingPhotoUpload, setPendingLoadingPhotoUpload] =
    React.useState<LoadingPhotoUploadState | null>(null);
  const [loadingPhotoViewer, setLoadingPhotoViewer] =
    React.useState<LoadingPhotoViewerState | null>(null);
  const [loadingPhotoDeleteTarget, setLoadingPhotoDeleteTarget] =
    React.useState<LoadingPhotoWithUrl | null>(null);
  const [isDeletingLoadingPhoto, setIsDeletingLoadingPhoto] = React.useState(false);
  const [pendingInTransitRequirements, setPendingInTransitRequirements] =
    React.useState<MissingInTransitDetailsOrder[] | null>(null);
  const [isSavingLogisticsDetails, setIsSavingLogisticsDetails] = React.useState(false);
  const [isUploadingManualShippingLabel, setIsUploadingManualShippingLabel] = React.useState(false);
  const [isUploadingLoadingPhotos, setIsUploadingLoadingPhotos] = React.useState(false);
  const selectedLoadingPhotoPreviews = React.useMemo(
    () =>
      (pendingLoadingPhotoUpload?.files ?? []).map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      })),
    [pendingLoadingPhotoUpload?.files],
  );
  React.useEffect(() => {
    return () => {
      for (const preview of selectedLoadingPhotoPreviews) {
        URL.revokeObjectURL(preview.previewUrl);
      }
    };
  }, [selectedLoadingPhotoPreviews]);
  const [pendingCarrierChange, setPendingCarrierChange] = React.useState<PendingCarrierChange | null>(null);
  const [isSavingCarrierChange, setIsSavingCarrierChange] = React.useState(false);
  const [carrierReratePreview, setCarrierReratePreview] =
    React.useState<CarrierReratePreviewResult | null>(null);
  const [isPreviewingCarrierRerate, setIsPreviewingCarrierRerate] = React.useState(false);
  const [upsRebookConfirm, setUpsRebookConfirm] = React.useState<{
    candidates: any[];
    withTracking: any[];
    clearSelection: () => void;
  } | null>(null);
  const [estesRebookConfirm, setEstesRebookConfirm] = React.useState<{
    candidates: any[];
    withTracking: any[];
    clearSelection: () => void;
  } | null>(null);
  const [estesCancelConfirm, setEstesCancelConfirm] = React.useState<{
    candidates: any[];
    clearSelection: () => void;
  } | null>(null);
  const createOrderDocumentR2UploadUrl = useAction(api.logistics.createOrderDocumentR2UploadUrl);
  const finalizeOrderDocumentR2Upload = useAction(api.logistics.finalizeOrderDocumentR2Upload);
  const clearPendingDocGeneration = useMutation(api.logistics.clearPendingDocGeneration);
  const resendAmendmentPaymentLinkMutation = useMutation(
    (api.orderAmendments as any).resendAmendmentPaymentLink,
  );
  const cancelPendingAmendmentMutation = useMutation(
    (api.orderAmendments as any).cancelPendingAmendment,
  );
  const adminCancellation = useAdminOrderCancellation(authUserId ?? undefined);
  const [confirmCancelAmendment, setConfirmCancelAmendment] = React.useState<string | null>(null);
  const [isCancellingAmendment, setIsCancellingAmendment] = React.useState(false);
  const [confirmCancelOrder, setConfirmCancelOrder] = React.useState<string | null>(null);
  const [isCancellingOrder, setIsCancellingOrder] = React.useState(false);
  const carrierChangeContext = useQuery(
    (api.logistics as any).getCarrierChangeContext,
    authUserId && pendingCarrierChange?.nextMode === "shipping"
      ? {
          authUserId,
          orderId: pendingCarrierChange.orderId as any,
        }
      : "skip",
  ) as CarrierChangeContextData | undefined;
  const carrierChangeAddresses = React.useMemo<CarrierChangeContextAddress[]>(
    () => (Array.isArray(carrierChangeContext?.shippingAddresses) ? carrierChangeContext.shippingAddresses : []),
    [carrierChangeContext?.shippingAddresses],
  );
  const isCarrierChangeContextLoading = Boolean(
    authUserId && pendingCarrierChange?.nextMode === "shipping" && carrierChangeContext === undefined,
  );
  const isPickupToShipping = Boolean(
    pendingCarrierChange?.nextMode === "shipping" && pendingCarrierChange?.currentMode === "pickup",
  );
  React.useEffect(() => {
    if (!pendingCarrierChange || pendingCarrierChange.nextMode !== "shipping") return;
    if (!carrierChangeContext) return;
    if (String(carrierChangeContext.orderId ?? "") !== pendingCarrierChange.orderId) return;

    setPendingCarrierChange((current) => {
      if (!current || current.orderId !== pendingCarrierChange.orderId || current.nextMode !== "shipping") {
        return current;
      }
      if (current.contextHydrated) return current;

      const recommendedShippingInfoId = String(
        carrierChangeContext.recommendedShippingInfoId ?? "",
      ).trim();
      const currentAddress = carrierChangeContext.currentDelivery?.deliveryAddress;

      return {
        ...current,
        selectedShippingInfoId: recommendedShippingInfoId,
        useManualShippingAddress: recommendedShippingInfoId.length === 0,
        manualShippingAddress:
          recommendedShippingInfoId.length > 0
            ? current.manualShippingAddress
            : {
                street: String(currentAddress?.street ?? ""),
                city: String(currentAddress?.city ?? ""),
                state: String(currentAddress?.state ?? ""),
                postalCode: String(currentAddress?.postalCode ?? ""),
                country: String(currentAddress?.country ?? "US"),
              },
        shippingDate: current.shippingDate || String(carrierChangeContext.dateSeed ?? ""),
        contextHydrated: true,
        contextError: null,
      };
    });
  }, [carrierChangeContext, pendingCarrierChange]);

  const carrierReratePreviewRequest = React.useMemo(() => {
    if (!authUserId || !pendingCarrierChange || pendingCarrierChange.nextMode !== "shipping") {
      return null;
    }
    if (isCarrierChangeContextLoading) return null;

    const shippingDate = String(pendingCarrierChange.shippingDate ?? "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(shippingDate)) return null;

    if (pendingCarrierChange.useManualShippingAddress) {
      if (!hasCompleteManualShippingAddress(pendingCarrierChange.manualShippingAddress)) return null;

      return {
        authUserId,
        orderId: pendingCarrierChange.orderId,
        carrierId: pendingCarrierChange.nextCarrierId,
        shippingDate,
        manualShippingAddress: {
          street: String(pendingCarrierChange.manualShippingAddress.street ?? "").trim(),
          city: String(pendingCarrierChange.manualShippingAddress.city ?? "").trim(),
          state: String(pendingCarrierChange.manualShippingAddress.state ?? "").trim(),
          postalCode: String(pendingCarrierChange.manualShippingAddress.postalCode ?? "").trim(),
          country: String(pendingCarrierChange.manualShippingAddress.country ?? "US").trim() || "US",
        },
      };
    }

    if (!pendingCarrierChange.selectedShippingInfoId) return null;

    return {
      authUserId,
      orderId: pendingCarrierChange.orderId,
      carrierId: pendingCarrierChange.nextCarrierId,
      shippingDate,
      shippingInfoId: pendingCarrierChange.selectedShippingInfoId,
    };
  }, [authUserId, isCarrierChangeContextLoading, pendingCarrierChange]);

  const carrierReratePreviewRequestKey = React.useMemo(
    () => JSON.stringify(carrierReratePreviewRequest ?? null),
    [carrierReratePreviewRequest],
  );

  React.useEffect(() => {
    if (!carrierReratePreviewRequest) {
      setIsPreviewingCarrierRerate(false);
      setCarrierReratePreview(null);
      return;
    }

    let cancelled = false;
    setCarrierReratePreview(null);

    const timeoutId = setTimeout(async () => {
      setIsPreviewingCarrierRerate(true);
      try {
        const result = (await previewCarrierRerate(
          carrierReratePreviewRequest as any,
        )) as CarrierReratePreviewResult;
        if (!cancelled) {
          setCarrierReratePreview(result);
        }
      } catch (error) {
        if (!cancelled) {
          setCarrierReratePreview({
            success: false,
            rerated: false,
            applied: false,
            autoRerate: true,
            rateNeedsReview: true,
            previousDeliveryMethod: isPickupToShipping ? "pickup" : "shipping",
            deliveryMethod: "shipping",
            carrierName: pendingCarrierChange?.nextCarrierName ?? null,
            newDeliveryCharge: 0,
            message:
              error instanceof Error
                ? error.message
                : "ShipPeek preflight failed. Please verify address and date.",
            selectedRate: null,
          });
        }
      } finally {
        if (!cancelled) {
          setIsPreviewingCarrierRerate(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    carrierReratePreviewRequest,
    carrierReratePreviewRequestKey,
    isPickupToShipping,
    pendingCarrierChange?.nextCarrierName,
    previewCarrierRerate,
  ]);

  /**
   * Generate PDFs and upload to R2 for a given order.
   * Skips doc types that already have a stored URL (avoids re-generation).
   */
  const generateAndUploadDocs = React.useCallback(
    async (order: any) => {
      if (!authUserId) return;
      if (order.test === true) { toast.info("Test orders do not upload shipping documents."); return; }
      if (order.status === "held_for_customer") { toast.info("Use the Holding DO for stored coffee. Shipping documents are available after release."); return; }

      const orderId = order._id ?? order.id;
      const orderNumber = order.orderNumber ?? orderId;
      const existingAttachments: any[] = order.attachments ?? [];

      // Determine which doc types still need generation
      const allDocTypes: GeneratableDocType[] = order.hasOwnedCoffee || order.customerStorage ? ["bol"] : ["bol", "order_resume"];
      const existingTypeSet = new Set(
        existingAttachments
          .filter((a: any) => !!a.url)
          .map((a: any) => {
            const t = String(a.type ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
            return t;
          }),
      );

      const missingDocTypes = allDocTypes.filter((dt) => !existingTypeSet.has(dt));

      if (missingDocTypes.length === 0) {
        toast.info(m.orders_docs_already_exist());
        return;
      }

      setGeneratingOrderIds((prev) => new Set(prev).add(orderId));
      const toastId = toast.loading(
        m.orders_generating_for_order({
          docs: missingDocTypes.map((dt) => DOC_TYPE_LABELS[dt]).join(", "),
          orderNumber: String(orderNumber),
        }),
      );

      let successCount = 0;
      let failCount = 0;
      let expectedDocumentRevision: number;
      try {
        // Capture before rendering any PDF. A late amendment invalidates the
        // whole batch, including uploads still in flight when it is applied.
        expectedDocumentRevision = await convex.query(api.logistics.getOrderDocumentRevision, {
          authUserId,
          orderId,
        });
      } catch (error) {
        toast.error(toUserFacingErrorMessage(error, "Unable to check the order before generating documents. Try again."), { id: toastId });
        setGeneratingOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        return;
      }

      for (const docType of missingDocTypes) {
        try {
          const url = getDocumentUrl(docType, order);
          const pdfData = await generateDocumentPdf(url);
          const upload = await createOrderDocumentR2UploadUrl({
            authUserId,
            orderId,
            documentType: docType,
            orderNumber: String(orderNumber),
            fileSize: pdfData.byteLength,
          });
          await uploadGeneratedPdfToSignedUrl(upload.uploadUrl, pdfData);
          await finalizeOrderDocumentR2Upload({
            authUserId,
            orderId,
            documentType: docType,
            key: upload.key,
            expectedDocumentRevision,
          });
          successCount++;
        } catch (error) {
          failCount++;
          console.error(`Failed to generate ${docType} for ${orderNumber}:`, error);
        }
      }

      // Clear pending flag if set
      if (order.pendingDocGeneration && failCount === 0) {
        try {
          await clearPendingDocGeneration({ authUserId, orderId, expectedDocumentRevision });
        } catch {
          // Non-critical
        }
      }

      setGeneratingOrderIds((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });

      if (failCount === 0) {
        toast.success(m.orders_docs_generated_success({ count: successCount, orderNumber: String(orderNumber) }), { id: toastId });
      } else {
        toast.error(m.orders_docs_generated_failure({ failed: failCount, total: missingDocTypes.length, orderNumber: String(orderNumber) }), { id: toastId });
      }
    },
    [authUserId, convex, createOrderDocumentR2UploadUrl, finalizeOrderDocumentR2Upload, clearPendingDocGeneration],
  );

  const handleRequestLogisticsDetailsEdit = React.useCallback((order: any) => {
    const orderId = String(order?._id ?? order?.id ?? "");
    if (!orderId) return;

    const companyName = order?.user?.company || order?.user?.name || order?.userId || "";

    setPendingLogisticsDetailsEdit({
      orderId,
      orderNumber: String(order?.orderNumber ?? orderId),
      companyName,
      trackingAuditSummary: getTrackingAuditSummary(order?.delivery),
      original: buildLogisticsDetailsOriginal(order),
      draft: buildLogisticsDetailsDraft(order),
    });
  }, []);

  const handleRequestManualShippingLabelUpload = React.useCallback((order: any) => {
    if (order.test === true || order.status === "held_for_customer" || (order.customerStorage && order.customerStorage.status !== "released")) return;
    const orderId = String(order?._id ?? order?.id ?? "").trim();
    if (!orderId) return;

    const companyName = order?.user?.company || order?.user?.name || order?.userId || "";
    setPendingManualShippingLabelUpload({
      orderId,
      orderNumber: String(order?.orderNumber ?? orderId),
      companyName,
      existingLabelCount: getShippingLabelAttachmentCount(order),
      file: null,
      error: null,
    });
  }, []);

  const handleManualShippingLabelFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      setPendingManualShippingLabelUpload((current) => {
        if (!current) return current;
        if (!file) {
          return { ...current, file: null, error: null };
        }
        if (file.size <= 0) {
          return { ...current, file: null, error: "Shipping label file is empty." };
        }
        if (file.size > MANUAL_SHIPPING_LABEL_MAX_BYTES) {
          return { ...current, file: null, error: "Shipping label must be 10 MB or smaller." };
        }
        if (!isSupportedManualShippingLabelFile(file)) {
          return {
            ...current,
            file: null,
            error: "Upload a PDF, image, TXT, or ZPL shipping label.",
          };
        }
        return { ...current, file, error: null };
      });
      event.target.value = "";
    },
    [],
  );

  const handleUploadManualShippingLabel = React.useCallback(async () => {
    if (!authUserId || !pendingManualShippingLabelUpload?.file) return;

    const file = pendingManualShippingLabelUpload.file;
    if (file.size <= 0) {
      setPendingManualShippingLabelUpload((current) =>
        current ? { ...current, error: "Shipping label file is empty." } : current,
      );
      return;
    }
    if (file.size > MANUAL_SHIPPING_LABEL_MAX_BYTES) {
      setPendingManualShippingLabelUpload((current) =>
        current ? { ...current, error: "Shipping label must be 10 MB or smaller." } : current,
      );
      return;
    }
    if (!isSupportedManualShippingLabelFile(file)) {
      setPendingManualShippingLabelUpload((current) =>
        current ? { ...current, error: "Upload a PDF, image, TXT, or ZPL shipping label." } : current,
      );
      return;
    }

    setIsUploadingManualShippingLabel(true);
    try {
      const fileData = await file.arrayBuffer();
      await uploadManualShippingLabel({
        authUserId,
        orderId: pendingManualShippingLabelUpload.orderId as any,
        fileData,
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || "application/octet-stream",
      });
      toast.success(`Shipping label uploaded for order ${pendingManualShippingLabelUpload.orderNumber}.`);
      setPendingManualShippingLabelUpload(null);
    } catch (error) {
      const message = toUserFacingErrorMessage(error, "Failed to upload shipping label.");
      setPendingManualShippingLabelUpload((current) =>
        current ? { ...current, error: message } : current,
      );
      toast.error(message);
    } finally {
      setIsUploadingManualShippingLabel(false);
    }
  }, [authUserId, pendingManualShippingLabelUpload, uploadManualShippingLabel]);

  const handleRequestLoadingPhotoUpload = React.useCallback((order: any, advanceAfterUpload = false) => {
    const orderId = String(order?._id ?? order?.id ?? "").trim();
    if (!orderId) return;

    const companyName = order?.user?.company || order?.user?.name || order?.userId || "";
    setPendingLoadingPhotoUpload({
      order,
      orderId,
      orderNumber: String(order?.orderNumber ?? orderId),
      companyName,
      existingPhotoCount: getOrderLoadingPhotoCount(order),
      files: [],
      error: null,
      advanceAfterUpload,
    });
  }, []);

  const handleLoadingPhotoFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(event.target.files ?? []);
      setPendingLoadingPhotoUpload((current) => {
        if (!current) return current;
        if (selectedFiles.length === 0) {
          return { ...current, error: null };
        }

        const fileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;
        const mergedFiles = [...current.files];
        const existingKeys = new Set(mergedFiles.map(fileKey));
        for (const file of selectedFiles) {
          if (!existingKeys.has(fileKey(file))) {
            mergedFiles.push(file);
            existingKeys.add(fileKey(file));
          }
        }

        if (current.existingPhotoCount + mergedFiles.length > ORDER_LOADING_PHOTO_MAX_COUNT) {
          return {
            ...current,
            error: `Orders can have at most ${ORDER_LOADING_PHOTO_MAX_COUNT} loading photos. Remove ${current.existingPhotoCount + mergedFiles.length - ORDER_LOADING_PHOTO_MAX_COUNT} selected photo${current.existingPhotoCount + mergedFiles.length - ORDER_LOADING_PHOTO_MAX_COUNT === 1 ? "" : "s"}.`,
          };
        }
        const invalidFile = mergedFiles.find(
          (file) =>
            file.size <= 0 ||
            file.size > ORDER_LOADING_PHOTO_MAX_BYTES ||
            !isSupportedOrderLoadingPhotoFile(file),
        );
        if (invalidFile) {
          return {
            ...current,
            error: `Upload image files up to ${formatManualShippingLabelFileSize(ORDER_LOADING_PHOTO_MAX_BYTES)} each. Check ${invalidFile.name}.`,
          };
        }
        return { ...current, files: mergedFiles, error: null };
      });
      event.target.value = "";
    },
    [],
  );

  const handleRemoveSelectedLoadingPhoto = React.useCallback((indexToRemove: number) => {
    setPendingLoadingPhotoUpload((current) => {
      if (!current) return current;
      return {
        ...current,
        files: current.files.filter((_, index) => index !== indexToRemove),
        error: null,
      };
    });
  }, []);

  const handleClearSelectedLoadingPhotos = React.useCallback(() => {
    setPendingLoadingPhotoUpload((current) =>
      current ? { ...current, files: [], error: null } : current,
    );
  }, []);

  const handleRequestViewLoadingPhotos = React.useCallback(
    async (order: any) => {
      const orderId = String(order?._id ?? order?.id ?? "").trim();
      if (!orderId) return;

      const companyName = order?.user?.company || order?.user?.name || order?.userId || "";
      const orderNumber = String(order?.orderNumber ?? orderId);
      setLoadingPhotoViewer({
        orderId,
        orderNumber,
        companyName,
        photos: null,
        isLoading: true,
        error: null,
      });

      try {
        const result = await getOrderLoadingPhotoUrls({ orderId: orderId as any });
        setLoadingPhotoViewer({
          orderId,
          orderNumber,
          companyName,
          photos: Array.isArray(result?.photos) ? result.photos : [],
          isLoading: false,
          error: null,
        });
      } catch (error) {
        const message = toUserFacingErrorMessage(error, "Failed to load loading photos.");
        setLoadingPhotoViewer({
          orderId,
          orderNumber,
          companyName,
          photos: [],
          isLoading: false,
          error: message,
        });
        toast.error(message);
      }
    },
    [getOrderLoadingPhotoUrls],
  );

  const handleDeleteLoadingPhoto = React.useCallback(async () => {
    if (!authUserId || !loadingPhotoViewer || !loadingPhotoDeleteTarget) return;

    setIsDeletingLoadingPhoto(true);
    try {
      await deleteOrderLoadingPhoto({
        authUserId,
        orderId: loadingPhotoViewer.orderId as any,
        photoId: loadingPhotoDeleteTarget.id as any,
      });
      setLoadingPhotoViewer((current) => {
        if (!current) return current;
        const nextPhotos = (current.photos ?? []).filter((photo) => photo.id !== loadingPhotoDeleteTarget.id);
        return { ...current, photos: nextPhotos };
      });
      toast.success(`Deleted ${loadingPhotoDeleteTarget.originalFilename}.`);
      setLoadingPhotoDeleteTarget(null);
    } catch (error) {
      toast.error(toUserFacingErrorMessage(error, "Failed to delete loading photo."));
    } finally {
      setIsDeletingLoadingPhoto(false);
    }
  }, [authUserId, deleteOrderLoadingPhoto, loadingPhotoDeleteTarget, loadingPhotoViewer]);

  const handleUploadLoadingPhotos = React.useCallback(async () => {
    if (!authUserId || !pendingLoadingPhotoUpload) return;

    const files = pendingLoadingPhotoUpload.files;
    if (files.length === 0) {
      setPendingLoadingPhotoUpload((current) =>
        current ? { ...current, error: "Select at least one photo." } : current,
      );
      return;
    }
    if (pendingLoadingPhotoUpload.existingPhotoCount + files.length > ORDER_LOADING_PHOTO_MAX_COUNT) {
      setPendingLoadingPhotoUpload((current) =>
        current
          ? {
              ...current,
              error: `Orders can have at most ${ORDER_LOADING_PHOTO_MAX_COUNT} loading photos.`,
            }
          : current,
      );
      return;
    }

    setIsUploadingLoadingPhotos(true);
    let uploadedFileCount = 0;
    let latestUploadedCount = pendingLoadingPhotoUpload.existingPhotoCount;
    try {
      let nextCount = pendingLoadingPhotoUpload.existingPhotoCount;
      for (const file of files) {
        const uploadTarget = await createOrderLoadingPhotoR2UploadUrl({
          authUserId,
          orderId: pendingLoadingPhotoUpload.orderId as any,
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type || "application/octet-stream",
        });
        const putResponse = await fetch(uploadTarget.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": uploadTarget.contentType },
          body: file,
        });
        if (!putResponse.ok) {
          throw new Error(`Photo upload failed with status ${putResponse.status}. Check ${file.name}.`);
        }
        const uploadResult = await finalizeOrderLoadingPhotoR2Upload({
          authUserId,
          orderId: pendingLoadingPhotoUpload.orderId as any,
          key: uploadTarget.key,
          fileName: file.name,
          uploadToken: uploadTarget.uploadToken,
        });
        nextCount = Number(uploadResult?.count ?? nextCount + 1);
        latestUploadedCount = nextCount;
        uploadedFileCount += 1;
      }
      toast.success(
        `Uploaded ${files.length} loading photo${files.length === 1 ? "" : "s"} for order ${pendingLoadingPhotoUpload.orderNumber}.`,
      );

      const shouldAdvance = pendingLoadingPhotoUpload.advanceAfterUpload && nextCount >= ORDER_LOADING_PHOTO_MIN_COUNT;
      const orderForValidation = {
        ...pendingLoadingPhotoUpload.order,
        loadingPhotoCount: nextCount,
      };
      const uploadedOrder = pendingLoadingPhotoUpload;
      setPendingLoadingPhotoUpload(null);

      if (!shouldAdvance) {
        try {
          const result = await getOrderLoadingPhotoUrls({ orderId: uploadedOrder.orderId as any });
          setLoadingPhotoViewer({
            orderId: uploadedOrder.orderId,
            orderNumber: uploadedOrder.orderNumber,
            companyName: uploadedOrder.companyName,
            photos: Array.isArray(result?.photos) ? result.photos : [],
            isLoading: false,
            error: null,
          });
        } catch {
          // Upload succeeded; photo viewer refresh is non-critical.
        }
      }

      if (shouldAdvance && getMissingInTransitDetails(orderForValidation).length === 0) {
        await updateOrderStatus({
          authUserId,
          orderId: pendingLoadingPhotoUpload.orderId as any,
          newStatus: "in_transit",
        });
        toast.success(m.orders_single_status_success({ status: formatStatusLabel("in_transit") }));
        if (!isDaySheetView && canBrowseStatusTabs) {
          handleTabChange("in_transit");
        }
      }
    } catch (error) {
      const baseMessage = toUserFacingErrorMessage(error, "Failed to upload loading photos.");
      const message = uploadedFileCount > 0
        ? `${baseMessage} ${uploadedFileCount} photo${uploadedFileCount === 1 ? " was" : "s were"} saved before the failure.`
        : baseMessage;
      setPendingLoadingPhotoUpload((current) =>
        current
          ? {
              ...current,
              existingPhotoCount: Math.max(current.existingPhotoCount, latestUploadedCount),
              files: uploadedFileCount > 0 ? current.files.slice(uploadedFileCount) : current.files,
              error: message,
            }
          : current,
      );
      toast.error(message);
    } finally {
      setIsUploadingLoadingPhotos(false);
    }
  }, [
    authUserId,
    canBrowseStatusTabs,
    createOrderLoadingPhotoR2UploadUrl,
    finalizeOrderLoadingPhotoR2Upload,
    getOrderLoadingPhotoUrls,
    handleTabChange,
    isDaySheetView,
    pendingLoadingPhotoUpload,
    updateOrderStatus,
  ]);

  const updateLogisticsDraftField = React.useCallback(
    (field: keyof LogisticsDetailsDraft, value: string) => {
      setPendingLogisticsDetailsEdit((current) => {
        if (!current) return current;
        return {
          ...current,
          draft: {
            ...current.draft,
            [field]: value,
          },
        };
      });
    },
    [],
  );

  const parsedLogisticsDateTimes = React.useMemo(() => {
    if (!pendingLogisticsDetailsEdit) return null;
    return {
      driverCheckin: parseDateTimeInputValue(pendingLogisticsDetailsEdit.draft.driverCheckin),
      driverCheckout: parseDateTimeInputValue(pendingLogisticsDetailsEdit.draft.driverCheckout),
    };
  }, [pendingLogisticsDetailsEdit]);

  const hasLogisticsDateOrderingError = React.useMemo(() => {
    if (!parsedLogisticsDateTimes) return false;

    const checkin = parsedLogisticsDateTimes.driverCheckin.value;
    const checkout = parsedLogisticsDateTimes.driverCheckout.value;
    if (checkin === undefined || checkout === undefined) return false;

    return checkout < checkin;
  }, [parsedLogisticsDateTimes]);

  const hasLogisticsShippingDateInvalid = React.useMemo(() => {
    if (!canEditCarrier || !pendingLogisticsDetailsEdit) return false;

    const shippingDate = String(pendingLogisticsDetailsEdit.draft.shippingDate ?? "").trim();
    if (shippingDate.length === 0) {
      return pendingLogisticsDetailsEdit.original.shippingDate !== undefined;
    }

    return !DATE_INPUT_PATTERN.test(shippingDate);
  }, [canEditCarrier, pendingLogisticsDetailsEdit]);

  const hasLogisticsDraftChanges = React.useMemo(() => {
    if (!pendingLogisticsDetailsEdit || !parsedLogisticsDateTimes) return false;

    const normalizedDriverFullname = normalizeOptionalTextValue(
      pendingLogisticsDetailsEdit.draft.driverFullname,
    );
    const normalizedTrackingNumber = normalizeOptionalTextValue(
      pendingLogisticsDetailsEdit.draft.trackingNumber,
    );
    const normalizedShippingDate = normalizeOptionalTextValue(
      pendingLogisticsDetailsEdit.draft.shippingDate,
    );
    const parsedCheckin = parsedLogisticsDateTimes.driverCheckin.value;
    const parsedCheckout = parsedLogisticsDateTimes.driverCheckout.value;

    return (
      normalizedDriverFullname !== pendingLogisticsDetailsEdit.original.driverFullname ||
      normalizedTrackingNumber !== pendingLogisticsDetailsEdit.original.trackingNumber ||
      (canEditCarrier && normalizedShippingDate !== pendingLogisticsDetailsEdit.original.shippingDate) ||
      parsedCheckin !== pendingLogisticsDetailsEdit.original.driverCheckin ||
      parsedCheckout !== pendingLogisticsDetailsEdit.original.driverCheckout
    );
  }, [canEditCarrier, pendingLogisticsDetailsEdit, parsedLogisticsDateTimes]);

  const handleSaveLogisticsDetails = React.useCallback(async () => {
    if (!authUserId || !pendingLogisticsDetailsEdit || !parsedLogisticsDateTimes) return;

    const { driverCheckin, driverCheckout } = parsedLogisticsDateTimes;
    const normalizedShippingDate = normalizeOptionalTextValue(
      pendingLogisticsDetailsEdit.draft.shippingDate,
    );
    if (driverCheckin.invalid || driverCheckout.invalid) {
      toast.error(m.orders_logistics_invalid_times());
      return;
    }
    if (canEditCarrier && hasLogisticsShippingDateInvalid) {
      toast.error(m.orders_carrier_shipping_date_required());
      return;
    }
    if (
      driverCheckin.value !== undefined &&
      driverCheckout.value !== undefined &&
      driverCheckout.value < driverCheckin.value
    ) {
      toast.error(m.orders_logistics_checkout_before_checkin());
      return;
    }
    if (!hasLogisticsDraftChanges) {
      toast.info(m.orders_logistics_no_changes());
      return;
    }

    setIsSavingLogisticsDetails(true);
    try {
      await updateLogisticsInfo({
        authUserId,
        orderId: pendingLogisticsDetailsEdit.orderId as any,
        driverFullname:
          normalizeOptionalTextValue(pendingLogisticsDetailsEdit.draft.driverFullname) ?? null,
        trackingNumber:
          normalizeOptionalTextValue(pendingLogisticsDetailsEdit.draft.trackingNumber) ?? null,
        ...(canEditCarrier && normalizedShippingDate !== pendingLogisticsDetailsEdit.original.shippingDate
          ? { shippingDate: normalizedShippingDate }
          : {}),
        driverCheckin: driverCheckin.value ?? null,
        driverCheckout: driverCheckout.value ?? null,
      } as any);
      toast.success(m.orders_logistics_updated());
      setPendingLogisticsDetailsEdit(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : m.orders_logistics_update_failed(),
      );
    } finally {
      setIsSavingLogisticsDetails(false);
    }
  }, [
    authUserId,
    canEditCarrier,
    hasLogisticsDraftChanges,
    hasLogisticsShippingDateInvalid,
    parsedLogisticsDateTimes,
    pendingLogisticsDetailsEdit,
    updateLogisticsInfo,
  ]);

  const handleRequestCarrierChange = React.useCallback(
    (order: any, carrier: CarrierOption) => {
      const orderId = String(order?._id ?? order?.id ?? "");
      if (!orderId) return;
      const nextCarrierId = String(carrier._id ?? "");
      if (!nextCarrierId) return;
      const isCurrentLocalPickup =
        normalizeCarrierValue(order?.delivery?.deliveryMethod) === "pickup" ||
        normalizeCarrierValue(order?.delivery?.deliveryMethod) === "local_pickup" ||
        order?.delivery?.isLocalPickup === true;

      if (nextCarrierId === LOCAL_PICKUP_OPTION_ID) {
        if (isCurrentLocalPickup) return;
        setPendingCarrierChange({
          orderId,
          orderNumber: String(order?.orderNumber ?? orderId),
          currentMode: isCurrentLocalPickup ? "pickup" : "shipping",
          currentCarrierName: String(order?.delivery?.carrierName ?? "").trim() || m.orders_unassigned(),
          currentCarrierCode: String(order?.delivery?.carrierCode ?? "").trim(),
          currentTrackingNumber: String(order?.delivery?.trackingNumber ?? "").trim(),
          currentTrackingUrl: String(order?.delivery?.trackingUrl ?? "").trim(),
          currentQuoteId: String(order?.delivery?.quoteId ?? "").trim(),
          paymentMethod: String(order?.payment?.method ?? "").trim(),
          paymentStatus: String(order?.payment?.status ?? "").trim(),
          nextMode: "pickup",
          nextCarrierId: "",
          nextCarrierName: m.delivery_method_local_pickup(),
          selectedShippingInfoId: "",
          useManualShippingAddress: true,
          manualShippingAddress: buildManualShippingAddressDraftFromOrder(order),
          shippingDate: toDateInputValue(
            order?.delivery?.logisticsScheduledDate ??
              order?.delivery?.deliveryDate ??
              order?.delivery?.pickupDate ??
              order?.createdAt ??
              order?._creationTime,
          ),
          contextHydrated: true,
          contextError: null,
        });
        return;
      }

      const currentCarrierId = String(order?.delivery?.carrierId ?? "");
      if (currentCarrierId && currentCarrierId === nextCarrierId) return;

      setPendingCarrierChange({
        orderId,
        orderNumber: String(order?.orderNumber ?? orderId),
        currentMode: isCurrentLocalPickup ? "pickup" : "shipping",
        currentCarrierName:
          isCurrentLocalPickup
            ? m.delivery_method_local_pickup()
            : String(order?.delivery?.carrierName ?? "").trim() || m.orders_unassigned(),
        currentCarrierCode: String(order?.delivery?.carrierCode ?? "").trim(),
        currentTrackingNumber: String(order?.delivery?.trackingNumber ?? "").trim(),
        currentTrackingUrl: String(order?.delivery?.trackingUrl ?? "").trim(),
        currentQuoteId: String(order?.delivery?.quoteId ?? "").trim(),
        paymentMethod: String(order?.payment?.method ?? "").trim(),
        paymentStatus: String(order?.payment?.status ?? "").trim(),
        nextMode: "shipping",
        nextCarrierId,
        nextCarrierName: carrier.name,
        selectedShippingInfoId: "",
        useManualShippingAddress: true,
        manualShippingAddress: buildManualShippingAddressDraftFromOrder(order),
        shippingDate: toDateInputValue(
          order?.delivery?.logisticsScheduledDate ??
            order?.delivery?.deliveryDate ??
            order?.delivery?.pickupDate ??
            order?.createdAt ??
            order?._creationTime,
        ),
        contextHydrated: false,
        contextError: null,
      });
    },
    [],
  );

  const submitCarrierChange = React.useCallback(
    async (autoRerate: boolean) => {
      if (!authUserId || !pendingCarrierChange) return;
      const isPickupToShippingSwitch =
        pendingCarrierChange.nextMode === "shipping" && pendingCarrierChange.currentMode === "pickup";

      if (isPickupToShippingSwitch && !autoRerate) {
        const errorMessage = m.orders_carrier_pickup_to_shipping_requires_rerate();
        setPendingCarrierChange((current) =>
          current
            ? {
                ...current,
                contextError: errorMessage,
              }
            : current,
        );
        toast.error(errorMessage);
        return;
      }

      if (pendingCarrierChange.nextMode === "shipping") {
        const shippingDate = String(pendingCarrierChange.shippingDate ?? "").trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(shippingDate)) {
          const errorMessage = m.orders_carrier_shipping_date_required();
          setPendingCarrierChange((current) =>
            current
              ? {
                  ...current,
                  contextError: errorMessage,
                }
              : current,
          );
          toast.error(errorMessage);
          return;
        }

        if (pendingCarrierChange.useManualShippingAddress) {
          if (!hasCompleteManualShippingAddress(pendingCarrierChange.manualShippingAddress)) {
            const errorMessage = m.orders_carrier_address_required();
            setPendingCarrierChange((current) =>
              current
                ? {
                    ...current,
                    contextError: errorMessage,
                  }
                : current,
            );
            toast.error(errorMessage);
            return;
          }
        } else if (!pendingCarrierChange.selectedShippingInfoId) {
          const errorMessage = m.orders_carrier_select_address();
          setPendingCarrierChange((current) =>
            current
              ? {
                  ...current,
                  contextError: errorMessage,
                }
              : current,
          );
          toast.error(errorMessage);
          return;
        }
      }

      if (autoRerate && pendingCarrierChange.nextMode === "shipping") {
        if (isPreviewingCarrierRerate) {
          toast.info(m.orders_carrier_checking_shippeek());
          return;
        }

        if (!carrierReratePreview?.success || !carrierReratePreview?.rerated) {
          const errorMessage =
            String(carrierReratePreview?.message ?? "").trim() ||
            m.orders_carrier_preflight_failed();
          setPendingCarrierChange((current) =>
            current
              ? {
                  ...current,
                  contextError: errorMessage,
                }
              : current,
          );
          toast.error(errorMessage);
          return;
        }
      }

      setIsSavingCarrierChange(true);
      try {
        const result = pendingCarrierChange.nextMode === "pickup"
          ? await updateOrderCarrier({
              authUserId,
              orderId: pendingCarrierChange.orderId as any,
              setLocalPickup: true,
            })
          : await updateOrderCarrier({
              authUserId,
              orderId: pendingCarrierChange.orderId as any,
              carrierId: pendingCarrierChange.nextCarrierId as any,
              autoRerate,
              shippingDate: pendingCarrierChange.shippingDate,
              ...(pendingCarrierChange.useManualShippingAddress
                ? {
                    manualShippingAddress: {
                      street: String(pendingCarrierChange.manualShippingAddress.street ?? "").trim(),
                      city: String(pendingCarrierChange.manualShippingAddress.city ?? "").trim(),
                      state: String(pendingCarrierChange.manualShippingAddress.state ?? "").trim(),
                      postalCode: String(pendingCarrierChange.manualShippingAddress.postalCode ?? "").trim(),
                      country:
                        String(pendingCarrierChange.manualShippingAddress.country ?? "US").trim() || "US",
                    },
                  }
                : {
                    shippingInfoId: pendingCarrierChange.selectedShippingInfoId as any,
                  }),
              ...(autoRerate && carrierReratePreview?.selectedRate
                ? {
                    preflightSelectedRate: carrierReratePreview.selectedRate,
                    preflightRerateMessage: carrierReratePreview.message,
                  }
                : {}),
            });

        if (result?.applied === false || result?.success === false) {
          const message =
            String(result?.message ?? "").trim() ||
            m.orders_carrier_apply_failed();
          setPendingCarrierChange((current) =>
            current
              ? {
                  ...current,
                  contextError: message,
                }
              : current,
          );
          toast.error(message);
          return;
        }

		const settlement = result?.impact?.settlement;
		const shouldShowImpactSummary = Boolean(
			result?.impact &&
				(
					isPickupToShipping ||
					settlement?.resolutionMethod === "scheduled_payment_updated" ||
					settlement?.resolutionMethod === "manual_review" ||
					settlement?.pointsDeducted ||
					settlement?.pointsCredited ||
					settlement?.paymentLinkAmount
				),
		);

		if (shouldShowImpactSummary) {
			const impact = result.impact;

			let impactMessage = isPickupToShipping
				? "Order switched to shipping. "
				: result?.rerated
					? "Carrier rerated. "
					: "Carrier updated. ";
			impactMessage += `Shipping: ${formatCurrency(impact?.shipping?.previousCharge || 0)} → ${formatCurrency(impact?.shipping?.newCharge || 0)}. `;

			if (settlement?.resolutionMethod === "scheduled_payment_updated") {
				impactMessage += "Pending installments updated. ";
			} else if (settlement?.pointsDeducted) {
				impactMessage += `${settlement.pointsDeducted} points applied. `;
			} else if (
				settlement?.resolutionMethod === "points_partial_link" ||
				settlement?.resolutionMethod === "payment_link"
			) {
				if (result?.paymentLinkError || settlement?.effectiveStatus === "link_failed") {
					impactMessage += "Payment link generation failed. ";
				} else if (result?.paymentLinkUrl) {
					impactMessage += `Payment link created for ${formatCurrency(Number(settlement?.paymentLinkAmount || 0))}. `;
				} else if (settlement?.paymentLinkAmount) {
					impactMessage += `Payment due: ${formatCurrency(Number(settlement.paymentLinkAmount))}. `;
				}
			} else if (settlement?.pointsCredited) {
				impactMessage += `${settlement.pointsCredited} points credited to customer. `;
			} else if (settlement?.resolutionMethod === "manual_review") {
				impactMessage += "Manual settlement review required. ";
			}

			impactMessage += settlement?.nextActionLabel
				? `Next: ${settlement.nextActionLabel}.`
				: "";

			toast.success(impactMessage, { duration: 6000 });
		} else if (result?.rerated) {
			toast.success(
				m.orders_carrier_rerated_success({
              carrier: pendingCarrierChange.nextCarrierName,
              amount: formatCurrency(Number(result.newDeliveryCharge ?? 0)),
            }),
          );
        } else if (result?.rateNeedsReview) {
          toast.warning(result?.message ?? m.orders_carrier_rate_review());
        } else {
          toast.success(result?.message ?? m.orders_carrier_updated());
        }

        if (result?.paymentLinkUrl) {
          toast.warning(
            m.orders_carrier_additional_payment_link({ url: String(result.paymentLinkUrl) }),
            { duration: 8000 }
          );
        }
        if (result?.paymentLinkError) {
          toast.error(
            m.orders_carrier_payment_link_failed({ message: String(result.paymentLinkError) }),
          );
        }
        setPendingCarrierChange(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : m.orders_carrier_update_failed();
        setPendingCarrierChange((current) =>
          current
            ? {
                ...current,
                contextError: message,
              }
            : current,
        );
        toast.error(
          message,
        );
      } finally {
        setIsSavingCarrierChange(false);
      }
    },
    [
      authUserId,
      carrierReratePreview,
      isPreviewingCarrierRerate,
		pendingCarrierChange,
		isPickupToShipping,
		updateOrderCarrier,
	],
	);

  const handleCarrierAddressSelectionChange = React.useCallback((value: string) => {
    setPendingCarrierChange((current) => {
      if (!current || current.nextMode !== "shipping") return current;
      if (value === MANUAL_ADDRESS_OPTION_ID) {
        return {
          ...current,
          useManualShippingAddress: true,
          selectedShippingInfoId: "",
          contextError: null,
        };
      }
      return {
        ...current,
        useManualShippingAddress: false,
        selectedShippingInfoId: value,
        contextError: null,
      };
    });
  }, []);

  const updateCarrierManualAddressField = React.useCallback(
    (field: keyof ManualShippingAddressDraft, value: string) => {
      setPendingCarrierChange((current) => {
        if (!current || current.nextMode !== "shipping") return current;
        return {
          ...current,
          manualShippingAddress: {
            ...current.manualShippingAddress,
            [field]: value,
          },
          contextError: null,
        };
      });
    },
    [],
  );

  const updateCarrierShippingDate = React.useCallback((value: string) => {
    setPendingCarrierChange((current) => {
      if (!current || current.nextMode !== "shipping") return current;
      return {
        ...current,
        shippingDate: value,
        contextError: null,
      };
    });
  }, []);

  const canSubmitShippingCarrierChange = React.useMemo(() => {
    if (!pendingCarrierChange || pendingCarrierChange.nextMode !== "shipping") return false;
    const hasValidDate = /^\d{4}-\d{2}-\d{2}$/.test(String(pendingCarrierChange.shippingDate ?? "").trim());
    const hasAddress = pendingCarrierChange.useManualShippingAddress
      ? hasCompleteManualShippingAddress(pendingCarrierChange.manualShippingAddress)
      : String(pendingCarrierChange.selectedShippingInfoId ?? "").trim().length > 0;
    return !isCarrierChangeContextLoading && hasValidDate && hasAddress;
  }, [isCarrierChangeContextLoading, pendingCarrierChange]);

  const columns = useColumns(
    showDriverColumns,
    Boolean(activeInventoryRecheckSummary),
    carriers,
    canEditCarrier,
    handleRequestCarrierChange,
    canEditLogisticsDetails,
    handleRequestLogisticsDetailsEdit,
    handleRequestLoadingPhotoUpload,
    handleRequestViewLoadingPhotos,
    canUploadShippingLabels,
    handleRequestManualShippingLabelUpload,
    canAdminCancelOrders,
    adminCancellation.isStarting,
    adminCancellation.start,
  );

  // Auto-generate docs for orders with pendingDocGeneration flag
  const autoGenRef = React.useRef<Map<string, number>>(new Map());
  React.useEffect(() => {
    if (!authUserId || orders.length === 0 || isReadOnlyOrdersViewer) return;

    for (const order of orders) {
      if (order.pendingDocGeneration !== true) {
        autoGenRef.current.delete(order._id ?? order.id);
      }
    }
    const pendingOrders = orders.filter(
      (o: any) =>
        o.pendingDocGeneration === true &&
        o.test !== true &&
        o.status !== "held_for_customer" &&
        !generatingOrderIds.has(o._id ?? o.id) &&
        autoGenRef.current.get(o._id ?? o.id) !== (o.metadata?.additionalData?.amendmentDocumentRevision ?? 0),
    );

    for (const order of pendingOrders) {
      const oid = order._id ?? order.id;
      autoGenRef.current.set(oid, order.metadata?.additionalData?.amendmentDocumentRevision ?? 0);
      void generateAndUploadDocs(order);
    }
  }, [authUserId, orders, generatingOrderIds, generateAndUploadDocs, isReadOnlyOrdersViewer]);

  const tabCounts = React.useMemo(() => {
    const countSource = isWarehouseQueueView ? orders : orderSummaries;
    const sourceOrders = Array.isArray(countSource) ? countSource : [];
    let ai = 0;
    let held = 0;
    let rp = 0;
    let rf = 0;
    let it = 0;
    let del = 0;
    let oh = 0;
    let can = 0;

    for (const order of sourceOrders) {
      const status = String(order?.status ?? "").trim();
      if (status === "awaiting_inventory") ai += 1;
      else if (status === "held_for_customer") held += 1;
      else if (status === "ready_to_prepare") rp += 1;
      else if (status === "ready_for_pickup") rf += 1;
      else if (status === "in_transit") it += 1;
      else if (status === "delivered") del += 1;
      else if (status === "on_hold") oh += 1;
      else if (status === "cancelled") can += 1;
    }

    return {
      all: ai + held + rp + rf + it + del + oh,
      held_for_customer: held,
      awaiting_inventory: ai,
      ready_to_prepare: rp,
      ready_for_pickup: rf,
      in_transit: it,
      delivered: del,
      on_hold: oh,
      cancelled: can,
    };
  }, [isWarehouseQueueView, orderSummaries, orders]);

  const amendmentQueueRows = React.useMemo<AmendmentQueueRow[]>(() => {
    const sourceOrders = Array.isArray(orderSummaries) ? orderSummaries : [];
    return sourceOrders
      .map((order: any) => {
        const indicators = getAmendmentIndicatorState(order);
        const orderId = String(order?._id ?? order?.id ?? "");
        const customer = order?.user?.company || order?.user?.name || order?.userId || "Unknown";
        return {
          orderId,
          orderNumber: String(order?.orderNumber ?? (orderId || "N/A")),
          customer: String(customer),
          pendingStartedAt: indicators.pendingStartedAt,
          holdReviewAt: indicators.holdReviewAt,
          amountDue: indicators.amountDue,
          resumeStatus: indicators.resumeStatus,
          paymentLinkStatus: indicators.paymentLinkStatus,
          isPendingThreeDays: indicators.isPendingThreeDays,
          isPendingOnHold: indicators.isPendingOnHold,
        };
      })
      .filter((row: AmendmentQueueRow) => row.isPendingOnHold)
      .sort((a: AmendmentQueueRow, b: AmendmentQueueRow) => {
        const aTs = a.pendingStartedAt ?? Number.POSITIVE_INFINITY;
        const bTs = b.pendingStartedAt ?? Number.POSITIVE_INFINITY;
        return aTs - bTs;
      });
  }, [orderSummaries]);

  const handleBulkStatusUpdate = React.useCallback(
    async (
      selectedRows: any[],
      newStatus: OrderStatus,
      clearSelection: () => void,
    ) => {
      if (!authUserId) {
        toast.error(m.orders_status_auth_required());
        return;
      }

      const actionableRows = selectedRows.filter(
        (row) => String(row?.status ?? "").trim() !== newStatus,
      );
      const skippedAlreadyInStatus = Math.max(0, selectedRows.length - actionableRows.length);
      const reviewBlockedRows = actionableRows.filter((row) => getReviewBlockReason(row, newStatus));
      const eligibleRows = actionableRows.filter((row) => !getReviewBlockReason(row, newStatus));
      const skippedForReview = reviewBlockedRows.length;

      if (newStatus === "in_transit") {
        const missingOrders = actionableRows
          .map((order): MissingInTransitDetailsOrder | null => {
            const missingFields = getMissingInTransitDetails(order);
            if (missingFields.length === 0) return null;

            const orderId = String(order?._id ?? order?.id ?? "");
            return {
              order,
              orderId,
              orderNumber: String(order?.orderNumber ?? orderId ?? m.status_unknown()),
              missingFields,
            };
          })
          .filter((entry): entry is MissingInTransitDetailsOrder => !!entry);

        if (missingOrders.length > 0) {
          setPendingInTransitRequirements(missingOrders);
          return;
        }
      }

      const orderIds = eligibleRows
        .map((row) => row._id ?? row.id)
        .filter(Boolean);

        const skipNotes: string[] = [];
        if (skippedAlreadyInStatus > 0) {
        skipNotes.push(m.orders_already_in_status({ count: skippedAlreadyInStatus }));
        }
        if (skippedForReview > 0) {
        skipNotes.push(m.orders_not_green_reviewed({ count: skippedForReview }));
        }
      const skipSuffix = skipNotes.length > 0 ? ` (${skipNotes.join(", ")})` : "";

      if (orderIds.length === 0) {
        if (skippedForReview > 0) {
          toast.error(m.orders_no_orders_moved({ status: formatStatusLabel(newStatus), suffix: skipSuffix }));
        } else {
          toast.info(
            m.orders_all_orders_already_status({ status: formatStatusLabel(newStatus).toLowerCase() }),
          );
        }
        clearSelection();
        return;
      }

      try {
        const result = await bulkUpdateOrderStatus({
          authUserId,
          orderIds: orderIds as any[],
          newStatus,
        });

        if (result.failed > 0) {
          const failureReasons = (result.results ?? [])
            .filter((entry: any) => entry.status === "rejected")
            .map((entry: any) => String(entry.reason ?? "").trim())
            .filter(Boolean);
          const uniqueReasons = Array.from(new Set(failureReasons));
          const shownReasons = uniqueReasons.slice(0, 3);
          const hiddenCount = Math.max(0, uniqueReasons.length - shownReasons.length);
          const description =
            shownReasons.length > 0
              ? `${shownReasons.join(" | ")}${hiddenCount > 0 ? ` | +${hiddenCount} more error${hiddenCount === 1 ? "" : "s"}` : ""}`
              : undefined;

          toast.error(
            m.orders_bulk_update_partial({
              successful: result.successful,
              total: result.total,
              suffix: skipSuffix,
              failed: result.failed,
            }),
            description ? { description } : undefined,
          );
        } else {
          toast.success(
            m.orders_bulk_update_success({
              count: result.successful,
              status: formatStatusLabel(newStatus),
              suffix: skipSuffix,
            }),
          );
        }
        clearSelection();
        if (result.successful > 0 && canBrowseStatusTabs) {
          handleTabChange(newStatus);
        }
      } catch (error) {
        toast.error(
          toUserFacingErrorMessage(error, m.orders_update_failed()),
        );
      }
    },
    [authUserId, bulkUpdateOrderStatus, canBrowseStatusTabs, handleTabChange],
  );

  const handleSingleStatusUpdate = React.useCallback(
    async (order: any, newStatus: FullOrderStatus) => {
      if (!authUserId) {
        toast.error(m.orders_status_auth_required());
        return;
      }

      const orderId = String(order?._id ?? order?.id ?? "");
      if (!orderId) return;

      const reviewBlockReason = getReviewBlockReason(order, newStatus);
      if (reviewBlockReason) {
        toast.error(reviewBlockReason);
        return;
      }

      if (newStatus === "in_transit") {
        const missingFields = getMissingInTransitDetails(order);
        if (missingFields.length > 0) {
          const onlyPhotosMissing = missingFields.every((field) => field.startsWith("Truck/loading photos"));
          if (onlyPhotosMissing) {
            handleRequestLoadingPhotoUpload(order, true);
          } else {
            setPendingInTransitRequirements([
              {
                order,
                orderId,
                orderNumber: String(order?.orderNumber ?? orderId ?? m.status_unknown()),
                missingFields,
              },
            ]);
          }
          return;
        }
      }

      try {
        await updateOrderStatus({
          authUserId,
          orderId: orderId as any,
          newStatus,
        });
        toast.success(m.orders_single_status_success({ status: formatStatusLabel(newStatus) }));
        // Only switch tabs in Modern view; Day Sheet should stay put so the
        // user keeps context while updating multiple orders inline.
        if (!isDaySheetView && canBrowseStatusTabs && ORDER_TAB_VALUES.has(newStatus as OrdersTabValue)) {
          handleTabChange(newStatus);
        }
      } catch (error) {
        toast.error(
          toUserFacingErrorMessage(error, m.orders_single_status_failed()),
        );
      }
    },
    [authUserId, updateOrderStatus, canBrowseStatusTabs, handleRequestLoadingPhotoUpload, handleTabChange, isDaySheetView],
  );

  const handleDaySheetReviewChange = React.useCallback(
    async (order: any, nextReviewStatus: DaySheetReviewStatus) => {
      if (!authUserId) {
        toast.error(m.orders_logistics_auth_required());
        return;
      }

      const orderId = String(order?._id ?? order?.id ?? "");
      if (!orderId) return;

      const currentReviewStatus = getDaySheetReviewStatus(order);
      if (currentReviewStatus === nextReviewStatus) return;

      const currentStatus = String(order?.status ?? "") as FullOrderStatus;

      const reviewUpdateArgs =
        nextReviewStatus === "pending"
          ? {
              authUserId,
              orderId: orderId as any,
              qualityCheckStatus: "",
              qualityCheckPerson: "",
            }
          : {
              authUserId,
              orderId: orderId as any,
              qualityCheckStatus: nextReviewStatus === "approved" ? "passed" : nextReviewStatus,
            };

      try {
        await updateLogisticsInfo(reviewUpdateArgs);

        if (
          nextReviewStatus === "hold" &&
          currentStatus !== "on_hold" &&
          (ORDER_STATUS_TRANSITIONS[currentStatus] ?? []).includes("on_hold")
        ) {
          await updateOrderStatus({
            authUserId,
            orderId: orderId as any,
            newStatus: "on_hold",
          });
          toast.success(m.orders_inspection_updated_hold());
          return;
        }

        toast.success(m.orders_inspection_updated_to({ status: getDaySheetReviewLabel(nextReviewStatus) }));
      } catch (error) {
        toast.error(
          toUserFacingErrorMessage(error, m.orders_logistics_update_failed()),
        );
      }
    },
    [authUserId, updateLogisticsInfo, updateOrderStatus],
  );

  const handleExportSelected = React.useCallback((selectedRows: any[]) => {
    if (selectedRows.length === 0) {
      toast.error(m.orders_none_selected());
      return;
    }

    exportToCSV(
      selectedRows.map((order) => {
        const quotedShippingCost = getQuotedShippingCost(order);
        const createdAt = getOrderCreatedTimestamp(order);
        const submittedAt = getOrderSubmittedTimestamp(order);

        return {
          orderNumber: order.orderNumber ?? "",
          customer: order.user?.company || order.user?.name || order.userId || "",
          enterprise: getEnterpriseBadgeLabel(order.isEnterprise),
          status: formatStatusLabel(getDisplayedOrderStatus(order) ?? ""),
          totalAmount: getMoneyDisplay(order.totalAmount),
          shippingQuotedCost:
            quotedShippingCost === null || quotedShippingCost === 0
              ? ""
              : getMoneyDisplay(quotedShippingCost),
          deliveryMethod: formatDeliveryMethod(order.delivery?.deliveryMethod),
          createdAt: createdAt ? formatOrderSubmittedTimestamp(createdAt) : "",
          submittedAt: submittedAt ? formatOrderSubmittedTimestamp(submittedAt) : "",
        };
      }),
      [
        { key: "orderNumber", label: "Order #" },
        { key: "customer", label: m.orders_customer() },
        { key: "enterprise", label: m.enterprise() },
        { key: "status", label: m.status() },
        { key: "totalAmount", label: m.orders_total() },
        { key: "shippingQuotedCost", label: m.orders_shipping_quoted() },
        { key: "deliveryMethod", label: m.orders_delivery_type() },
        { key: "createdAt", label: m.created() },
        { key: "submittedAt", label: ORDER_SUBMITTED_LABEL },
      ],
      `${m.orders_export_filename_prefix()}-${new Date().toISOString().split("T")[0]}.csv`,
    );
  }, []);

  const getUpsBookingCandidates = React.useCallback((selectedRows: any[]) => {
    return selectedRows.filter((row) => isUpsBookingEligible(row));
  }, []);

  const executeUpsBooking = React.useCallback(
    async (candidates: any[], clearSelection: () => void) => {
      if (!authUserId) {
        toast.error(m.orders_ups_auth_required());
        return;
      }

      const orderIds = candidates
        .map((row) => row._id ?? row.id)
        .filter(Boolean);

      if (orderIds.length === 0) {
        toast.error(m.orders_no_valid_order_ids());
        return;
      }

      setIsBookingUps(true);
      try {
        const result = await bookUpsParcelOrders({
          authUserId,
          orderIds: orderIds as any[],
        });

        if (result.successful > 0) {
          toast.success(
            m.orders_ups_booked_success({ count: result.successful }),
          );
          clearSelection();
        }

        if (result.failed > 0) {
          const failedResults = (result.results ?? [])
            .filter((entry: any) => !entry.success)
            .slice(0, 2)
            .map((entry: any) => `${entry.orderNumber ?? entry.orderId}: ${entry.error}`)
            .join(" | ");

          toast.error(
            m.orders_ups_booking_failed({ count: result.failed, details: failedResults ? ` (${failedResults})` : "" }),
          );
        }

        const statusWarnings = (result.results ?? []).filter(
          (entry: any) => entry.success && !entry.statusAdvanced && entry.statusAdvanceError,
        );
        if (statusWarnings.length > 0) {
          const warningDetails = statusWarnings
            .slice(0, 2)
            .map(
              (entry: any) =>
                `${entry.orderNumber ?? entry.orderId}: ${String(entry.statusAdvanceError ?? "").trim()}`,
            )
            .filter((line: string) => !!line)
            .join(" | ");
          toast.warning(
            m.orders_booking_manual_status_warning({ count: statusWarnings.length }),
            warningDetails ? { description: warningDetails } : undefined,
          );
        }
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : m.orders_ups_booking_failed_generic(),
        );
      } finally {
        setIsBookingUps(false);
      }
    },
    [authUserId, bookUpsParcelOrders],
  );

  const handleBookUpsSelected = React.useCallback(
    (selectedRows: any[], clearSelection: () => void) => {
      const candidates = getUpsBookingCandidates(selectedRows);
      if (candidates.length === 0) {
        toast.error(m.orders_no_ups_ready());
        return;
      }

      const withTracking = candidates.filter(
        (row) => !!String(row.delivery?.trackingNumber ?? "").trim(),
      );

      if (withTracking.length > 0) {
        setUpsRebookConfirm({ candidates, withTracking, clearSelection });
        return;
      }

      void executeUpsBooking(candidates, clearSelection);
    },
    [getUpsBookingCandidates, executeUpsBooking],
  );

  const getEstesBookingCandidates = React.useCallback((selectedRows: any[]) => {
    return selectedRows.filter((row) => isEstesBookingEligible(row));
  }, []);

  const executeEstesBooking = React.useCallback(
    async (candidates: any[], clearSelection: () => void) => {
      if (!authUserId) {
        toast.error(m.orders_estes_auth_required());
        return;
      }

      const orderIds = candidates
        .map((row) => row._id ?? row.id)
        .filter(Boolean);

      if (orderIds.length === 0) {
        toast.error(m.orders_no_valid_order_ids());
        return;
      }

      setIsBookingEstes(true);
      try {
        const result = await bookEstesLtlOrders({
          authUserId,
          orderIds: orderIds as any[],
        });

        if (result.successful > 0) {
          toast.success(
            m.orders_estes_booked_success({ count: result.successful }),
          );
          clearSelection();
        }

        if (result.failed > 0) {
          const failedResults = (result.results ?? [])
            .filter((entry: any) => !entry.success)
            .slice(0, 2)
            .map((entry: any) => `${entry.orderNumber ?? entry.orderId}: ${entry.error}`)
            .join(" | ");

          toast.error(
            m.orders_estes_booking_failed({ count: result.failed, details: failedResults ? ` (${failedResults})` : "" }),
          );
        }

        const statusWarnings = (result.results ?? []).filter(
          (entry: any) => entry.success && !entry.statusAdvanced && entry.statusAdvanceError,
        );
        if (statusWarnings.length > 0) {
          const warningDetails = statusWarnings
            .slice(0, 2)
            .map(
              (entry: any) =>
                `${entry.orderNumber ?? entry.orderId}: ${String(entry.statusAdvanceError ?? "").trim()}`,
            )
            .filter((line: string) => !!line)
            .join(" | ");
          toast.warning(
            m.orders_booking_manual_status_warning({ count: statusWarnings.length }),
            warningDetails ? { description: warningDetails } : undefined,
          );
        }
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : m.orders_estes_booking_failed_generic(),
        );
      } finally {
        setIsBookingEstes(false);
      }
    },
    [authUserId, bookEstesLtlOrders],
  );

  const handleBookEstesSelected = React.useCallback(
    (selectedRows: any[], clearSelection: () => void) => {
      const candidates = getEstesBookingCandidates(selectedRows);
      if (candidates.length === 0) {
        toast.error(m.orders_no_estes_ready());
        return;
      }

      const withTracking = candidates.filter(
        (row) => !!String(row.delivery?.trackingNumber ?? "").trim(),
      );

      if (withTracking.length > 0) {
        setEstesRebookConfirm({ candidates, withTracking, clearSelection });
        return;
      }

      void executeEstesBooking(candidates, clearSelection);
    },
    [getEstesBookingCandidates, executeEstesBooking],
  );

  const getEstesCancellationCandidates = React.useCallback((selectedRows: any[]) => {
    return selectedRows.filter((row) => isEstesCancellationEligible(row));
  }, []);

  const executeEstesCancellation = React.useCallback(
    async (candidates: any[], clearSelection: () => void) => {
      if (!authUserId) {
        toast.error(m.orders_estes_cancel_auth_required());
        return;
      }

      const orderIds = candidates
        .map((row) => row._id ?? row.id)
        .filter(Boolean);

      if (orderIds.length === 0) {
        toast.error(m.orders_no_valid_order_ids());
        return;
      }

      setIsCancellingEstes(true);
      try {
        const result = await cancelEstesLtlOrders({
          authUserId,
          orderIds: orderIds as any[],
        });

        if (result.successful > 0) {
          toast.success(
            m.orders_estes_pickup_cancelled_success({ count: result.successful }),
          );
          clearSelection();
        }

        if (result.failed > 0) {
          const failedResults = (result.results ?? [])
            .filter((entry: any) => !entry.success)
            .slice(0, 2)
            .map((entry: any) => `${entry.orderNumber ?? entry.orderId}: ${entry.error}`)
            .join(" | ");

          toast.error(
            m.orders_estes_cancellation_failed({ count: result.failed, details: failedResults ? ` (${failedResults})` : "" }),
          );
        }

        const statusWarnings = (result.results ?? []).filter(
          (entry: any) => entry.success && !entry.statusReverted && entry.statusRevertError,
        );
        if (statusWarnings.length > 0) {
          const warningDetails = statusWarnings
            .slice(0, 2)
            .map(
              (entry: any) =>
                `${entry.orderNumber ?? entry.orderId}: ${String(entry.statusRevertError ?? "").trim()}`,
            )
            .filter((line: string) => !!line)
            .join(" | ");
          toast.warning(
            m.orders_cancellation_manual_status_warning({ count: statusWarnings.length }),
            warningDetails ? { description: warningDetails } : undefined,
          );
        }
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : m.orders_estes_cancel_failed_generic(),
        );
      } finally {
        setIsCancellingEstes(false);
      }
    },
    [authUserId, cancelEstesLtlOrders],
  );

  const handleCancelEstesSelected = React.useCallback(
    (selectedRows: any[], clearSelection: () => void) => {
      const candidates = getEstesCancellationCandidates(selectedRows);
      if (candidates.length === 0) {
        toast.error(m.orders_no_estes_pickups_selected());
        return;
      }

      setEstesCancelConfirm({ candidates, clearSelection });
    },
    [getEstesCancellationCandidates],
  );

  const getAvailableBulkActions = React.useCallback((selectedRows: any[]) => {
    const statuses = Array.from(
      new Set(
        selectedRows
          .map((row) => getDisplayedOrderStatus(row))
          .filter((status): status is FullOrderStatus => !!status),
      ),
    );

    if (statuses.length === 0) return [];

    const commonTargets = statuses.reduce<Set<FullOrderStatus> | null>(
      (acc, status) => {
        const targets = new Set(ORDER_STATUS_TRANSITIONS[status] ?? []);
        if (!acc) return targets;
        return new Set(Array.from(acc).filter((target) => targets.has(target)));
      },
      null,
    );

    if (!commonTargets || commonTargets.size === 0) return [];

    return getBulkStatusActions().filter(
      (action) =>
        commonTargets.has(action.target) &&
        selectedRows.every((row) =>
          canShowOrderStatusTarget(row, getDisplayedOrderStatus(row), action.target),
        ),
    );
  }, [locale]);

  const handleResendPaymentLink = React.useCallback(
    async (orderId: string) => {
      try {
        await resendAmendmentPaymentLinkMutation({ orderId: orderId as any });
        toast.success(m.orders_payment_link_resent());
      } catch (err: any) {
        toast.error(err?.message ?? m.orders_payment_link_resend_failed());
      }
    },
    [resendAmendmentPaymentLinkMutation],
  );

  const handleCancelAmendment = React.useCallback(
    async (orderId: string) => {
      setIsCancellingAmendment(true);
      try {
        await cancelPendingAmendmentMutation({ orderId: orderId as any });
        toast.success(m.orders_amendment_cancelled());
        setConfirmCancelAmendment(null);
      } catch (err: any) {
        toast.error(err?.message ?? m.orders_amendment_cancel_failed());
      } finally {
        setIsCancellingAmendment(false);
      }
    },
    [cancelPendingAmendmentMutation],
  );

  const handleCancelOrderFromQueue = React.useCallback(
    async (orderId: string) => {
      if (!authUserId) {
        toast.error(m.orders_cancel_auth_required());
        return;
      }

      setIsCancellingOrder(true);
      try {
        await updateOrderStatus({
          authUserId,
          orderId: orderId as any,
          newStatus: "cancelled",
        });
        toast.success(m.orders_cancelled());
        setConfirmCancelOrder(null);
      } catch (error: unknown) {
        toast.error(toUserFacingErrorMessage(error, m.orders_cancel_failed()));
      } finally {
        setIsCancellingOrder(false);
      }
    },
    [authUserId, updateOrderStatus],
  );

  return (
    <div className="space-y-6">
      <WarehouseOrderAlertsDialog
        alerts={unacknowledgedWarehouseOrderAlerts}
        onAcknowledge={handleAcknowledgeWarehouseAlerts}
      />

      <div>
        <h1 className="text-3xl font-bold tracking-tight">{m.orders_page_title()}</h1>
        <p className="text-muted-foreground mt-1">
          {m.orders_page_description()}
          {orders ? <span className="ml-1 font-medium text-foreground">{m.orders_count({ count: orders.length })}</span> : null}
        </p>
      </div>

      {warehouseOrderAlerts.length > 0 ? (
        <Card className="border-red-200 bg-red-50/70">
          <CardContent className="space-y-3 pt-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-red-300 bg-white text-red-900">
                Warehouse changes / Cambios
              </Badge>
              <Badge variant="secondary">{warehouseOrderAlerts.length}</Badge>
            </div>
            <div className="grid gap-2 lg:grid-cols-2">
              {warehouseOrderAlerts.slice(0, 4).map((alert) => (
                <div key={alert.key} className="rounded-md border border-red-200 bg-white px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={getOrderStatusBadgeClass(alert.status)}>
                      {formatStatusLabel(alert.status)}
                    </Badge>
                    <span className="text-sm font-semibold">#{alert.orderNumber}</span>
                  </div>
                  <p className="mt-1 text-xs text-red-900">{alert.englishMessage}</p>
                  <p className="mt-1 text-xs font-medium text-red-900">{alert.spanishMessage}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {activeInventoryRecheckSummary ? (
        <Card className="border-amber-200 bg-amber-50/70">
          <CardContent className="flex flex-col gap-3 pt-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-amber-300 bg-white text-amber-900">
                  Inventory recheck active
                </Badge>
                <Badge variant="outline" className="border-amber-300 bg-white text-amber-900">
                  {activeInventoryRecheckSummary.cargoCount} cargos in scope
                </Badge>
              </div>
              <p className="text-sm text-amber-950">
                Tracking order-driven storefront movement since {formatDate(activeInventoryRecheckSummary.startedAt)}
                {activeInventoryRecheckSummary.warehouseName ? ` for ${activeInventoryRecheckSummary.warehouseName}` : ""}.
              </p>
            </div>
            <div className="grid gap-2 text-sm text-amber-950 sm:grid-cols-3">
              <div className="rounded-md border border-amber-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-amber-700">Visible impacts</p>
                <p className="font-semibold">{visibleInventoryRecheckOrderCount}</p>
              </div>
              <div className="rounded-md border border-amber-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-amber-700">Visible order delta</p>
                <p className="font-semibold">{formatInventoryRecheckDelta(visibleInventoryRecheckDelta)}</p>
              </div>
              <div className="rounded-md border border-amber-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-amber-700">Auto-close</p>
                <p className="font-semibold">{formatDate(activeInventoryRecheckSummary.expiresAt)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>{m.nav_orders()}</CardTitle>
              <CardDescription>
                {isWarehouseQueueView
                  ? m.orders_warehouse_queue_description()
                  : m.orders_pipeline_description()}
              </CardDescription>
            </div>
            <div className="flex flex-col gap-1.5 sm:items-end">
              <Label htmlFor="orders-view-mode" className="text-xs text-muted-foreground">
                {m.orders_view_mode_label()}
              </Label>
              <Select
                value={viewMode}
                onValueChange={(value) => handleViewModeChange(value as OrdersViewMode)}
              >
                <SelectTrigger id="orders-view-mode" className="w-full max-w-[220px] sm:w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="day_sheet">{m.orders_view_day_sheet()}</SelectItem>
                  <SelectItem value="modern">{m.orders_view_modern()}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isWarehouseQueueView ? (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={getOrderStatusBadgeClass("ready_to_prepare")}>
                {m.status_ready_to_pull()}
              </Badge>
              <Badge variant="secondary">{tabCounts.ready_to_prepare}</Badge>
              <Badge variant="outline" className={getOrderStatusBadgeClass("ready_for_pickup")}>
                {m.status_ready_for_pickup()}
              </Badge>
              <Badge variant="secondary">{tabCounts.ready_for_pickup}</Badge>
              <Badge variant="outline" className={getOrderStatusBadgeClass("held_for_customer")}>
                Held for customer
              </Badge>
              <Badge variant="secondary">{tabCounts.held_for_customer}</Badge>
              <Badge variant="outline" className={getOrderStatusBadgeClass("on_hold")}>
                {m.status_on_hold()}
              </Badge>
              <Badge variant="secondary">{tabCounts.on_hold}</Badge>
              <Badge variant="outline" className={getOrderStatusBadgeClass("cancelled")}>
                {formatStatusLabel("cancelled")}
              </Badge>
              <Badge variant="secondary">{tabCounts.cancelled}</Badge>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-4">
              <TabsList>
                {statusTabs.map((tab) => (
                  <TabsTrigger key={tab.value} value={tab.value}>
                    {tab.label}
                    {tabCounts[tab.value] !== undefined ? (
                      <Badge variant="secondary" className="ml-2">{tabCounts[tab.value]}</Badge>
                    ) : null}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}

          {isDaySheetView ? (
            ordersResponse === undefined || (isPending && orders.length === 0) ? (
              <div className="rounded-md border bg-muted/20 px-3 py-6 text-sm text-muted-foreground">
                {m.orders_loading_day_sheet()}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-md border bg-muted/20 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {m.orders_scheduled_day()}
                      </Label>
                      <DaySheetDatePicker
                        value={selectedSheetDay}
                        onChange={handleSheetDayFilterChange}
                        orderDates={orderScheduledDates}
                      />
                      {selectedSheetDay ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSheetDayFilterChange("")}
                        >
                          {m.orders_clear_day_filter()}
                        </Button>
                      ) : null}
                    </div>
                    <p className="hidden text-xs text-muted-foreground md:block">
                      {m.orders_day_sheet_help()}
                    </p>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="ml-auto h-8 gap-1.5 text-xs text-muted-foreground"
                        >
                          <Info className="h-3.5 w-3.5" />
                          Color legend
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-72 space-y-1 p-2">
                        {daySheetReviewOptions.map((option) => (
                          <div
                            key={option.value}
                            className="flex items-start gap-2 rounded px-1.5 py-1 text-xs"
                          >
                            <span
                              aria-hidden
                              className={cn(
                                "mt-1 h-2.5 w-2.5 shrink-0 rounded-full border",
                                getDaySheetReviewBadgeClass(option.value),
                              )}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium">{option.colorLabel}</span>
                              <span className="text-muted-foreground">{option.description}</span>
                            </div>
                          </div>
                        ))}
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {daySheetGroups.length === 0 ? (
                  <div className="rounded-md border bg-muted/20 px-3 py-6 text-sm text-muted-foreground">
                    {m.orders_no_matches()}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {daySheetGroups.map((group) => (
                      <div key={group.dateKey} className="overflow-hidden rounded-md border">
                        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b bg-muted/95 px-3 py-2 backdrop-blur supports-[backdrop-filter]:bg-muted/70">
                          <div className="flex items-center gap-3">
                            <p className="text-sm font-medium">{group.dateLabel}</p>
                            <Badge variant="secondary" className="text-[11px]">
                              {m.orders_count({ count: group.orders.length })}
                            </Badge>
                          </div>
                          <DaySheetReviewSparkline orders={group.orders} />
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[2040px] text-sm">
                            <thead className="bg-background text-[11px] uppercase tracking-wide text-muted-foreground">
                              <tr>
                                <th className="sticky left-0 z-10 bg-background px-2 py-1.5 text-left font-medium">{m.table_order_number()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_amendment()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.created()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_customer()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_shipper()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_ready_question()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_scheduled_day()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_driver()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_tracking_number()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_time_in()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_time_out()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_inspection()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_bag_count()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_docs()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.status()}</th>
                                <th className="px-2 py-1.5 text-left font-medium">{m.orders_actions()}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.orders.map((order: any) => {
                                const orderId = String(order?._id ?? order?.id ?? "");
                                const currentStatus = getDisplayedOrderStatus(order);
                                const statusTargets = Array.from(
                                  new Set(getDaySheetStatusTargets(order, currentStatus)),
                                );
                                const reviewStatus = getDaySheetReviewStatus(order);
                                const rowClass = getDaySheetRowClass(reviewStatus);
                                const orderNumberClass = getDaySheetOrderNumberCellClass(currentStatus);
                                const bagCount = getOrderBagCount(order);
                                const scheduledAt = getOrderScheduledTimestamp(order);
                                const createdAt = getOrderCreatedTimestamp(order);
                                const submittedAt = getOrderSubmittedTimestamp(order);
                                const submittedDeltaLabel = formatSubmittedDelta(createdAt, submittedAt);
                                const readyValue = getReadyCellValue(order);
                                const reviewedBy = getActorLabel(order?.qualityCheckedBy);
                                const recheckImpact = order?.inventoryRecheckImpact ?? null;
                                const trackingNumber = String(order?.delivery?.trackingNumber ?? "").trim();
                                const driverFullname = String(order?.delivery?.driverFullname ?? "").trim();
                                const customerName = String(
                                  order?.user?.company || order?.user?.name || order?.userId || "",
                                ).trim();
                                const daySheetOrderRow = { original: order };
                                const canAdminCancelThisOrder =
                                  canAdminCancelOrders && canStartAdminCancellation(order);

                                return (
                                  <tr
                                    key={orderId || `${group.dateKey}-${order?.orderNumber ?? "order"}`}
                                    className={cn("group border-t transition-colors", rowClass)}
                                  >
                                    <td
                                      className={cn(
                                        "sticky left-0 z-[1] px-2 py-1.5 font-mono text-xs font-semibold",
                                        rowClass,
                                        orderNumberClass,
                                      )}
                                    >
                                      {String(order?.orderNumber ?? orderId ?? "N/A")}
                                      <StorageQueueSignal order={order} onReview={() => setDaySheetDetailsOrder(order)} />
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <OrderAmendmentIndicatorBadge order={order} />
                                    </td>
                                    <td className="px-2 py-1.5">
                                      {createdAt ? (
                                        <span className="inline-flex items-baseline gap-1 whitespace-nowrap text-xs">
                                          {formatOrderSubmittedTimestamp(createdAt)}
                                          {submittedDeltaLabel ? (
                                            <span
                                              className="text-[10px] tabular-nums text-muted-foreground"
                                              title={
                                                submittedAt
                                                  ? `${ORDER_SUBMITTED_LABEL}: ${formatOrderSubmittedTimestamp(submittedAt)}`
                                                  : undefined
                                              }
                                            >
                                              {submittedDeltaLabel}
                                            </span>
                                          ) : null}
                                        </span>
                                      ) : (
                                        <EmptyDaySheetCell />
                                      )}
                                    </td>
                                    <td className="px-2 py-1.5">
                                      {customerName ? customerName : <EmptyDaySheetCell />}
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <CarrierAssignmentCell
                                        order={order}
                                        carriers={carriers}
                                        isEditable={canEditCarrier}
                                        onRequestChange={handleRequestCarrierChange}
                                      />
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <Badge variant="outline" className={getDaySheetReadyBadgeClass(readyValue)}>
                                        {readyValue === "YES" ? m.orders_ready_yes() : m.orders_ready_no()}
                                      </Badge>
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <DaySheetText
                                        value={
                                          isDateOnlyScheduledTimestamp(order)
                                            ? formatUtcDateOnly(scheduledAt)
                                            : formatDate(scheduledAt)
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-1.5">
                                      {driverFullname ? driverFullname : <EmptyDaySheetCell />}
                                    </td>
                                    <td className="px-2 py-1.5">
                                      {trackingNumber ? (
                                        <div className="space-y-0.5">
                                          <TrackingEventsDialogTrigger
                                            orderId={orderId}
                                            trackingNumber={trackingNumber}
                                            carrierName={order?.delivery?.carrierName}
                                            carrierCode={order?.delivery?.carrierCode}
                                            trackingUrl={order?.delivery?.trackingUrl}
                                            triggerClassName="text-[11px]"
                                          />
                                          <TrackingAuditLine
                                            delivery={order?.delivery}
                                            className="max-w-48"
                                          />
                                        </div>
                                      ) : (
                                        <EmptyDaySheetCell />
                                      )}
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <DaySheetTimeCell timestamp={order?.delivery?.driverCheckin} />
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <DaySheetTimeCell timestamp={order?.delivery?.driverCheckout} />
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <div className="space-y-2">
                                        <Badge
                                          variant="outline"
                                          className={getDaySheetReviewBadgeClass(reviewStatus)}
                                        >
                                          {getDaySheetReviewLabel(reviewStatus)}
                                        </Badge>
                                        {reviewedBy ? (
                                          <p className="text-[11px] text-muted-foreground">{reviewedBy}</p>
                                        ) : null}
                                        {canEditLogisticsDetails && orderId ? (
                                          <div
                                            className="flex flex-wrap gap-1"
                                            role="group"
                                            aria-label={m.orders_inspection_review_aria({ orderNumber: String(order?.orderNumber ?? orderId) })}
                                          >
                                            {daySheetReviewOptions.map((option) => (
                                              <Button
                                                key={option.value}
                                                size="sm"
                                                variant="outline"
                                                className={getDaySheetReviewButtonClass(
                                                  option.value,
                                                  reviewStatus === option.value,
                                                )}
                                                aria-pressed={reviewStatus === option.value}
                                                onClick={() => void handleDaySheetReviewChange(order, option.value)}
                                              >
                                                {option.colorLabel}
                                              </Button>
                                            ))}
                                          </div>
                                        ) : null}
                                      </div>
                                    </td>
                                    <td className="px-2 py-1.5 font-medium tabular-nums">
                                      {bagCount > 0 ? bagCount : <EmptyDaySheetCell />}
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <AttachmentsCell
                                        row={daySheetOrderRow}
                                        canUploadShippingLabels={canUploadShippingLabels}
                                        onRequestManualShippingLabelUpload={handleRequestManualShippingLabelUpload}
                                      />
                                    </td>
                                    <td className="px-2 py-1.5">
                                       <div className="space-y-1">
                                         {recheckImpact ? (
                                           <Badge
                                             variant="outline"
                                             className={getInventoryRecheckBadgeClass(recheckImpact.netAvailableChange)}
                                           >
                                             Recheck {formatInventoryRecheckDelta(recheckImpact.netAvailableChange)}
                                           </Badge>
                                         ) : null}
                                         <Select
                                           value={currentStatus}
                                           onValueChange={(nextStatus) => {
                                             if (!orderId || nextStatus === currentStatus) return;
                                             void handleSingleStatusUpdate(order, nextStatus as FullOrderStatus);
                                           }}
                                          disabled={!canChangeOrderStatus || !orderId}
                                        >
                                          <SelectTrigger
                                            className={cn(
                                              "h-8 w-[190px] text-xs font-medium",
                                              getDaySheetStatusSelectClass(currentStatus),
                                            )}
                                          >
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {statusTargets.map((target) => (
                                              <SelectItem key={target} value={target}>
                                                {target === "ready_to_prepare"
                                                  ? m.status_ready_to_pull()
                                                  : formatStatusLabel(target)}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-7 px-2 text-xs"
                                          onClick={() => setDaySheetDetailsOrder(order)}
                                        >
                                          {currentStatus === "held_for_customer" ? "Storage & items" : m.orders_items()}
                                        </Button>
                                        {showDaySheetLogisticsEditAction && canEditLogisticsDetails && orderId ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 px-2 text-xs"
                                            onClick={() => handleRequestLogisticsDetailsEdit(order)}
                                          >
                                            {m.orders_edit_logistics()}
                                          </Button>
                                        ) : null}
                                        {orderId && getOrderLoadingPhotoCount(order) > 0 ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 px-2 text-xs"
                                            onClick={() => void handleRequestViewLoadingPhotos(order)}
                                          >
                                            {getOrderLoadingPhotoCount(order)} photo{getOrderLoadingPhotoCount(order) === 1 ? "" : "s"}
                                          </Button>
                                        ) : null}
                                        {canEditLogisticsDetails && orderId && getOrderLoadingPhotoCount(order) < ORDER_LOADING_PHOTO_MAX_COUNT ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 px-2 text-xs"
                                            onClick={() => handleRequestLoadingPhotoUpload(order)}
                                          >
                                            Upload photos
                                          </Button>
                                        ) : null}
                                        {canAdminCancelThisOrder ? (
                                          <Button
                                            size="sm"
                                            variant="destructive"
                                            className="h-7 px-2 text-xs transition-transform active:scale-[0.96]"
                                            disabled={adminCancellation.isStarting}
                                            onClick={() => void adminCancellation.start(order)}
                                          >
                                            Admin cancel
                                          </Button>
                                        ) : null}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          ) : (
            ordersResponse === undefined || (isPending && orders.length === 0) ? (
              <DataTableSkeleton columnCount={columns.length} rowCount={10} withPagination />
            ) : (
            <InternalEntityDataTable
              data={ordersWithRecheck.map((o: any) => ({ ...o, id: o._id || o.id }))}
              columns={columns}
              initialSorting={[
                { id: "carrier", desc: false },
                { id: "submittedAt", desc: true },
              ]}
              expandedRow={{
                render: ({ row }) => {
                  return (
                    <OrderLineItemsPanel
                      order={row.original}
                      showRecheckColumn={Boolean(activeInventoryRecheckSummary)}
                    />
                  );
                },
              }}
              actionBar={({ selectedRows, clearSelection }) => {
                const upsCandidates = getUpsBookingCandidates(selectedRows);
                const estesCandidates = getEstesBookingCandidates(selectedRows);
                const estesCancelCandidates = getEstesCancellationCandidates(selectedRows);
                const showUpsBookingAction = upsCandidates.length > 0;
                const showEstesBookingAction = estesCandidates.length > 0;
                const showEstesCancelAction = estesCancelCandidates.length > 0;

                return (
                  <DataTableFloatingBar
                    selectedCount={selectedRows.length}
                    onClear={clearSelection}
                  >
                    {!isReadOnlyOrdersViewer && getAvailableBulkActions(selectedRows).map((action) => (
                      <Button
                        key={action.target}
                        size="sm"
                        variant={action.variant}
                        onClick={() =>
                          void handleBulkStatusUpdate(
                            selectedRows,
                            action.target as OrderStatus,
                            clearSelection,
                          )
                        }
                      >
                        {action.label}
                      </Button>
                    ))}
                    {!isReadOnlyOrdersViewer && showUpsBookingAction ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={isBookingUps}
                        onClick={() => void handleBookUpsSelected(selectedRows, clearSelection)}
                      >
                        {isBookingUps
                          ? m.orders_booking_ups()
                          : m.orders_pay_book_ups({ count: upsCandidates.length })}
                      </Button>
                    ) : null}
                    {!isReadOnlyOrdersViewer && showEstesBookingAction ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={isBookingEstes}
                        onClick={() => void handleBookEstesSelected(selectedRows, clearSelection)}
                      >
                        {isBookingEstes
                          ? m.orders_booking_estes()
                          : m.orders_pay_book_estes({ count: estesCandidates.length })}
                      </Button>
                    ) : null}
                    {!isReadOnlyOrdersViewer && showEstesCancelAction ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={isCancellingEstes}
                        onClick={() => void handleCancelEstesSelected(selectedRows, clearSelection)}
                      >
                        {isCancellingEstes
                          ? m.orders_cancelling_estes()
                          : m.orders_cancel_estes_pickup({ count: estesCancelCandidates.length })}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExportSelected(selectedRows)}
                    >
                      {m.export()}
                    </Button>
                  </DataTableFloatingBar>
                );
              }}
            />
            )
          )}
        </CardContent>
      </Card>

      {hasRole(userRole, INTERNAL_ROLES.ADMIN) && (
      <Card>
        <CardHeader>
          <CardTitle>{m.orders_amendment_queue_title()}</CardTitle>
          <CardDescription>
            {m.orders_amendment_queue_description()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {amendmentQueueRows.length === 0 ? (
            <div className="rounded-md border bg-muted/20 px-3 py-6 text-sm text-muted-foreground">
              {m.orders_no_amendment_pending()}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">{m.table_order_number()}</th>
                    <th className="px-3 py-2 text-left font-medium">{m.orders_customer()}</th>
                    <th className="px-3 py-2 text-left font-medium">{m.orders_pending_age()}</th>
                    <th className="px-3 py-2 text-left font-medium">{m.orders_hold_review_date()}</th>
                    <th className="px-3 py-2 text-left font-medium">{m.orders_amount_due()}</th>
                    <th className="px-3 py-2 text-left font-medium">{m.orders_resume_status()}</th>
                    <th className="px-3 py-2 text-left font-medium">{m.orders_link_status()}</th>
                    <th className="px-3 py-2 text-left font-medium">{m.orders_priority()}</th>
                    <th className="px-3 py-2 text-left font-medium">{m.orders_actions()}</th>
                  </tr>
                </thead>
                <tbody>
                  {amendmentQueueRows.map((row: AmendmentQueueRow) => (
                    <tr key={row.orderId} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{row.orderNumber}</td>
                      <td className="px-3 py-2">{row.customer}</td>
                      <td className="px-3 py-2">
                        {row.pendingStartedAt ? formatRelativeTime(row.pendingStartedAt) : "-"}
                      </td>
                      <td className="px-3 py-2">
                        {row.holdReviewAt ? formatDate(row.holdReviewAt) : "-"}
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {row.amountDue > 0 ? formatCurrency(row.amountDue) : "-"}
                      </td>
                      <td className="px-3 py-2">
                        {row.resumeStatus ? formatStatusLabel(row.resumeStatus) : "-"}
                      </td>
                      <td className="px-3 py-2">
                        {row.paymentLinkStatus === "created" ? (
                          <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">
                            {m.orders_amendment_link_created()}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                            {m.orders_amendment_link_missing()}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {row.isPendingThreeDays ? (
                          <Badge variant="outline" className="border-red-300 bg-red-50 text-red-800">
                            {m.orders_priority_stale()}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-slate-300 bg-slate-50 text-slate-700">
                            {m.orders_priority_active()}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResendPaymentLink(row.orderId)}
                          >
                            {m.orders_resend_payment_link()}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmCancelAmendment(row.orderId)}
                          >
                            {m.orders_cancel_amendment()}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={adminCancellation.isStarting}
                            onClick={() => {
                              if (canAdminCancelOrders) {
                                void adminCancellation.start({ id: row.orderId, orderNumber: row.orderNumber, status: "on_hold" });
                              } else {
                                setConfirmCancelOrder(row.orderId);
                              }
                            }}
                          >
                            {m.orders_cancel_order()}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      <Dialog
        open={!!daySheetDetailsOrder}
        onOpenChange={(open) => {
          if (!open) {
            setDaySheetDetailsOrder(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-[1100px]">
          <DialogHeader>
            <DialogTitle>{m.orders_items()}</DialogTitle>
            <DialogDescription>
              {daySheetDetailsOrder
                ? m.orders_order_label({
                    orderNumber: String(
                      daySheetDetailsOrder.orderNumber
                        ?? daySheetDetailsOrder._id
                        ?? daySheetDetailsOrder.id
                        ?? "N/A",
                    ),
                  })
                : m.orders_items()}
            </DialogDescription>
          </DialogHeader>

          {daySheetDetailsOrder ? (
            <div className="overflow-y-auto pr-1">
              {getOrderHoldReason(daySheetDetailsOrder) ? (
                <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-100">
                  <p className="font-medium">{m.status_on_hold()}</p>
                  <p className="mt-1">{getOrderHoldReason(daySheetDetailsOrder)}</p>
                </div>
              ) : null}
              <OrderLineItemsPanel
                order={daySheetDetailsOrder}
                showRecheckColumn={Boolean(activeInventoryRecheckSummary)}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pendingManualShippingLabelUpload}
        onOpenChange={(open) => {
          if (!open && !isUploadingManualShippingLabel) {
            setPendingManualShippingLabelUpload(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Upload shipping label</DialogTitle>
            <DialogDescription>
              {pendingManualShippingLabelUpload
                ? `${m.orders_order_label({ orderNumber: pendingManualShippingLabelUpload.orderNumber })}${pendingManualShippingLabelUpload.companyName ? ` • ${pendingManualShippingLabelUpload.companyName}` : ""}`
                : "Attach a carrier label to the order."}
            </DialogDescription>
          </DialogHeader>

          {pendingManualShippingLabelUpload ? (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                Upload a PDF, image (including HEIC), TXT, or ZPL label. This replaces the current Shipping Label document for the order
                {pendingManualShippingLabelUpload.existingLabelCount > 1
                  ? " and removes older duplicate labels from the document list."
                  : "."}
              </div>

              <div className="space-y-2">
                <Label htmlFor="manual-shipping-label-file">Label file</Label>
                <Input
                  id="manual-shipping-label-file"
                  type="file"
                  accept={MANUAL_SHIPPING_LABEL_ACCEPT}
                  disabled={isUploadingManualShippingLabel}
                  onChange={handleManualShippingLabelFileChange}
                />
                <p className="text-xs text-muted-foreground">
                  Max {formatManualShippingLabelFileSize(MANUAL_SHIPPING_LABEL_MAX_BYTES)}.
                </p>
              </div>

              {pendingManualShippingLabelUpload.file ? (
                <div className="rounded-md border bg-muted/20 p-3 text-sm">
                  <p className="font-medium">{pendingManualShippingLabelUpload.file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatManualShippingLabelFileSize(pendingManualShippingLabelUpload.file.size)}
                  </p>
                </div>
              ) : null}

              {pendingManualShippingLabelUpload.error ? (
                <p className="text-sm text-destructive">{pendingManualShippingLabelUpload.error}</p>
              ) : null}
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingManualShippingLabelUpload(null)}
              disabled={isUploadingManualShippingLabel}
            >
              {m.cancel()}
            </Button>
            <Button
              type="button"
              onClick={() => void handleUploadManualShippingLabel()}
              disabled={
                isUploadingManualShippingLabel ||
                !pendingManualShippingLabelUpload?.file ||
                Boolean(pendingManualShippingLabelUpload?.error)
              }
            >
              {isUploadingManualShippingLabel ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading
                </>
              ) : (
                "Upload label"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pendingLoadingPhotoUpload}
        onOpenChange={(open) => {
          if (!open && !isUploadingLoadingPhotos) {
            setPendingLoadingPhotoUpload(null);
          }
        }}
      >
        <DialogContent className={cn("overflow-hidden rounded-2xl p-0 sm:max-w-[680px]", SHADOW_MODAL)}>
          <DialogHeader className="border-b bg-muted/20 px-6 py-5 pr-12">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ImageIcon className="size-5" />
              </div>
              <div className="min-w-0 space-y-1">
                <DialogTitle className="text-balance text-xl">Upload truck/loading photos</DialogTitle>
                <DialogDescription className="text-sm">
                  {pendingLoadingPhotoUpload
                    ? `${m.orders_order_label({ orderNumber: pendingLoadingPhotoUpload.orderNumber })}${pendingLoadingPhotoUpload.companyName ? ` • ${pendingLoadingPhotoUpload.companyName}` : ""}`
                    : "Attach loading photos before moving the order to In Transit."}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {pendingLoadingPhotoUpload ? (
            <div className="space-y-5 px-6 py-5">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className={cn("rounded-xl bg-background p-3", SHADOW_BORDER)}>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Required</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">
                    {Math.min(pendingLoadingPhotoUpload.existingPhotoCount, ORDER_LOADING_PHOTO_MIN_COUNT)}/{ORDER_LOADING_PHOTO_MIN_COUNT}
                  </p>
                </div>
                <div className={cn("rounded-xl bg-background p-3", SHADOW_BORDER)}>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Room left</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">
                    {Math.max(0, ORDER_LOADING_PHOTO_MAX_COUNT - pendingLoadingPhotoUpload.existingPhotoCount)}
                  </p>
                </div>
                <div className={cn("rounded-xl bg-background p-3", SHADOW_BORDER)}>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Limit</p>
                  <p className="mt-1 text-sm font-medium">
                    {formatManualShippingLabelFileSize(ORDER_LOADING_PHOTO_MAX_BYTES)} each
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Input
                  id="loading-photo-camera"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  disabled={isUploadingLoadingPhotos}
                  onChange={handleLoadingPhotoFileChange}
                  className="sr-only"
                />
                <Input
                  id="loading-photo-files"
                  type="file"
                  accept={ORDER_LOADING_PHOTO_ACCEPT}
                  multiple
                  disabled={isUploadingLoadingPhotos}
                  onChange={handleLoadingPhotoFileChange}
                  className="sr-only"
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Label
                    htmlFor="loading-photo-camera"
                    className={cn(
                      "group flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-muted-foreground/30 bg-muted/20 px-6 py-8 text-center transition-[background-color,border-color,box-shadow] hover:border-primary/60 hover:bg-primary/5",
                      SHADOW_BORDER_HOVER,
                      isUploadingLoadingPhotos && "pointer-events-none opacity-60",
                    )}
                  >
                    <span className="flex size-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-[1.03]">
                      <Camera className="size-5 text-primary" />
                    </span>
                    <span className="mt-4 text-base font-semibold text-foreground">
                      Take photo
                    </span>
                    <span className="mt-1 max-w-md text-sm font-normal text-muted-foreground">
                      Opens the device camera on supported phones and tablets.
                    </span>
                  </Label>
                  <Label
                    htmlFor="loading-photo-files"
                    className={cn(
                      "group flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-muted-foreground/30 bg-muted/20 px-6 py-8 text-center transition-[background-color,border-color,box-shadow] hover:border-primary/60 hover:bg-primary/5",
                      SHADOW_BORDER_HOVER,
                      isUploadingLoadingPhotos && "pointer-events-none opacity-60",
                    )}
                  >
                    <span className="flex size-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-[1.03]">
                      <ImageIcon className="size-5 text-primary" />
                    </span>
                    <span className="mt-4 text-base font-semibold text-foreground">
                      Choose photos
                    </span>
                    <span className="mt-1 max-w-md text-sm font-normal text-muted-foreground">
                      Select clear images of the truck, pallets, and final loaded condition.
                    </span>
                  </Label>
                </div>
              </div>

              {selectedLoadingPhotoPreviews.length > 0 ? (
                <div className={cn("overflow-hidden rounded-xl bg-background", SHADOW_BORDER)}>
                  <div className="flex items-center justify-between gap-3 border-b bg-muted/30 px-4 py-2.5 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium">Selected photos</span>
                      <span className="ml-2 text-muted-foreground tabular-nums">
                        {selectedLoadingPhotoPreviews.length} file{selectedLoadingPhotoPreviews.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 px-2 text-xs text-muted-foreground transition-transform hover:text-destructive active:scale-[0.96]"
                      onClick={handleClearSelectedLoadingPhotos}
                      disabled={isUploadingLoadingPhotos}
                    >
                      Clear all
                    </Button>
                  </div>
                  <div className="grid max-h-72 gap-3 overflow-y-auto p-3 sm:grid-cols-2">
                    {selectedLoadingPhotoPreviews.map(({ file, previewUrl }, index) => (
                      <div
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        className={cn(
                          "group overflow-hidden rounded-lg bg-background transition-[box-shadow]",
                          SHADOW_BORDER,
                          SHADOW_BORDER_HOVER,
                        )}
                      >
                        <div className="relative aspect-video bg-muted">
                          <img
                            src={previewUrl}
                            alt={file.name}
                            className="h-full w-full object-cover outline outline-1 -outline-offset-1 outline-black/10 transition-transform group-hover:scale-[1.02] dark:outline-white/10"
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="secondary"
                            aria-label={`Remove ${file.name}`}
                            className={cn(
                              "absolute right-2 top-2 size-10 rounded-full bg-background/95 text-muted-foreground backdrop-blur transition-[color,box-shadow,transform] hover:text-destructive active:scale-[0.96]",
                              SHADOW_BORDER,
                            )}
                            disabled={isUploadingLoadingPhotos}
                            onClick={() => handleRemoveSelectedLoadingPhoto(index)}
                          >
                            <X className="size-4" />
                          </Button>
                        </div>
                        <div className="min-w-0 p-3 text-sm">
                          <p className="truncate font-medium">{file.name}</p>
                          <p className="text-xs text-muted-foreground tabular-nums">
                            {formatManualShippingLabelFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {pendingLoadingPhotoUpload.error ? (
                <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {pendingLoadingPhotoUpload.error}
                </p>
              ) : null}
            </div>
          ) : null}

          <DialogFooter className="border-t bg-muted/20 px-6 py-4 sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              {pendingLoadingPhotoUpload?.advanceAfterUpload
                ? "Photos will be saved first, then the order will move to In Transit."
                : `Minimum ${ORDER_LOADING_PHOTO_MIN_COUNT} photos required for In Transit.`}
            </p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                className="transition-transform active:scale-[0.96]"
                onClick={() => setPendingLoadingPhotoUpload(null)}
                disabled={isUploadingLoadingPhotos}
              >
                {m.cancel()}
              </Button>
              <Button
                type="button"
                className="min-w-[150px] transition-transform active:scale-[0.96]"
                onClick={() => void handleUploadLoadingPhotos()}
                disabled={
                  isUploadingLoadingPhotos ||
                  pendingLoadingPhotoUpload?.files.length === 0 ||
                  Boolean(pendingLoadingPhotoUpload?.error) ||
                  Boolean(
                    pendingLoadingPhotoUpload &&
                    pendingLoadingPhotoUpload.existingPhotoCount < ORDER_LOADING_PHOTO_MIN_COUNT &&
                    (pendingLoadingPhotoUpload.existingPhotoCount + pendingLoadingPhotoUpload.files.length) < ORDER_LOADING_PHOTO_MIN_COUNT,
                  )
                }
              >
                {isUploadingLoadingPhotos ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading
                  </>
                ) : pendingLoadingPhotoUpload &&
                  pendingLoadingPhotoUpload.existingPhotoCount < ORDER_LOADING_PHOTO_MIN_COUNT &&
                  (pendingLoadingPhotoUpload.existingPhotoCount + pendingLoadingPhotoUpload.files.length) < ORDER_LOADING_PHOTO_MIN_COUNT ? (
                  `Select ${ORDER_LOADING_PHOTO_MIN_COUNT - pendingLoadingPhotoUpload.existingPhotoCount - pendingLoadingPhotoUpload.files.length} more`
                ) : pendingLoadingPhotoUpload?.advanceAfterUpload ? (
                  "Upload and mark In Transit"
                ) : (
                  "Upload photos"
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!loadingPhotoViewer}
        onOpenChange={(open) => {
          if (!open) {
            setLoadingPhotoViewer(null);
          }
        }}
      >
        <DialogContent className={cn("overflow-hidden rounded-2xl p-0 sm:max-w-[780px]", SHADOW_MODAL)}>
          <DialogHeader className="border-b bg-muted/20 px-6 py-5 pr-12">
            <DialogTitle className="text-balance text-xl">Truck/loading photos</DialogTitle>
            <DialogDescription>
              {loadingPhotoViewer
                ? `${m.orders_order_label({ orderNumber: loadingPhotoViewer.orderNumber })}${loadingPhotoViewer.companyName ? ` • ${loadingPhotoViewer.companyName}` : ""}`
                : "Photos uploaded before shipment departure."}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-5">
            {loadingPhotoViewer?.isLoading ? (
              <div className={cn("flex items-center justify-center gap-2 rounded-xl bg-muted/20 py-12 text-sm text-muted-foreground", SHADOW_BORDER)}>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading photos
              </div>
            ) : loadingPhotoViewer?.error ? (
              <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {loadingPhotoViewer.error}
              </p>
            ) : loadingPhotoViewer?.photos?.length ? (
              <div className="grid max-h-[60vh] gap-3 overflow-y-auto p-0.5 sm:grid-cols-2">
                {loadingPhotoViewer.photos.map((photo) => (
                  <div
                    key={photo.id}
                    className={cn(
                      "group overflow-hidden rounded-xl bg-background transition-[box-shadow]",
                      SHADOW_BORDER,
                      SHADOW_BORDER_HOVER,
                    )}
                  >
                    <div className="relative aspect-video bg-muted">
                      <a href={photo.url} target="_blank" rel="noopener noreferrer" className="block h-full w-full">
                        <img
                          src={photo.url}
                          alt={photo.originalFilename}
                          className="h-full w-full object-cover outline outline-1 -outline-offset-1 outline-black/10 transition-transform group-hover:scale-[1.02] dark:outline-white/10"
                          loading="lazy"
                        />
                      </a>
                      {canEditLogisticsDetails ? (
                        <Button
                          type="button"
                          size="icon"
                          variant="secondary"
                          aria-label={`Delete ${photo.originalFilename}`}
                          className={cn(
                            "absolute right-2 top-2 size-10 rounded-full bg-background/95 text-muted-foreground backdrop-blur transition-[color,box-shadow,transform] hover:text-destructive active:scale-[0.96]",
                            SHADOW_BORDER,
                          )}
                          onClick={() => setLoadingPhotoDeleteTarget(photo)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      ) : null}
                    </div>
                    <div className="space-y-1 p-3 text-xs">
                      <p className="truncate font-medium">{photo.originalFilename}</p>
                      <p className="text-muted-foreground tabular-nums">
                        {formatManualShippingLabelFileSize(photo.fileSize)}
                        {photo.uploadedAt ? ` • ${formatDate(photo.uploadedAt)}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={cn("rounded-xl bg-muted/20 p-8 text-center text-sm text-muted-foreground", SHADOW_BORDER)}>
                No loading photos uploaded yet.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!loadingPhotoDeleteTarget}
        onOpenChange={(open) => {
          if (!open && !isDeletingLoadingPhoto) setLoadingPhotoDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete loading photo?</AlertDialogTitle>
            <AlertDialogDescription>
              {loadingPhotoDeleteTarget
                ? `This will remove ${loadingPhotoDeleteTarget.originalFilename} from the order and delete it from R2.`
                : "This will remove the photo from the order and delete it from R2."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingLoadingPhoto}>Keep photo</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground transition-transform hover:bg-destructive/90 active:scale-[0.96]"
              onClick={() => void handleDeleteLoadingPhoto()}
              disabled={isDeletingLoadingPhoto}
            >
              {isDeletingLoadingPhoto ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Deleting
                </>
              ) : (
                "Delete photo"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmCancelAmendment}
        onOpenChange={(open) => {
          if (!open && !isCancellingAmendment) setConfirmCancelAmendment(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.orders_cancel_pending_amendment_title()}</AlertDialogTitle>
            <AlertDialogDescription>
              {m.orders_cancel_pending_amendment_description()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancellingAmendment}>{m.orders_keep_amendment()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmCancelAmendment) {
                  void handleCancelAmendment(confirmCancelAmendment);
                }
              }}
              disabled={isCancellingAmendment}
            >
              {isCancellingAmendment ? m.orders_cancelling() : m.orders_cancel_amendment()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmCancelOrder}
        onOpenChange={(open) => {
          if (!open && !isCancellingOrder) setConfirmCancelOrder(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.orders_cancel_order_dialog_title()}</AlertDialogTitle>
            <AlertDialogDescription>
              {m.orders_cancel_order_dialog_description()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancellingOrder}>{m.orders_keep_order()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmCancelOrder) {
                  void handleCancelOrderFromQueue(confirmCancelOrder);
                }
              }}
              disabled={isCancellingOrder}
            >
              {isCancellingOrder ? m.orders_cancelling() : m.orders_cancel_order()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AdminCancellationDialog
        order={adminCancellation.order}
        authUserId={authUserId ?? undefined}
        onClose={adminCancellation.close}
      />

      <AlertDialog
        open={!!pendingInTransitRequirements}
        onOpenChange={(open) => {
          if (!open) setPendingInTransitRequirements(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.orders_in_transit_requirements_title()}</AlertDialogTitle>
            <AlertDialogDescription>
              {m.orders_in_transit_requirements_description()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingInTransitRequirements ? (
            <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border bg-muted/20 p-3 text-sm">
              {pendingInTransitRequirements.slice(0, 6).map((entry) => (
                <p key={entry.orderId || entry.orderNumber}>
                  <span className="font-medium">{entry.orderNumber}</span>
                  {" "}
                  {m.orders_missing_label()}
                  {" "}
                  {entry.missingFields.join(", ")}
                </p>
              ))}
              {pendingInTransitRequirements.length > 6 ? (
                <p className="text-muted-foreground">
                  {m.orders_more_selected_orders({ count: pendingInTransitRequirements.length - 6 })}
                </p>
              ) : null}
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const firstMissingOrder = pendingInTransitRequirements?.[0];
                if (firstMissingOrder) {
                  const onlyPhotosMissing = firstMissingOrder.missingFields.every((field) =>
                    field.startsWith("Truck/loading photos"),
                  );
                  if (onlyPhotosMissing) {
                    handleRequestLoadingPhotoUpload(firstMissingOrder.order, true);
                  } else {
                    handleRequestLogisticsDetailsEdit(firstMissingOrder.order);
                  }
                }
                setPendingInTransitRequirements(null);
              }}
            >
              {m.orders_edit_first_order()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!pendingLogisticsDetailsEdit}
        onOpenChange={(open) => {
          if (!open && !isSavingLogisticsDetails) {
            setPendingLogisticsDetailsEdit(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{m.orders_edit_logistics_details_title()}</DialogTitle>
            <DialogDescription>
              {pendingLogisticsDetailsEdit
                ? `${m.orders_order_label({ orderNumber: pendingLogisticsDetailsEdit.orderNumber })} • ${pendingLogisticsDetailsEdit.companyName}`
                : m.orders_edit_logistics_details_description()}
            </DialogDescription>
          </DialogHeader>

          {pendingLogisticsDetailsEdit ? (
            <div className="space-y-5">
              <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
                {m.orders_logistics_capture_dispatch_metadata()}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="logistics-driver-name">{m.orders_driver()}</Label>
                  <Input
                    id="logistics-driver-name"
                    value={pendingLogisticsDetailsEdit.draft.driverFullname}
                    onChange={(event) =>
                      updateLogisticsDraftField("driverFullname", event.target.value)
                    }
                    placeholder={m.orders_enter_driver_name()}
                    disabled={isSavingLogisticsDetails}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="logistics-tracking-number">{m.orders_tracking_number()}</Label>
                  <Input
                    id="logistics-tracking-number"
                    value={pendingLogisticsDetailsEdit.draft.trackingNumber}
                    onChange={(event) =>
                      updateLogisticsDraftField("trackingNumber", event.target.value)
                    }
                    placeholder={m.orders_enter_tracking_number()}
                    disabled={isSavingLogisticsDetails}
                  />
                  {pendingLogisticsDetailsEdit.original.trackingNumber ? (
                    <p className="text-xs text-muted-foreground">
                      {pendingLogisticsDetailsEdit.trackingAuditSummary}
                    </p>
                  ) : null}
                </div>
              </div>

              {canEditCarrier ? (
                <div className="space-y-2">
                  <Label htmlFor="logistics-shipping-date">{m.orders_shipping_date()}</Label>
                  <DatePicker
                    id="logistics-shipping-date"
                    value={pendingLogisticsDetailsEdit.draft.shippingDate}
                    onChange={(value) => updateLogisticsDraftField("shippingDate", value)}
                    placeholder={m.select_date()}
                    disabled={isSavingLogisticsDetails}
                    disableWeekends
                    aria-describedby="logistics-shipping-date-help"
                    aria-invalid={hasLogisticsShippingDateInvalid}
                  />
                  {hasLogisticsShippingDateInvalid ? (
                    <p id="logistics-shipping-date-help" className="text-xs text-destructive">
                      {m.orders_carrier_shipping_date_required()}
                    </p>
                  ) : (
                    <p id="logistics-shipping-date-help" className="text-xs text-muted-foreground">
                      Updating this date only changes the logistics schedule on this page. It does not change the customer-requested date or regenerate BOL/DO paperwork.
                    </p>
                  )}
                </div>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-3 rounded-md border p-3">
                  <Label>{m.orders_logistics_driver_checkin()}</Label>
					<DateTimePicker
						id="logistics-driver-checkin"
						value={pendingLogisticsDetailsEdit.draft.driverCheckin}
						onChange={(value) => updateLogisticsDraftField("driverCheckin", value)}
						datePlaceholder={m.select_date()}
						timeLabel="Check-in time"
						defaultTime="10:30:00"
						disabled={isSavingLogisticsDetails}
						aria-label={m.orders_logistics_driver_checkin()}
						aria-describedby="logistics-driver-checkin-help"
						aria-invalid={parsedLogisticsDateTimes?.driverCheckin.invalid}
					/>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        updateLogisticsDraftField(
                          "driverCheckin",
                          getCurrentDateTimeInputValue(),
                        )
                      }
                      disabled={isSavingLogisticsDetails}
                    >
                      {m.orders_set_now()}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => updateLogisticsDraftField("driverCheckin", "")}
                      disabled={isSavingLogisticsDetails}
                    >
                      {m.orders_clear()}
                    </Button>
                  </div>
                  {parsedLogisticsDateTimes?.driverCheckin.invalid ? (
                    <p id="logistics-driver-checkin-help" className="text-xs text-destructive">
                      {m.orders_checkin_invalid()}
                    </p>
                  ) : (
                    <p id="logistics-driver-checkin-help" className="text-xs text-muted-foreground">
                      {m.orders_stored_as_utc()}
                    </p>
                  )}
                </div>

                <div className="space-y-3 rounded-md border p-3">
                  <Label>{m.orders_logistics_driver_checkout()}</Label>
					<DateTimePicker
						id="logistics-driver-checkout"
						value={pendingLogisticsDetailsEdit.draft.driverCheckout}
						onChange={(value) => updateLogisticsDraftField("driverCheckout", value)}
						datePlaceholder={m.select_date()}
						timeLabel="Check-out time"
						defaultTime="12:30:00"
						disabled={isSavingLogisticsDetails}
						aria-label={m.orders_logistics_driver_checkout()}
						aria-describedby="logistics-driver-checkout-help"
						aria-invalid={parsedLogisticsDateTimes?.driverCheckout.invalid}
					/>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        updateLogisticsDraftField(
                          "driverCheckout",
                          getCurrentDateTimeInputValue(),
                        )
                      }
                      disabled={isSavingLogisticsDetails}
                    >
                      {m.orders_set_now()}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => updateLogisticsDraftField("driverCheckout", "")}
                      disabled={isSavingLogisticsDetails}
                    >
                      {m.orders_clear()}
                    </Button>
                  </div>
                  {parsedLogisticsDateTimes?.driverCheckout.invalid ? (
                    <p id="logistics-driver-checkout-help" className="text-xs text-destructive">
                      {m.orders_checkout_invalid()}
                    </p>
                  ) : (
                    <p id="logistics-driver-checkout-help" className="text-xs text-muted-foreground">
                      {m.orders_leave_empty_until_checkout()}
                    </p>
                  )}
                </div>
              </div>

              {hasLogisticsDateOrderingError ? (
                <p className="text-xs text-destructive">
                  {m.orders_logistics_checkout_before_checkin()}
                </p>
              ) : null}
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingLogisticsDetailsEdit(null)}
              disabled={isSavingLogisticsDetails}
            >
              {m.cancel()}
            </Button>
            <Button
              type="button"
              onClick={() => void handleSaveLogisticsDetails()}
              disabled={
                isSavingLogisticsDetails ||
                !hasLogisticsDraftChanges ||
                !!parsedLogisticsDateTimes?.driverCheckin.invalid ||
                !!parsedLogisticsDateTimes?.driverCheckout.invalid ||
                hasLogisticsDateOrderingError ||
                hasLogisticsShippingDateInvalid
              }
            >
              {isSavingLogisticsDetails ? m.orders_saving() : m.orders_save_logistics_details()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pendingCarrierChange}
        onOpenChange={(open) => {
          if (!open && !isSavingCarrierChange) {
            setPendingCarrierChange(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>
              {pendingCarrierChange?.nextMode === "pickup"
                ? m.orders_confirm_local_pickup_switch()
                : m.orders_confirm_carrier_change()}
            </DialogTitle>
            <DialogDescription className="break-words pr-8">
              {pendingCarrierChange
                ? m.orders_carrier_change_summary({
                    orderNumber: pendingCarrierChange.orderNumber,
                    currentCarrier: pendingCarrierChange.currentCarrierName,
                    nextCarrier: pendingCarrierChange.nextCarrierName,
                  })
                : m.orders_carrier_review_before_apply()}
            </DialogDescription>
          </DialogHeader>
          {pendingCarrierChange ? (
            <div className="min-w-0 space-y-3">
              <div className="space-y-2 rounded-md border bg-muted/40 p-3 text-sm">
                <p>
                  {pendingCarrierChange.nextMode === "pickup"
                    ? m.orders_switching_to_local_pickup_explanation()
                    : m.orders_changing_carrier_explanation()}
                </p>
                {pendingCarrierChange.nextMode === "pickup" &&
                normalizeCarrierValue(pendingCarrierChange.paymentMethod) === "cheque" &&
                !["paid", "completed"].includes(
                  normalizeCarrierValue(pendingCarrierChange.paymentStatus),
                ) ? (
                  <p className="text-amber-700 dark:text-amber-300">
                    {m.orders_offline_payment_pending_warning()}
                  </p>
                ) : null}
                {pendingCarrierChange.currentTrackingNumber ? (
                  <p className="text-amber-700 dark:text-amber-300">
                    {m.orders_existing_tracking()}{" "}
                    <TrackingEventsDialogTrigger
                      orderId={pendingCarrierChange.orderId}
                      trackingNumber={pendingCarrierChange.currentTrackingNumber}
                      carrierName={pendingCarrierChange.currentCarrierName}
                      carrierCode={pendingCarrierChange.currentCarrierCode}
                      trackingUrl={pendingCarrierChange.currentTrackingUrl}
                      triggerClassName="text-xs text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
                    />
                  </p>
                ) : null}
                {pendingCarrierChange.currentQuoteId ? (
                  <p className="text-amber-700 dark:text-amber-300">
                    {m.orders_existing_quote()} <span className="font-mono">{pendingCarrierChange.currentQuoteId}</span>
                  </p>
                ) : null}
                {pendingCarrierChange.nextMode === "shipping" ? (
                  <p className="text-muted-foreground">
                    {m.orders_recommended_change_and_rerate()}
                  </p>
                ) : null}
              </div>

              {pendingCarrierChange.nextMode === "shipping" ? (
                <div className="min-w-0 space-y-4 rounded-md border p-3">
                  <div className="space-y-2">
                    <Label htmlFor="carrier-change-address">{m.orders_delivery_address()}</Label>
                    {isCarrierChangeContextLoading ? (
                      <div className="flex items-center gap-2 rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {m.orders_loading_saved_addresses()}
                      </div>
                    ) : (
                      <Select
                        value={
                          pendingCarrierChange.useManualShippingAddress
                            ? MANUAL_ADDRESS_OPTION_ID
                            : pendingCarrierChange.selectedShippingInfoId || MANUAL_ADDRESS_OPTION_ID
                        }
                        onValueChange={handleCarrierAddressSelectionChange}
                        disabled={isSavingCarrierChange}
                      >
                        <SelectTrigger id="carrier-change-address" className="w-full min-w-0">
                          <SelectValue placeholder={m.orders_select_saved_address()} />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          align="start"
                          className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-4rem)]"
                        >
                          {carrierChangeAddresses.map((address) => {
                            const addressLabel = `${formatShippingAddressLabel(address)}${address.isDefault ? ` ${m.orders_default_suffix()}` : ""}`;
                            return (
                              <SelectItem key={address.shippingInfoId} value={address.shippingInfoId}>
                                <span className="block truncate" title={addressLabel}>
                                  {addressLabel}
                                </span>
                              </SelectItem>
                            );
                          })}
                          <SelectItem value={MANUAL_ADDRESS_OPTION_ID}>
                            {m.orders_add_new_address()}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {m.orders_saved_addresses_help()}
                    </p>
                    {isPickupToShipping ? (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {m.orders_pickup_to_shipping_requires_rerate_help()}
                      </p>
                    ) : null}
                  </div>

                  {pendingCarrierChange.useManualShippingAddress ? (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="carrier-change-manual-street">{m.orders_street()}</Label>
                        <Input
                          id="carrier-change-manual-street"
                          value={pendingCarrierChange.manualShippingAddress.street}
                          onChange={(event) =>
                            updateCarrierManualAddressField("street", event.target.value)
                          }
                          placeholder={m.orders_street_address_placeholder()}
                          disabled={isSavingCarrierChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="carrier-change-manual-city">{m.orders_city()}</Label>
                        <Input
                          id="carrier-change-manual-city"
                          value={pendingCarrierChange.manualShippingAddress.city}
                          onChange={(event) =>
                            updateCarrierManualAddressField("city", event.target.value)
                          }
                          placeholder={m.orders_city()}
                          disabled={isSavingCarrierChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="carrier-change-manual-state">{m.orders_state()}</Label>
                        <Input
                          id="carrier-change-manual-state"
                          value={pendingCarrierChange.manualShippingAddress.state}
                          onChange={(event) =>
                            updateCarrierManualAddressField("state", event.target.value)
                          }
                          placeholder={m.orders_state()}
                          disabled={isSavingCarrierChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="carrier-change-manual-postal">{m.orders_postal_code()}</Label>
                        <Input
                          id="carrier-change-manual-postal"
                          value={pendingCarrierChange.manualShippingAddress.postalCode}
                          onChange={(event) =>
                            updateCarrierManualAddressField("postalCode", event.target.value)
                          }
                          placeholder={m.orders_postal_code_placeholder()}
                          disabled={isSavingCarrierChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="carrier-change-manual-country">{m.orders_country()}</Label>
                        <Input
                          id="carrier-change-manual-country"
                          value={pendingCarrierChange.manualShippingAddress.country}
                          onChange={(event) =>
                            updateCarrierManualAddressField("country", event.target.value)
                          }
                          placeholder={m.orders_country_placeholder()}
                          disabled={isSavingCarrierChange}
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className="space-y-2">
                    <Label htmlFor="carrier-change-shipping-date">{m.orders_shipping_date()}</Label>
                    <DatePicker
                      id="carrier-change-shipping-date"
                      value={pendingCarrierChange.shippingDate}
                      onChange={updateCarrierShippingDate}
                      placeholder={m.select_date()}
                      disabled={isSavingCarrierChange}
                      disableWeekends
                      aria-describedby="carrier-change-shipping-date-help"
                    />
                    <p id="carrier-change-shipping-date-help" className="text-xs text-muted-foreground">
                      {m.orders_shipping_date_help()}
                    </p>
                  </div>

                  <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
                    <div className="flex items-center gap-2 font-medium">
                      {m.orders_fetching_shipping_rates()}
                      {isPreviewingCarrierRerate ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    </div>
                    {isPreviewingCarrierRerate ? (
                      <p className="text-muted-foreground">
                        {m.orders_fetching_selected_carrier_rates()}
                      </p>
                    ) : carrierReratePreview ? (
                      carrierReratePreview.success && carrierReratePreview.rerated ? (
                        <div className="space-y-2">
                          <p className="text-emerald-700 dark:text-emerald-300">
                            {m.orders_rate_ready({ amount: formatCurrency(Number(carrierReratePreview.newDeliveryCharge ?? 0)) })}
                            {carrierReratePreview.selectedRate?.serviceName
                              ? ` · ${carrierReratePreview.selectedRate.serviceName}`
                              : ""}
                          </p>
                        </div>
                      ) : (
                        <p className="text-destructive">
                          {carrierReratePreview.message || m.orders_no_matching_shippeek_rate()}
                        </p>
                      )
                    ) : (
                      <p className="text-muted-foreground">
                        {m.orders_complete_address_to_prefetch_rates()}
                      </p>
                    )}
                  </div>

                  {/* Impact Summary Preview */}
                  {carrierReratePreview?.success && carrierReratePreview?.rerated && isPickupToShipping && (
                    <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20 p-3 text-sm">
                      <div className="font-medium text-amber-900 dark:text-amber-100 flex items-center gap-2">
                        <span>Financial Impact Preview</span>
                        <Badge variant="outline" className="text-xs">Review Before Applying</Badge>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Previous Shipping</p>
                          <p className="font-medium">{formatCurrency(0)}</p>
                          <p className="text-xs text-muted-foreground">Local pickup</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">New Shipping</p>
                          <p className="font-medium text-emerald-700 dark:text-emerald-300">
                            {formatCurrency(Number(carrierReratePreview.newDeliveryCharge ?? 0))}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Increase</p>
                          <p className="font-medium text-red-600">
                            +{formatCurrency(Number(carrierReratePreview.newDeliveryCharge ?? 0))}
                          </p>
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        <p className="font-medium mb-1">What happens next:</p>
                        <ul className="space-y-1 list-disc list-inside">
                          <li>This order will switch from pickup to shipping</li>
                          <li>Customer's scheduled payment will be updated</li>
                          <li>OR a payment link may be created if needed</li>
                          <li>Previous tracking/quote will be cleared</li>
                        </ul>
                      </div>

                      <div className="text-xs text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/50 rounded px-2 py-1">
                        Click "Change + Auto-Rerate" to apply these changes
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {pendingCarrierChange.contextError ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                  {pendingCarrierChange.contextError}
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setPendingCarrierChange(null)}
              disabled={isSavingCarrierChange}
            >
              {m.cancel()}
            </Button>
            <div className="flex gap-2">
              {pendingCarrierChange?.nextMode === "pickup" ? (
                <Button
                  onClick={() => void submitCarrierChange(false)}
                  disabled={isSavingCarrierChange}
                >
                  {isSavingCarrierChange ? m.orders_saving() : m.orders_switch_to_local_pickup()}
                </Button>
              ) : null}
              {pendingCarrierChange?.nextMode === "shipping" && !isPickupToShipping ? (
                <Button
                  variant="secondary"
                  onClick={() => void submitCarrierChange(false)}
                  disabled={isSavingCarrierChange || !canSubmitShippingCarrierChange}
                >
                  {isSavingCarrierChange ? m.orders_saving() : m.orders_change_only()}
                </Button>
              ) : null}
              {pendingCarrierChange?.nextMode === "shipping" ? (
                <Button
                  onClick={() => void submitCarrierChange(true)}
                  disabled={
                    isSavingCarrierChange ||
                    !canSubmitShippingCarrierChange ||
                    isPreviewingCarrierRerate ||
                    !carrierReratePreview?.success ||
                    !carrierReratePreview?.rerated
                  }
                >
                  {isSavingCarrierChange
                    ? m.orders_rerating()
                    : isPreviewingCarrierRerate
                      ? m.orders_checking_rates()
                      : m.orders_change_auto_rerate()}
                </Button>
              ) : null}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!upsRebookConfirm}
        onOpenChange={(open) => { if (!open) setUpsRebookConfirm(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.orders_ups_rebook_title()}</AlertDialogTitle>
            <AlertDialogDescription>
              {upsRebookConfirm?.withTracking.length === 1
                ? m.orders_ups_rebook_description_one({ orderNumber: upsRebookConfirm.withTracking[0].orderNumber })
                : m.orders_ups_rebook_description_many({ count: upsRebookConfirm?.withTracking.length ?? 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!upsRebookConfirm || !authUserId) return;
                const { withTracking, candidates, clearSelection } = upsRebookConfirm;
                setUpsRebookConfirm(null);
                void (async () => {
                  for (const order of withTracking) {
                    const orderId = order._id ?? order.id;
                    if (!orderId) continue;
                    await updateLogisticsInfo({
                      authUserId,
                      orderId,
                      trackingNumber: "",
                    });
                  }
                  await executeUpsBooking(candidates, clearSelection);
                })();
              }}
            >
              {m.orders_rebook()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!estesRebookConfirm}
        onOpenChange={(open) => { if (!open) setEstesRebookConfirm(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.orders_estes_rebook_title()}</AlertDialogTitle>
            <AlertDialogDescription>
              {estesRebookConfirm?.withTracking.length === 1
                ? m.orders_estes_rebook_description_one({ orderNumber: estesRebookConfirm.withTracking[0].orderNumber })
                : m.orders_estes_rebook_description_many({ count: estesRebookConfirm?.withTracking.length ?? 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!estesRebookConfirm || !authUserId) return;
                const { withTracking, candidates, clearSelection } = estesRebookConfirm;
                setEstesRebookConfirm(null);
                void (async () => {
                  for (const order of withTracking) {
                    const orderId = order._id ?? order.id;
                    if (!orderId) continue;
                    await updateLogisticsInfo({
                      authUserId,
                      orderId,
                      trackingNumber: "",
                    });
                  }
                  await executeEstesBooking(candidates, clearSelection);
                })();
              }}
            >
              {m.orders_rebook()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!estesCancelConfirm}
        onOpenChange={(open) => { if (!open) setEstesCancelConfirm(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.orders_cancel_estes_pickup_title()}</AlertDialogTitle>
            <AlertDialogDescription>
              {estesCancelConfirm?.candidates.length === 1
                ? m.orders_cancel_estes_pickup_description_one({ orderNumber: estesCancelConfirm.candidates[0].orderNumber })
                : m.orders_cancel_estes_pickup_description_many({ count: estesCancelConfirm?.candidates.length ?? 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancellingEstes}>{m.orders_keep()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!estesCancelConfirm) return;
                const { candidates, clearSelection } = estesCancelConfirm;
                setEstesCancelConfirm(null);
                void executeEstesCancellation(candidates, clearSelection);
              }}
              disabled={isCancellingEstes}
            >
              {isCancellingEstes ? m.orders_cancelling() : m.orders_cancel_pickup()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
